import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import "../isf/isf.scss";
import "./MilestoneRecipientRoles.scss";
import Dropdown from "../../common/dropDown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SignatureCanvas from "react-signature-canvas";

import { useUserData } from "../../../hooks/useUserData";
import { useVisitTracking } from "../../../context/VisitTrackingContext";

import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import DocumentPreviewPane from "../../common/DocumentPreview/DocumentPreviewPane";
import { normalizeFileExtension } from "../../common/DocumentPreview/documentUrlUtils";
import {
  fetchEisfDocumentTypes,
  fetchEisfDocumentCategories,
} from "../../../services/eisfMasterService";
import useConfirmModal from "../../../hooks/useConfirmModal";
import useInfoAlert from "../../../hooks/useInfoAlert";
import useToast from "../../../hooks/useToast";
import ConfirmModal from "../../common/ConfirmModal";
import InfoAlert from "../../common/InfoAlert";
import ToastContainer from "../../common/ToastContainer";
import TreeView from "../../common/treeView";
import { useLoader } from "../../../context/LoaderContext";
import utils from "../../../utils/utils";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import FileUpload from "../../common/fileupload/FileUpload";
import "../../common/fileupload/FileUpload.css";
import Sidemodal from "../../common/sidemodal/sidemodal";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import CommentSystem from "./commentSystem";
import StudyPlaceholderManager from "./StudyPlaceholderManager";
import {
  canManageStudyPlaceholders,
  ISF_PERMISSIONLESS_ACTIONS,
} from "./isfPermissionUtils";
import MilestoneDashboard from "./MilestoneDashboard";
import IsfSiteSelector from "./IsfSiteSelector";
import IsfStudyGatePicker from "./IsfStudyGatePicker";
import IsfSiteOverviewPanel from "./IsfSiteOverviewPanel";
import StudyGateAdvanceModal from "../../common/StudyGateAdvanceModal/StudyGateAdvanceModal";
import { buildSiteOverviewSnapshot } from "./isfSiteOverviewService";
import {
  fetchStudyMilestones,
  findStudyMilestoneForCode,
  buildMilestoneCodeMap,
  computeSuggestedMilestoneDate,
} from "./isfStudyMilestoneService";
import StudyNumberBadge from "../../common/StudyNumberBadge/StudyNumberBadge";
import StudyGateProgressStrip from "./StudyGateProgressStrip";
import {
  fetchStudyGate,
  gateNameForCode,
  buildStudyGateOptions,
  HISTORICAL_GATE_READONLY_MESSAGE,
  isViewingHistoricalStudyGate,
  normalizeGateCode,
  getGatePickerSelectionMode,
} from "./isfStudyGateService";
import {
  ARCHIVED_STUDY_READONLY_MESSAGE,
  isArchivedStudyReadOnly,
} from "../studymanagement/studyArchivedReadOnly";
import IsfPageTour from "./IsfPageTour";
import { buildIsfPageTourSteps, findFirstTreeNode, treeHasNodeType } from "./isfPageTourSteps";
import { hasCompletedIsfTour } from "./isfPageTourStorage";
import MilestoneSingleOptionPicker from "./MilestoneSingleOptionPicker";
import {
  fetchStudyPlaceholders,
  fetchStudyPlaceholdersForActiveSite,
  fetchStudySourceTemplate,
  ensureStudyPlaceholdersCloned,
  seedStudyPlaceholderAssignees,
  waiveStudyPlaceholder,
  stripGuid,
  formatStudyPlaceholderDueOffset,
  formatStudyPlaceholderTreeLabel,
  findStudyPlaceholderForDocument,
  isMilestoneLinkedStudyPlaceholder,
  studyPlaceholderAwaitingMilestoneDates,
  isPendingStudyPlaceholder,
  studyPlaceholderCanUpload,
  studyPlaceholderCanBeWaived,
  studyPlaceholderRequiresSignature,
  studyPlaceholderMatchesSite,
  canCreateStudyAdHocPlaceholder,
  canEditStudyAdHocPlaceholder,
  canRemoveStudyPlaceholderRow,
  resolveStudyPlaceholderRow,
  getStudyPlaceholderDisplayStatus,
  enrichDocumentsWithPlaceholderLinks,
  consolidateDocumentSignatureRows,
  linkStudyPlaceholderDocument,
  syncBrokenPlaceholderDocumentLinks,
  findLinkableFolderDocuments,
  resolveUploadedStudyDocumentId,
  resolveDocumentIdAfterSupersedeUpload,
  findStudyFolderNameInTree,
  getStudyPlaceholderTreeNodeId,
  findDocumentForStudyPlaceholder,
  buildUniqueUploadDocumentName,
  resolveStudyPlaceholderForDocumentRow,
  validateUploadDocumentName,
  validateUploadExternalDocumentId,
  buildStudyDocumentTree,
  dedupeStudyPlaceholdersForTree,
  augmentPlaceholdersFromDocuments,
  buildMandatorySignUsersFromPlaceholder,
  findStudyPlaceholdersForMilestone,
  resolveMilestoneRefForSelection,
  resolveMilestonePlaceholderForSelection,
  resolveMilestoneLinkedDocument,
  resolveStudyPlaceholderLinkedDocument,
  isAdHocStudyPlaceholder,
  isSupersededStudyDocument,
  computeMilestoneConsolidatedCompleteness,
  buildPlaceholderUploadRoleGroups,
  buildPlaceholderSignatureAvatarGroups,
  buildMilestoneSignerStaffStatusRows,
  isPlaceholderUploadComplete,
  documentIdentityKey,
  buildDocumentSignatureStatePatch,
  patchDocumentRowsWithSignature,
  patchTreeNodesWithSignature,
} from "./isfPlaceholderService";
import {
  getStudyAssociatedSitesLabel,
  resolveStudyAssociatedSites,
} from "./isfStudySiteService";
import IsfSiteSetupWizard from "./IsfSiteSetupWizard";
import IsfStudyPickerModal from "./IsfStudyPickerModal";
import { fetchEisfStudiesForBinder } from "./isfDashboardService";
import {
  filterStudiesForSponsorCro,
  isSponsorCroSession,
  resolveSponsorCroLandingRoute,
  refreshSponsorCroLiveGrants,
  croHasLiveIsfAccessToStudy,
  getCroGrantedStudyIds,
  resolveIsfOpenBinderCategories,
  filterPlaceholdersForCroFolderScope,
  SPONSOR_CRO_EMPTY_HOME_ROUTE,
} from "../../../utils/sponsorCroAccess";
import {
  applyStudySiteSetup,
  buildInitialPerSiteReferenceDates,
  buildSiteSelectOptions,
  buildConfiguredSiteSelectOptions,
  buildStudySiteSetupPayload,
  defaultReferenceDateValue,
  enrichStudySiteRowsFromLookup,
  fetchStudySitesForSetup,
  formatReferenceDateForApi,
  getAllowedStudySites,
  getUniformTemplateNotice,
  hasReferenceDateValue,
  isEisfApiSuccess,
  normalizeStudyFolderResponseData,
  normalizeStudySiteRows,
  resolveCreateFlowSiteId,
  resolveCreateFlowTemplateOption,
  resolvePerSiteTemplateSelection,
  resolveDefaultStudySiteId,
  resolveActiveStudySiteId,
  resolveTemplateOptionForSite,
  saveStudyReferenceDates,
  studySitesNeedSetup,
  validateStudySiteSetup,
} from "./isfSiteSetupService";
import "./isfSiteSetupWizard.scss";
import ResizableDiv from "../../common/resizeablediv/resizeablediv";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import EpsfPageBreadcrumb from "../epsf/EpsfPageBreadcrumb";
import ISFAuditLog from "../../common/auditLog/ISFAuditLog";
import CheckBox from "../../common/checkBox";
import { jsPDF } from "jspdf";
import DataTable from "../../common/dataTable";
import { ACTIONS, getPermissionMessage } from "../../../constants/message";
import { getAllPermissionKeys } from "../../../utils/permissionUtils";
import { fetchRoleLookup } from "../../../services/lookupService";
import {
  buildCapaContextFromIsfDocument,
  normalizeIsfTemplateCategoryForApi,
} from "../capa/capaConstants";
import CapaCreateModal from "../capa/CapaCreateModal";
import { canCreateCapaRecord } from "../capa/capaAccess";
import DualListBox from "../../common/dualListBox";

/** External Document Id only — never falls back to internal GUID / DocumentCode. */
function getExternalDocumentIdDisplay(node) {
  if (!node || typeof node !== "object") return "N/A";
  const internalId = String(
    node.DocumentIdRef || node.id || "",
  ).toLowerCase();
  for (const key of ["ExtDocumentId", "extDocumentId", "EXTDocumentId"]) {
    const v = node[key];
    const trimmed = v != null ? String(v).trim() : "";
    if (!trimmed || trimmed.toUpperCase() === "N/A") continue;
    if (internalId && trimmed.toLowerCase() === internalId) continue;
    return trimmed;
  }
  return "N/A";
}

/** Normalize dashes/spaces so "PA - X" and "PA – X" compare equal. */
function normalizeMilestoneLabelPart(value) {
  return String(value || "")
    .trim()
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/\s+/g, " ");
}

/** Prefer a single clean milestone label (avoid code+name duplication). */
function buildMilestoneDisplayLabel(code, name, fallback = "") {
  const c = normalizeMilestoneLabelPart(code);
  const n = normalizeMilestoneLabelPart(name);
  if (c && n) {
    const cLower = c.toLowerCase();
    const nLower = n.toLowerCase();
    if (cLower === nLower) return n;
    if (nLower.includes(cLower)) return n;
    if (cLower.includes(nLower)) return c;
    return `${c} - ${n}`;
  }
  return c || n || normalizeMilestoneLabelPart(fallback);
}

/** Ellipsis chip: start visible + padding + full text on title (no Bootstrap .badge). */
function InfoBadge({ className = "badge-doc", title, children }) {
  const text = children == null ? "" : String(children);
  const tip = title != null && String(title).trim() !== "" ? String(title) : text;
  return (
    <div className={`isf-info-chip ${className}`} title={tip}>
      <span className="isf-info-chip__text">{text}</span>
    </div>
  );
}

/** UI label: prefer ExtDocumentId when set; else DocumentCode → CertificationDocumentCode → DocumentIdRef → id */
function getDisplayDocumentId(node) {
  if (!node || typeof node !== "object") return "N/A";
  const externalId = getExternalDocumentIdDisplay(node);
  if (externalId !== "N/A") return externalId;
  for (const v of [
    node.DocumentCode,
    node.CertificationDocumentCode,
    node.DocumentIdRef,
    node.id,
  ]) {
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "N/A";
}

function findStudyDocument(documents, nodeId) {
  if (!nodeId || !Array.isArray(documents)) return null;
  const target = String(nodeId).toLowerCase();
  return (
    documents.find(
      (d) => String(d.DocumentIdRef || "").toLowerCase() === target,
    ) || null
  );
}

function getDisplayDocumentType(node, typeOptions = []) {
  if (!node || typeof node !== "object") return "N/A";
  const direct = (node.DocumentType || "").trim();
  if (direct) return direct;
  const typeId = node.DocumentTypeId;
  if (typeId) {
    const match = (typeOptions || []).find(
      (o) => String(o.value).toLowerCase() === String(typeId).toLowerCase(),
    );
    if (match?.label) return match.label;
  }
  return "N/A";
}

function getDisplayDocumentCategory(node, categoryOptions = []) {
  if (!node || typeof node !== "object") return null;
  const direct = (node.DocumentCategory || "").trim();
  if (direct) return direct;
  const categoryId = node.DocumentCategoryId;
  if (categoryId) {
    const match = (categoryOptions || []).find(
      (o) => String(o.value).toLowerCase() === String(categoryId).toLowerCase(),
    );
    if (match?.label) return match.label;
  }
  return null;
}

/** Linked document placeholder label for uploaded files */
function getLinkedPlaceholderLabel(doc, studyPlaceholders = []) {
  const row = findStudyPlaceholderForDocument(doc, studyPlaceholders);
  if (row?.PlaceholderName) return String(row.PlaceholderName).trim();
  return String(doc?.PlaceholderName || doc?.placeholderName || "").trim();
}

function isGuidLikeString(s) {
  if (s == null || typeof s !== "string") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    s.trim(),
  );
}

function formatDisplayDueDate(value) {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function collectMilestoneNodeIdsWithChildren(nodes, ids = []) {
  (nodes || []).forEach((node) => {
    if (
      (node.type === "milestone" || node.type === "placeholder") &&
      node.children?.length
    ) {
      ids.push(node.id);
    }
    if (node.children?.length) {
      collectMilestoneNodeIdsWithChildren(node.children, ids);
    }
  });
  return ids;
}

function MilestoneRoleStatusRow({ roleName, displayName, subtitle, status }) {
  const label = displayName || roleName || "Staff";
  return (
    <div className="isf-milestone-role-row">
      <span
        className="isf-milestone-role-row__avatar"
        style={{ backgroundColor: getAvatarColor(label) }}
        title={label}
      >
        {getInitials(label)}
      </span>
      <span className="isf-milestone-role-row__name">
        {label}
        {subtitle ? (
          <small className="text-muted d-block">{subtitle}</small>
        ) : null}
      </span>
      <span
        className={`isf-milestone-role-row__status badge ${
          status === "Completed" ? "bg-success" : "bg-warning text-dark"
        }`}
      >
        {status}
      </span>
    </div>
  );
}

function MilestoneLibraryStyleAvatars({ items = [], emptyLabel = "None" }) {
  if (!items.length) {
    return (
      <span className="isf-milestone-avatar-stack__empty">{emptyLabel}</span>
    );
  }

  return (
    <div className="milestone-recipient-roles__avatars isf-milestone-readonly-avatars">
      {items.map((item) => {
        const label = item.label || item.roleName || "Role";
        return (
          <span
            key={item.staffId || item.id || label}
            className="milestone-recipient-roles__avatar"
            style={{ backgroundColor: getAvatarColor(label) }}
            title={label}
          >
            {getInitials(label)}
          </span>
        );
      })}
    </div>
  );
}

/** User avatars for signature done / pending — initials from person name, not role. */
function MilestoneUserAvatars({ items = [], emptyLabel = "None" }) {
  if (!items.length) {
    return (
      <span className="isf-milestone-signature-panel__empty">{emptyLabel}</span>
    );
  }

  return (
    <div className="milestone-recipient-roles__avatars isf-milestone-readonly-avatars">
      {items.map((item) => {
        const displayName = item.displayName || item.label || "Staff";
        const title = item.roleName
          ? `${displayName} (${item.roleName})`
          : displayName;
        return (
          <span
            key={item.staffId || displayName}
            className="milestone-recipient-roles__avatar"
            style={{ backgroundColor: getAvatarColor(displayName) }}
            title={title}
          >
            {getInitials(displayName)}
          </span>
        );
      })}
    </div>
  );
}

function SignaturePendingSignLink({ visible, onSign, label = "Sign" }) {
  if (!visible) return null;
  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0 isf-signature-sign-link"
      onClick={onSign}
    >
      {label}
    </button>
  );
}

/**
 * Human-readable certifier for UI/PDF. Prefer CertifiedByName; never show StaffId GUID as the "name".
 */
function getCertifiedByDisplayName(node, currentUser) {
  if (!node) return "—";
  const name = (node.CertifiedByName || "").trim();
  if (name && !isGuidLikeString(name)) return name;
  const id = node.CertifiedBy;
  if (id == null || id === "") return "—";
  if (
    currentUser?.StaffId &&
    String(currentUser.StaffId).toLowerCase() === String(id).toLowerCase()
  ) {
    return (
      (currentUser.UserName || "").trim() ||
      (currentUser.EmailId || "").trim() ||
      "—"
    );
  }
  if (isGuidLikeString(String(id))) return "—";
  return String(id);
}

/** Case-insensitive signer / signed-user id match (API GUIDs may differ only by case). */
function normalizeSignerIdForCompare(id) {
  return String(id ?? "")
    .trim()
    .toLowerCase();
}

/** Comma-separated ids from API → normalized lowercase tokens */
function parseSignerIdList(raw) {
  if (raw == null) return [];
  return String(raw)
    .split(",")
    .map((s) => normalizeSignerIdForCompare(s))
    .filter(Boolean);
}

/** Backend may use PascalCase or camelCase (e.g. signedIds vs SignedUserIds). */
function getMandatorySignUserIdsRaw(node) {
  if (!node || typeof node !== "object") return "";
  return (
    node.MandatorySignUserIds ||
    node.mandatorySignUserIds ||
    node.MandatorySignUserId ||
    node.mandatorySignUserId ||
    ""
  );
}

function getSignedUserIdsRawFromNode(node) {
  if (!node || typeof node !== "object") return "";
  return (
    node.SignedUserIds ||
    node.signedIds ||
    node.signedUserIds ||
    node.SignedIds ||
    node.SignedByIds ||
    node.signedByIds ||
    node.SignedUserId ||
    node.signedUserId ||
    ""
  );
}

const ISF = ({ lockedCategory: lockedCategoryProp = null } = {}) => {
  const ACTION_PERMISSION_MAP = {
    newFolder: "CREATE_FOLDER",
    subFolder: "CREATE_SUB_FOLDER",
    renameFolder: "UPDATE_FOLDER",
    moveFolder: "MOVE_FOLDER",
    deleteFolder: "REMOVE_FOLDER",
    generateCertification: "GENERATE_CERTIFICATION",
    createCapa: "ADD_CAPA",
  };

  const hasActionPermission = (action, permissions) => {
    if (ISF_PERMISSIONLESS_ACTIONS.has(action)) return true;
    const requiredPermission = ACTION_PERMISSION_MAP[action];
    if (!requiredPermission) return true;
    return permissions.includes(requiredPermission);
  };

  const allowedExtensions = [
    // Documents
    "doc",
    "docx",
    "pdf",

    // Spreadsheets
    "xls",
    "xlsx",
    "csv",

    // Presentations
    "ppt",
    "pptx",
    "odp",

    // Images
    "jpg",
    "jpeg",
    "png",
    "gif",
  ];
  // Document type options will be loaded from DMS DocumentTypeActivity
  const [dmsDocumentTypeOptions, setDmsDocumentTypeOptions] = useState([
    { label: "Select document type", value: "" },
  ]);
  const [dmsDocumentCategoryOptions, setDmsDocumentCategoryOptions] = useState([
    { label: "Select document category", value: "" },
  ]);
  const { userData, refreshUserData } = useUserData();
  const isCroReadOnlyViewer = isSponsorCroSession(userData);
  // System Administrator gets all permissions (same as Sidebar); others use backend Permission string
  const permissions =
    userData?.Role === "System Administrator"
      ? getAllPermissionKeys()
      : (userData?.Permission?.split(",") || [])
          .map((p) => p.trim())
          .filter(Boolean);
  const canCreateCapa =
    userData?.Role === "System Administrator" ||
    permissions.includes("ADD_CAPA");
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { updateRecentVisitSubtitle } = useVisitTracking();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const lockedCategory =
    lockedCategoryProp === "ePSF" || lockedCategoryProp === "eISF"
      ? lockedCategoryProp
      : null;
  const binderHomePath = lockedCategory === "ePSF" ? "/epsf/binder" : "/isf";
  const appliedUrlParamsRef = useRef(false);
  const lastUrlStudyIdRef = useRef(null);
  const justNavigatedFromGoRef = useRef(false);
  const commentDeepLinkAppliedRef = useRef(null);

  const studyIdFromUrlInitial = searchParams.get("studyId");
  const [showStudyListPopup, setShowStudyListPopup] = useState(
    () => !studyIdFromUrlInitial,
  );
  const [isViewReady, setIsViewReady] = useState(false);
  const [urlBootstrapLoading, setUrlBootstrapLoading] = useState(
    () => !!studyIdFromUrlInitial,
  );
  /** Full-page CRO access gate: { kind: 'all' | 'study' } */
  const [croAccessGate, setCroAccessGate] = useState(null);
  const [showStudyBasicInfo, setShowStudyBasicInfo] = useState(true);
  const [showIsfTour, setShowIsfTour] = useState(false);
  const isfTourUploadDemoRef = useRef(false);
  const [studies, setStudies] = useState([]);
  const [studiesLoading, setStudiesLoading] = useState(
    () => !!studyIdFromUrlInitial,
  );
  const [options, setOptions] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [studyRoleOptions, setStudyRoleOptions] = useState([]);

  useEffect(() => {
    if (!userData?.OrganizationId || !userData?.StaffId) {
      setStudyRoleOptions([]);
      return;
    }
    fetchRoleLookup(userData)
      .then((rows) => setStudyRoleOptions(rows || []))
      .catch(() => setStudyRoleOptions([]));
  }, [userData?.OrganizationId, userData?.StaffId]);
  useEffect(() => {
    const loadDocumentTypes = async () => {
      if (!userData?.OrganizationId || !userData?.StaffId) return;
      try {
        const list = await fetchEisfDocumentTypes(userData);
        const opts = [
          { label: "Select document type", value: "" },
          ...(list || []).map((row) => ({
            label: row.DocumentType,
            value: row.DocumentTypeId,
          })),
        ];
        setDmsDocumentTypeOptions(opts);
      } catch (error) {
        console.error("Failed to load document types for ISF upload:", error);
      }
    };
    loadDocumentTypes();
    const loadDocumentCategories = async () => {
      if (!userData?.OrganizationId || !userData?.StaffId) return;
      try {
        const list = await fetchEisfDocumentCategories(userData);
        const opts = [
          { label: "Select document category", value: "" },
          ...(list || []).map((row) => ({
            label: row.DocumentCategory,
            value: row.DocumentCategoryId,
          })),
        ];
        setDmsDocumentCategoryOptions(opts);
      } catch (error) {
        console.error("Failed to load document categories for ISF upload:", error);
      }
    };
    loadDocumentCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, userData?.StaffId]);

  // Study list — background load (cached); binder modal opens instantly when cached
  useEffect(() => {
    if (userData) {
      getAllStudies(undefined, {
        withLoader: false,
        force: isSponsorCroSession(userData),
      });
    }
  }, [userData]);

  useEffect(() => {
    if (showStudyListPopup && isSponsorCroSession(userData)) {
      (async () => {
        const sessionUser = await refreshSponsorCroLiveGrants(userData);
        refreshUserData?.();
        await getAllStudies(undefined, { withLoader: false, force: true });
        const grantedIds = getCroGrantedStudyIds(sessionUser);
        if (!grantedIds || grantedIds.size === 0) {
          setCroAccessGate({ kind: "all" });
          setShowStudyListPopup(false);
          return;
        }
        const currentStudyId = formData?.study?.value;
        if (currentStudyId) {
          const studyRow =
            studies.find(
              (row) =>
                String(row.StudyId).toLowerCase() ===
                String(currentStudyId).toLowerCase(),
            ) || {
              StudyId: currentStudyId,
              StudyName: formData?.study?.label,
            };
          syncOpenBinderCategoriesForStudy(studyRow, sessionUser);
        }
      })();
    }
    // Reload live grants + binder categories each time the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStudyListPopup]);

  const getAllStudies = async (_siteData, options = {}) => {
    const { withLoader = false, force = false } = options;
    if (!userData?.OrganizationId || !userData?.StaffId) {
      return { data: [] };
    }
    try {
      if (withLoader) {
        showLoader("Studies are loading");
      } else {
        setStudiesLoading(true);
      }
      const { studies: list, options: studyOpts } = await fetchEisfStudiesForBinder({
        organizationId: userData.OrganizationId,
        createdBy: userData.StaffId,
        force,
      });
      const visibleList = filterStudiesForSponsorCro(list, userData);
      setStudies(visibleList);
      setOptions(
        isSponsorCroSession(userData)
          ? (studyOpts || []).filter((opt) =>
              visibleList.some(
                (row) =>
                  String(row?.StudyId || "").toLowerCase() ===
                  String(opt?.value || "").toLowerCase(),
              ),
            )
          : studyOpts,
      );
      return { data: list };
    } catch (error) {
      console.error("Error fetching study:", error);
      throw error;
    } finally {
      if (withLoader) {
        hideLoader();
      } else {
        setStudiesLoading(false);
      }
    }
  };
  const [templateList, setTemplateList] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const templatesFetchPromiseRef = useRef(null);

  const getAllTemplates = async ({ silent = true } = {}) => {
    if (templateList.length) return templateList;
    if (templatesFetchPromiseRef.current) {
      return templatesFetchPromiseRef.current;
    }
    const promise = (async () => {
      try {
        if (silent) {
          setTemplatesLoading(true);
        } else {
          showLoader("Templates are loading");
        }
        const response = await apiClient.post(
          API_ENDPOINTS.ISF.GET_STUDY_FOLDERS,
          {
            OrganizationIdRef: userData.OrganizationId,
            ActionType: "GetAllTemplates",
          },
        );
        const templates = response?.data?.Templates || [];
        setTemplateList(templates);
        setTemplateOptions([]);
        return templates;
      } catch (error) {
        console.error("Error fetching templates:", error);
        throw error;
      } finally {
        if (silent) {
          setTemplatesLoading(false);
        } else {
          hideLoader();
        }
        templatesFetchPromiseRef.current = null;
      }
    })();
    templatesFetchPromiseRef.current = promise;
    return promise;
  };

  const [templateCategoryCreationOptions, setTemplateCategoryCreationOptions] =
    useState([
      { label: "eISF", value: "eISF" },
      { label: "ePSF", value: "ePSF" },
    ]);

  const [formData, setFormData] = useState({
    study: "",
    templateCategory: "",
    selectedtTmplate: "",
    haseISF: false,
    hasePSF: false,
    isfCreationCategory: "",
    templateCategoryCreation: "",
    selectedTemplateOption: "",
    previousDoc: "",
    isMandatorySign: false,
    mandatorySignUsers: [],
    documentCode: "",
    uploadFileName: "",
    documentType: "",
    documentCategory: "",
    expiryDate: null,
    searchTags: "",
  });
  const [activeCategory, setActiveCategory] = useState("");

  const [createModalSites, setCreateModalSites] = useState([]);
  const [createModalSitesLoading, setCreateModalSitesLoading] = useState(false);
  const [sameTemplateForAllSites, setSameTemplateForAllSites] = useState(true);
  const [perSiteTemplateOptions, setPerSiteTemplateOptions] = useState({});
  const [createReferenceDate, setCreateReferenceDate] = useState(() =>
    defaultReferenceDateValue(),
  );
  const [perSiteReferenceDates, setPerSiteReferenceDates] = useState({});

  const [studySourceTemplate, setStudySourceTemplate] = useState(null);
  const [studyPlaceholders, setStudyPlaceholders] = useState([]);
  const [studyGate, setStudyGate] = useState(null);
  const [studyGateList, setStudyGateList] = useState([]);
  const [viewGateCode, setViewGateCode] = useState(null);
  const [showGateAdvanceModal, setShowGateAdvanceModal] = useState(false);
  const prevStudyGateCodeRef = useRef(null);
  const pendingSignatureRefreshRef = useRef(null);

  const [studyDetails, setStudyDetails] = useState({
    PIName: "",
    StudyName: "",
    StudyNumber: "",
    StudySchColor: "",
    StudyColor: "",
    StudyStatus: "",
    SiteName: "",
    MappedSiteNames: "",
    MappedSiteIds: "",
  });

  const isStudyArchivedReadOnlyFlag = useMemo(
    () => isArchivedStudyReadOnly(studyDetails),
    [studyDetails],
  );

  const canEditCurrentStudyGate = useMemo(
    () =>
      !isCroReadOnlyViewer &&
      !isStudyArchivedReadOnlyFlag &&
      !isViewingHistoricalStudyGate(
        viewGateCode,
        studyGate?.CurrentGateCode || studyGate?.currentGateCode,
      ),
    [isCroReadOnlyViewer, viewGateCode, studyGate, isStudyArchivedReadOnlyFlag],
  );

  const canUploadToPlaceholder = useCallback(
    (node) => {
      if (!canEditCurrentStudyGate) return false;
      const row = resolveStudyPlaceholderRow(node, studyPlaceholders);
      return studyPlaceholderCanUpload(row, userData, {
        staffList,
        roleOptions: studyRoleOptions,
      });
    },
    [
      canEditCurrentStudyGate,
      studyPlaceholders,
      userData,
      staffList,
      studyRoleOptions,
    ],
  );
  const [uploadPlaceholderId, setUploadPlaceholderId] = useState("");
  const [uploadPlaceholderLocked, setUploadPlaceholderLocked] = useState(false);
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState("");
  const [uploadSupersedeDocumentId, setUploadSupersedeDocumentId] = useState("");
  const [uploadFileKey, setUploadFileKey] = useState(0);
  const [waiveReason, setWaiveReason] = useState("");
  const [showWaiveModal, setShowWaiveModal] = useState(false);
  const [waiveTarget, setWaiveTarget] = useState(null);

  const [templateCategoryoptions, setTemplateCategoryOptions] = useState([
    { label: "eISF", value: "eISF" },
    { label: "ePSF", value: "ePSF" },
  ]);

  const prepareCreationForm = (studyFlags = {}) => {
    const hasEisf = studyFlags.haseISF ?? formData.haseISF;
    const hasEpsf = studyFlags.hasePSF ?? formData.hasePSF;
    const both = [
      { label: "eISF", value: "eISF" },
      { label: "ePSF", value: "ePSF" },
    ];
    const singleEPSF = { label: "ePSF", value: "ePSF" };
    const singleEISF = { label: "eISF", value: "eISF" };

    if (lockedCategory === "ePSF") {
      setTemplateCategoryCreationOptions([singleEPSF]);
      setFormData((prev) => ({
        ...prev,
        templateCategoryCreation: singleEPSF,
      }));
      return;
    }
    if (lockedCategory === "eISF") {
      setTemplateCategoryCreationOptions([singleEISF]);
      setFormData((prev) => ({
        ...prev,
        templateCategoryCreation: singleEISF,
      }));
      return;
    }

    if (hasEisf && !hasEpsf) {
      setTemplateCategoryCreationOptions([singleEPSF]);
      setFormData((prev) => ({
        ...prev,
        templateCategoryCreation: singleEPSF,
        selectedTemplateOption: "",
      }));
    } else if (hasEpsf && !hasEisf) {
      setTemplateCategoryCreationOptions([singleEISF]);
      setFormData((prev) => ({
        ...prev,
        templateCategoryCreation: singleEISF,
        selectedTemplateOption: "",
      }));
    } else {
      setTemplateCategoryCreationOptions(both);
      setFormData((prev) => ({
        ...prev,
        templateCategoryCreation: "",
        selectedTemplateOption: "",
      }));
    }

    setTemplateOptions([]);
    setSameTemplateForAllSites(true);
    setPerSiteTemplateOptions({});
    setCreateReferenceDate(defaultReferenceDateValue());
    setPerSiteReferenceDates({});
    setCreateModalSites([]);
  };

  const applyStudySelection = (study) => {
    if (!study) return;

    const selectedStudy = {
      label: study.StudyName,
      value: study.StudyId,
    };

    setShowCreatePanel(false);
    setViewGateCode(null);
    seteISF(false);

    const croSession = isSponsorCroSession(userData);
    let { hasEisf, hasEpsf, categoryOptions, defaultCategory } =
      resolveIsfOpenBinderCategories(userData, study);

    if (lockedCategory === "ePSF") {
      hasEisf = false;
      categoryOptions = hasEpsf ? [{ label: "ePSF", value: "ePSF" }] : [];
      defaultCategory = hasEpsf ? { label: "ePSF", value: "ePSF" } : "";
    } else if (lockedCategory === "eISF") {
      hasEpsf = false;
      categoryOptions = hasEisf ? [{ label: "eISF", value: "eISF" }] : [];
      defaultCategory = hasEisf ? { label: "eISF", value: "eISF" } : "";
    }

    if (hasEisf || hasEpsf) {
      seteISF(true);
      setTemplateCategoryOptions(categoryOptions);
      setFormData((prev) => ({
        ...prev,
        study: selectedStudy,
        haseISF: hasEisf,
        hasePSF: hasEpsf,
        templateCategory: defaultCategory,
        templateCategoryCreation: "",
        selectedTemplateOption: "",
      }));
    } else {
      setTemplateCategoryOptions([]);
      setFormData((prev) => ({
        ...prev,
        study: selectedStudy,
        haseISF: false,
        hasePSF: false,
        templateCategory: "",
        templateCategoryCreation: "",
        selectedTemplateOption: "",
      }));
      if (!croSession) {
        setShowCreatePanel(true);
        prepareCreationForm({ haseISF: false, hasePSF: false });
      }
    }
  };

  const syncOpenBinderCategoriesForStudy = useCallback(
    (study, sessionUser = userData) => {
      if (!study) return;
      let { hasEisf, hasEpsf, categoryOptions, defaultCategory } =
        resolveIsfOpenBinderCategories(sessionUser, study);
      if (lockedCategory === "ePSF") {
        hasEisf = false;
        categoryOptions = hasEpsf ? [{ label: "ePSF", value: "ePSF" }] : [];
        defaultCategory = hasEpsf ? { label: "ePSF", value: "ePSF" } : "";
      } else if (lockedCategory === "eISF") {
        hasEpsf = false;
        categoryOptions = hasEisf ? [{ label: "eISF", value: "eISF" }] : [];
        defaultCategory = hasEisf ? { label: "eISF", value: "eISF" } : "";
      }
      setTemplateCategoryOptions(categoryOptions);
      setFormData((prev) => {
        const currentValue = String(
          prev.templateCategory?.value || prev.templateCategory || "",
        ).toLowerCase();
        const stillValid = categoryOptions.some(
          (opt) => String(opt.value).toLowerCase() === currentValue,
        );
        return {
          ...prev,
          haseISF: hasEisf,
          hasePSF: hasEpsf,
          templateCategory: stillValid
            ? prev.templateCategory
            : defaultCategory || "",
        };
      });
      seteISF(Boolean(hasEisf || hasEpsf));
    },
    [userData, lockedCategory],
  );

  const handleSelectStudyRecord = (study) => {
    applyStudySelection(study);
  };

  const handleSelect = (selectedStudy) => {
    const study = studies.find(
      (item) =>
        String(item.StudyId).toLowerCase() ===
        String(selectedStudy.value).toLowerCase(),
    );
    if (study) applyStudySelection(study);
  };

  const handleRequestCreate = ({ cancel } = {}) => {
    if (cancel) {
      setShowCreatePanel(false);
      return;
    }
    if (isSponsorCroSession(userData)) {
      return;
    }
    if (!permissions.includes("CREATE_ISF")) {
      showAlert({
        title: "Warning",
        message: getPermissionMessage(ACTIONS.CREATE_ISF),
        type: "warning",
      });
      return;
    }
    prepareCreationForm();
    setShowCreatePanel(true);
  };

  const handleCreateStudy = () => {
    if (!permissions.includes("CREATE_ISF")) {
      showAlert({
        title: "Warning",
        message: getPermissionMessage(ACTIONS.CREATE_ISF),
        type: "warning",
      });
      return;
    }
    redirectToISFScreen({ mode: "create" });
  };

  const handlePerSiteTemplateChange = (siteId, opt) => {
    const result = resolvePerSiteTemplateSelection(
      createModalSites,
      perSiteTemplateOptions,
      siteId,
      opt,
    );
    setPerSiteTemplateOptions(result.perSiteTemplates);
  };

  const handlePerSiteReferenceDateChange = (siteId, value) => {
    setPerSiteReferenceDates((prev) => ({ ...prev, [siteId]: value }));
  };

  const createModalTemplateInfo = useMemo(
    () =>
      getUniformTemplateNotice(
        createModalSites,
        perSiteTemplateOptions,
        sameTemplateForAllSites,
      ),
    [createModalSites, perSiteTemplateOptions, sameTemplateForAllSites],
  );

  const handleSelectTemplateCategory = (value) => {
    setFormData((prev) => ({
      ...prev,
      templateCategory: value || "",
    }));
  };

  const handleSelectTemplateCategoryCreation = (value) => {
    const categoryValue = value?.value ?? value ?? "";
    setFormData((prev) => ({
      ...prev,
      templateCategoryCreation: value || "",
      selectedTemplateOption: "", // reset template when category changes
    }));
    // Filter templates by selected category (eISF / ePSF); show only templates for this category
    if (templateList.length && categoryValue) {
      const filtered = templateList.filter(
        (t) =>
          String(t.TemplateCategory || t.Category || "").toLowerCase() ===
          String(categoryValue).toLowerCase(),
      );
      setTemplateOptions(
        filtered.length
          ? utils.mapToOptions(filtered, "TemplateName", "TemplateId")
          : [],
      );
    } else {
      setTemplateOptions([]);
    }
  };

  //step 3 open that screen and got the data
  const redirectToISFScreen = async ({ mode = "open" } = {}) => {
    if (!formData?.study?.value) {
      showAlert({
        title: "Error",
        message: "Please select a study",
        type: "danger",
      });
      return;
    }

    const studyId = formData.study.value;
    const category =
      formData.templateCategoryCreation?.value ||
      formData.templateCategory?.value ||
      "";

    if (!category) {
      showAlert({
        title: "Error",
        message: "Please select a template category",
        type: "danger",
      });
      return;
    }

    const isCreateFlow = mode === "create";
    const createSites = (createModalSites || []).filter((row) => row.SiteId);
    const isMultiSiteCreate = isCreateFlow && createSites.length > 1;
    // Site setup saves reference date + config on create (single- and multi-site).
    const shouldApplySiteSetup = isCreateFlow && createSites.length >= 1;

    if (isCreateFlow && !shouldApplySiteSetup && !formData.selectedTemplateOption?.value) {
      showAlert({
        title: "Error",
        message: "Please select a template",
        type: "danger",
      });
      return;
    }

    if (isCreateFlow && !shouldApplySiteSetup && !hasReferenceDateValue(createReferenceDate)) {
      showAlert({
        title: "Error",
        message: "Please select a reference date",
        type: "danger",
      });
      return;
    }

    if (shouldApplySiteSetup) {
      const setupValidation = validateStudySiteSetup({
        siteRows: createSites,
        sameTemplateForAllSites,
        sharedTemplateId: formData.selectedTemplateOption?.value,
        perSiteTemplates: perSiteTemplateOptions,
        sharedReferenceDate: createReferenceDate,
        perSiteReferenceDates,
      });
      if (!setupValidation.valid) {
        showAlert({
          title: "Error",
          message: setupValidation.message,
          type: "danger",
        });
        return;
      }
    }

    try {
      let setupPayload = null;
      if (shouldApplySiteSetup) {
        showLoader("Setting up study sites…");
        setupPayload = buildStudySiteSetupPayload({
          siteRows: createSites,
          sameTemplateForAllSites,
          sharedTemplateId: formData.selectedTemplateOption?.value,
          perSiteTemplates: perSiteTemplateOptions,
          sharedReferenceDate: createReferenceDate,
          perSiteReferenceDates,
        });
        const setupResponse = await applyStudySiteSetup({
          organizationId: userData.OrganizationId,
          studyId,
          templateCategory: category,
          createdBy: userData.StaffId,
          sameTemplateForAllSites: setupPayload.sameTemplateForAllSites,
          defaultTemplateId: setupPayload.defaultTemplateId,
          sites: setupPayload.sites,
        });
        if (!isEisfApiSuccess(setupResponse)) {
          throw new Error(
            utils.getApiResponseMessage(
              setupResponse,
              "Site setup failed. Please try again or contact your system administrator.",
            ),
          );
        }
      }

      const templateOptionForLoad = resolveCreateFlowTemplateOption({
        isMultiSiteCreate,
        sharedTemplateOption: formData.selectedTemplateOption,
        createSites,
        perSiteTemplates: perSiteTemplateOptions,
        sameTemplateForAllSites,
        templateOptions,
      });

      justNavigatedFromGoRef.current = true;
      cleanupEditor();
      setEditorSessionKey((k) => k + 1);
      setImagePreviewUrl(null);
      setSelectedNode(null);
      const createFlowSiteId =
        resolveCreateFlowSiteId(createSites, null) || null;
      const pendingRefDate =
        isCreateFlow && hasReferenceDateValue(createReferenceDate)
          ? formatReferenceDateForApi(createReferenceDate)
          : null;
      navigate(`/isf?studyId=${studyId}&category=${category}`, {
        replace: true,
        state: pendingRefDate
          ? {
              pendingReferenceDate: pendingRefDate,
              pendingReferenceSiteId: createFlowSiteId,
            }
          : undefined,
      });
      if (pendingRefDate) {
        setOptimisticSiteReferenceDate(pendingRefDate);
      }
      if (formData.templateCategoryCreation || formData.templateCategory) {
        setActiveCategory(
          formData.templateCategoryCreation || formData.templateCategory,
        );
      }
      seteISF(true);
      setShowStudyListPopup(false);
      setShowCreatePanel(false);

      const siteContext = await loadStudySiteContext(studyId, category);
      const siteIdForLoad =
        resolveCreateFlowSiteId(createSites, siteContext) ??
        siteContext?.defaultSiteId;
      const templateForLoad =
        templateOptionForLoad ?? siteContext?.defaultTemplateOption;
      await getStudyData(studyId, category, templateForLoad, {
        siteIdRef: siteIdForLoad,
        siteRows: siteContext?.rows,
      });

      if (isCreateFlow && hasReferenceDateValue(createReferenceDate)) {
        const refDate =
          formatReferenceDateForApi(createReferenceDate) ||
          defaultReferenceDateValue();
        const siteIdForRefSave =
          siteIdForLoad ||
          resolveCreateFlowSiteId(createSites, siteContext) ||
          siteContext?.defaultSiteId;
        try {
          if (!siteIdForRefSave) {
            throw new Error(
              "Could not resolve the study site for saving the reference date.",
            );
          }
          const refSaveResponse = await saveStudyReferenceDates({
            organizationId: userData.OrganizationId,
            studyId,
            templateCategory: category,
            createdBy: userData.StaffId,
            siteIdRef: siteIdForRefSave,
            referenceDate: refDate,
          });
          if (!isEisfApiSuccess(refSaveResponse)) {
            throw new Error(
              utils.getApiResponseMessage(
                refSaveResponse,
                "Reference date could not be saved after binder create.",
              ),
            );
          }
          await getStudyData(studyId, category, templateForLoad, {
            withLoader: false,
            siteIdRef: siteIdForRefSave,
            siteRows: siteContext?.rows,
            skipRepair: true,
          });
        } catch (refErr) {
          showAlert({
            title: "Reference date not saved",
            message: utils.sanitizeUserFacingMessage(
              refErr?.message,
              "The binder was created but the reference date was not saved. Contact your administrator if this continues.",
            ),
            type: "warning",
          });
        }
      }

      try {
        const res = await getAllStudies(undefined, { withLoader: false, force: true });
        const updatedStudies = res?.data ?? [];
        const updated = updatedStudies.find(
          (s) =>
            String(s.StudyId).toLowerCase() === String(studyId).toLowerCase(),
        );
        if (updated) {
          if (isSponsorCroSession(userData)) {
            syncOpenBinderCategoriesForStudy(updated, userData);
          } else {
            setFormData((prev) => ({
              ...prev,
              haseISF: !!updated.HaseISFTemplate,
              hasePSF: !!updated.HasePSFTemplate,
            }));
            if (updated.HaseISFTemplate && !updated.HasePSFTemplate) {
              setTemplateCategoryOptions([{ label: "ePSF", value: "ePSF" }]);
            } else if (updated.HasePSFTemplate && !updated.HaseISFTemplate) {
              setTemplateCategoryOptions([{ label: "eISF", value: "eISF" }]);
            } else if (updated.HaseISFTemplate && updated.HasePSFTemplate) {
              setTemplateCategoryOptions([
                { label: "eISF", value: "eISF" },
                { label: "ePSF", value: "ePSF" },
              ]);
            } else {
              setTemplateCategoryOptions([
                { label: "eISF", value: "eISF" },
                { label: "ePSF", value: "ePSF" },
              ]);
            }
          }
        }
      } catch (e) {
        console.error("Error refetching studies after create:", e);
      }
      setIsViewReady(true);
    } catch (error) {
      showAlert({
        title: "Error",
        message: utils.sanitizeUserFacingMessage(
          error?.message,
          "Failed to create eISF/ePSF. Please try again or contact your system administrator.",
        ),
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const [treeData, setTreeData] = useState([]);
  const [treeForceExpandedIds, setTreeForceExpandedIds] = useState([]);
  const [studySiteRows, setStudySiteRows] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedSiteOption, setSelectedSiteOption] = useState(null);
  const [showSiteSetupWizard, setShowSiteSetupWizard] = useState(false);
  const [studyMilestones, setStudyMilestones] = useState([]);
  const [optimisticSiteReferenceDate, setOptimisticSiteReferenceDate] =
    useState("");
  const pendingReferenceSaveRef = useRef(false);

  const getActiveSiteIdRef = useCallback(
    () => resolveActiveStudySiteId(studySiteRows, userData, selectedSiteId),
    [studySiteRows, userData, selectedSiteId],
  );

  const configuredSiteOptions = useMemo(
    () => buildConfiguredSiteSelectOptions(studySiteRows, userData),
    [studySiteRows, userData],
  );

  const activeSiteLabel = useMemo(
    () =>
      selectedSiteOption?.label ||
      configuredSiteOptions.find(
        (opt) =>
          String(opt.value).toLowerCase() ===
          String(selectedSiteId || "").toLowerCase(),
      )?.label ||
      studyDetails?.SiteName ||
      "",
    [
      selectedSiteOption,
      configuredSiteOptions,
      selectedSiteId,
      studyDetails?.SiteName,
    ],
  );
  const [documents, setDocuments] = useState([]);

  // Load studies when binder modal opens only if list is still empty
  useEffect(() => {
    if (!showStudyListPopup || !userData?.OrganizationId) return;
    if (studies.length > 0) return;
    getAllStudies(undefined, { withLoader: false, force: false }).catch(() => {});
  }, [
    showStudyListPopup,
    userData?.OrganizationId,
    userData?.StaffId,
    studies.length,
  ]);

  // When opening the study modal, reset its fields to the currently active study/category
  useEffect(() => {
    if (!showStudyListPopup) return;

    const currentStudyId =
      studyDetails?.StudyId || formData?.study?.value || null;
    let studyOption = formData.study;
    if (currentStudyId && options.length) {
      const found = options.find(
        (o) =>
          String(o.value).toLowerCase() ===
          String(currentStudyId).toLowerCase(),
      );
      if (found) {
        studyOption = found;
      }
    }

    setFormData((prev) => ({
      ...prev,
      study: studyOption || prev.study,
      templateCategory: activeCategory || prev.templateCategory,
    }));
  }, [showStudyListPopup, options, studyDetails?.StudyId, activeCategory]);

  const loadStudySiteContext = async (studyId, categoryVal) => {
    if (!studyId || !userData?.OrganizationId) {
      setStudySiteRows([]);
      setSelectedSiteId(null);
      setSelectedSiteOption(null);
      setShowSiteSetupWizard(false);
      return null;
    }

    try {
      const response = await fetchStudySitesForSetup({
        organizationId: userData.OrganizationId,
        studyId,
        templateCategory: categoryVal || "",
        createdBy: userData.StaffId,
      });
      const rows = await enrichStudySiteRowsFromLookup(
        normalizeStudySiteRows(response),
        userData,
      );
      const allowed = getAllowedStudySites(rows, userData, studyId);
      const croViewer = isSponsorCroSession(userData);
      setStudySiteRows(croViewer ? allowed : allowed.length ? allowed : rows);

      const needsSetup = studySitesNeedSetup(rows);
      setShowSiteSetupWizard(!croViewer && needsSetup);

      const defaultSiteId = resolveDefaultStudySiteId(
        allowed.length ? allowed : rows,
        userData,
      );
      const defaultTemplateOption = resolveTemplateOptionForSite(
        rows,
        defaultSiteId,
      );
      setSelectedSiteId(defaultSiteId);
      const optionsForSite = buildConfiguredSiteSelectOptions(rows, userData);
      setSelectedSiteOption(
        optionsForSite.find(
          (opt) => String(opt.value).toLowerCase() === String(defaultSiteId || "").toLowerCase(),
        ) || null,
      );
      return { rows, defaultSiteId, needsSetup, defaultTemplateOption };
    } catch (error) {
      console.warn("Could not load study site context:", error);
      setStudySiteRows([]);
      setSelectedSiteId(null);
      setSelectedSiteOption(null);
      setShowSiteSetupWizard(false);
      return null;
    }
  };

  const handleSiteChange = async (opt) => {
    setSelectedSiteOption(opt || null);
    const nextSiteId = opt?.value || null;
    setSelectedSiteId(nextSiteId);
    setSelectedNode(null);
    setSelectedId("");
    cleanupEditor();
    setEditorSessionKey((k) => k + 1);
    setImagePreviewUrl(null);
    const templateOpt = resolveTemplateOptionForSite(studySiteRows, nextSiteId);
    await getStudyData(undefined, undefined, templateOpt, {
      withLoader: true,
      siteIdRef: nextSiteId,
      siteRows: studySiteRows,
    });
  };

  const handleSiteSetupApplied = async () => {
    setShowSiteSetupWizard(false);
    const studyId = formData?.study?.value;
    const categoryVal =
      activeCategory?.value ||
      formData?.templateCategory?.value ||
      formData?.templateCategoryCreation?.value ||
      "";
    const siteContext = await loadStudySiteContext(studyId, categoryVal);
    const activeSiteId =
      siteContext?.defaultSiteId ?? selectedSiteId ?? getActiveSiteIdRef();

    if (studyId && userData?.OrganizationId && activeSiteId) {
      try {
        await seedStudyPlaceholderAssignees({
          organizationId: userData.OrganizationId,
          studyId,
          templateCategory: categoryVal,
          siteIdRef: activeSiteId,
          createdBy: userData.StaffId,
        });
      } catch (seedErr) {
        console.warn("Could not seed placeholder assignees after site setup:", seedErr);
      }
    }

    await getStudyData(studyId, categoryVal, siteContext?.defaultTemplateOption, {
      siteIdRef: activeSiteId,
      siteRows: siteContext?.rows,
    });
  };

  const getStudyData = async (
    overrideStudyId,
    overrideCategoryValue,
    overrideTemplateOption,
    options = {},
  ) => {
    const {
      withLoader = true,
      siteIdRef: siteIdOverride,
      siteRows: siteRowsOverride,
      skipRepair = false,
      viewGateCode: viewGateOverride,
      folderLoadRetried = false,
    } = options;
    const studyId = overrideStudyId ?? formData?.study?.value;
    const categoryVal =
      overrideCategoryValue ??
      (formData?.templateCategoryCreation
        ? formData?.templateCategoryCreation.value
        : formData?.templateCategory?.value) ??
      "";

    const rowsForSite = siteRowsOverride ?? studySiteRows;
    let activeSiteId =
      siteIdOverride !== undefined ? siteIdOverride : selectedSiteId;
    activeSiteId = resolveActiveStudySiteId(
      rowsForSite,
      userData,
      activeSiteId,
    );
    if (!activeSiteId && rowsForSite.length) {
      activeSiteId = resolveDefaultStudySiteId(rowsForSite, userData);
    }

    let templateOption =
      overrideTemplateOption ||
      resolveTemplateOptionForSite(rowsForSite, activeSiteId) ||
      formData?.selectedTemplateOption ||
      studySourceTemplate ||
      null;

    const resolveTemplateForLoad = async () => {
      if (
        templateOption?.value ||
        !studyId ||
        !userData?.OrganizationId ||
        !categoryVal
      ) {
        return templateOption;
      }
      try {
        const src = await fetchStudySourceTemplate({
          organizationId: userData.OrganizationId,
          studyId,
          templateCategory: categoryVal,
          createdBy: userData.StaffId,
          siteIdRef: activeSiteId || null,
        });
        if (src?.TemplateId) {
          return {
            value: src.TemplateId,
            label: src.TemplateName || "",
          };
        }
      } catch {
        /* non-blocking */
      }
      return templateOption;
    };

    const ensureBinderPlaceholdersReady = async (resolvedTemplate) => {
      const organizationId = stripGuid(userData?.OrganizationId);
      const createdBy = stripGuid(userData?.StaffId);
      const studyIdRef = stripGuid(studyId);
      if (
        !studyIdRef ||
        !organizationId ||
        !createdBy ||
        !categoryVal ||
        !resolvedTemplate?.value
      ) {
        return;
      }
      try {
        await ensureStudyPlaceholdersCloned({
          organizationId,
          studyId: studyIdRef,
          templateId: resolvedTemplate.value,
          templateCategory: categoryVal,
          siteIdRef: activeSiteId ? stripGuid(activeSiteId) || null : null,
          createdBy,
        });
      } catch (bootstrapErr) {
        console.warn("Could not ensure study placeholders before folder load:", bootstrapErr);
      }
    };

    templateOption = await resolveTemplateForLoad();

    if (templateOption?.value) {
      setStudySourceTemplate(templateOption);
    }

    await ensureBinderPlaceholdersReady(templateOption);

    try {
      if (withLoader) showLoader("Documents are loading");
      const response = await apiClient.post(
        API_ENDPOINTS.ISF.GET_STUDY_FOLDERS,
        {
          OrganizationIdRef: userData?.OrganizationId || "",
          ActionType: "GetAllFolder",
          StaudyIdRef: studyId || "",
          StaffIdRef: userData?.StaffId || "",
          TemplateId: templateOption?.value || "",
          TemplateName: templateOption?.label || "",
          TemplateCategory: categoryVal || "",
          SiteIdRef: activeSiteId || null,
        },
      );

      const responseData = normalizeStudyFolderResponseData(response?.data ?? {});
      if (!isEisfApiSuccess(response) || response?.data == null) {
        if (
          !folderLoadRetried &&
          studyId &&
          userData?.OrganizationId &&
          categoryVal
        ) {
          templateOption = await resolveTemplateForLoad();
          if (templateOption?.value) {
            setStudySourceTemplate(templateOption);
          }
          await ensureBinderPlaceholdersReady(templateOption);
          return getStudyData(overrideStudyId, overrideCategoryValue, templateOption, {
            ...options,
            siteIdRef: activeSiteId,
            siteRows: rowsForSite,
            folderLoadRetried: true,
          });
        }
        showAlert({
          title: "Error",
          message: utils.getApiResponseMessage(
            response,
            "Could not load study folders. Verify the study site and template are configured, then try again.",
          ),
          type: "danger",
        });
        setTreeData([]);
        setDocuments([]);
        setStudyPlaceholders([]);
        return response;
      }

      // Use `prev` only — outer `formData` can be stale after await and clear templateCategory
      setFormData((prev) => ({
        ...prev,
        templateCategory: prev.templateCategoryCreation
          ? prev.templateCategoryCreation
          : prev.templateCategory || "",
      }));
      const staffRecords = Array.isArray(responseData.StaffListRecord)
        ? responseData.StaffListRecord
        : [];
      const tempStaffFilterData = staffRecords.map((item) => ({
        value: item.id,
        label: item.name,
        email: item.EmailId,
        Role: item.Role,
        roleId: item.RoleId || item.roleId,
      }));
      setStaffList(tempStaffFilterData);
      const restrictedFolders = responseData.RestrictedFolders || [];

      const studyFolderRows = Array.isArray(responseData.StudyFolder)
        ? responseData.StudyFolder
        : [];
      const formattedData = handleTreeDataFormat(
        studyFolderRows,
        restrictedFolders,
      );
      const folderDocuments = consolidateDocumentSignatureRows(
        Array.isArray(responseData.FolderDocuments)
          ? responseData.FolderDocuments
          : [],
        tempStaffFilterData,
      );
      let phList = [];
      let enrichedFolderDocuments = folderDocuments;
      let activeGateCode = "PRE_STUDY";

      try {
        const gateResult = await fetchStudyGate({ userData, studyId });
        const nextGate = gateResult?.state || null;
        const nextCode = nextGate?.CurrentGateCode || nextGate?.currentGateCode || null;
        if (
          prevStudyGateCodeRef.current &&
          nextCode &&
          prevStudyGateCodeRef.current !== nextCode
        ) {
          showToast({
            message: `Study advanced to ${gateNameForCode(nextCode)} phase.`,
            type: "success",
          });
          setViewGateCode(null);
        }
        if (nextCode) prevStudyGateCodeRef.current = nextCode;
        const actualGateCode = nextCode || activeGateCode;
        if (viewGateOverride != null) {
          setViewGateCode(normalizeGateCode(viewGateOverride));
        }
        activeGateCode =
          viewGateOverride != null
            ? normalizeGateCode(viewGateOverride)
            : viewGateCode != null
              ? normalizeGateCode(viewGateCode)
              : actualGateCode;
        setStudyGate(nextGate);
        setStudyGateList(gateResult?.gates || []);
      } catch {
        setStudyGate(null);
        setStudyGateList([]);
      }

      try {
        const croViewer = isSponsorCroSession(userData);
        const applyCroPlaceholderScope = (list) =>
          filterPlaceholdersForCroFolderScope(list, userData, studyId, {
            siteId: activeSiteId || null,
            category:
              categoryVal || formData?.templateCategory?.value || "eISF",
          });

        phList = applyCroPlaceholderScope(
          await fetchStudyPlaceholdersForActiveSite({
            organizationId: userData?.OrganizationId,
            studyId: studyId,
            templateCategory:
              categoryVal || formData?.templateCategory?.value || "",
            siteIdRef: activeSiteId || null,
            createdBy: userData?.StaffId,
            gateCode: activeGateCode,
          }),
        );
        if (activeSiteId) {
          phList = (phList || []).filter((row) =>
            studyPlaceholderMatchesSite(row, activeSiteId),
          );
        }

        // CRO must not clone placeholders — only consume granted binder content.
        if (!croViewer && !phList.length && studyId && userData?.OrganizationId) {
          try {
            await ensureStudyPlaceholdersCloned({
              organizationId: userData.OrganizationId,
              studyId,
              templateId: templateOption?.value || "",
              templateCategory: categoryVal,
              siteIdRef: activeSiteId || null,
              createdBy: userData.StaffId,
            });
            phList = applyCroPlaceholderScope(
              await fetchStudyPlaceholdersForActiveSite({
                organizationId: userData?.OrganizationId,
                studyId: studyId,
                templateCategory:
                  categoryVal || formData?.templateCategory?.value || "",
                siteIdRef: activeSiteId || null,
                createdBy: userData?.StaffId,
                gateCode: activeGateCode,
              }),
            );
            if (activeSiteId) {
              phList = (phList || []).filter((row) =>
                studyPlaceholderMatchesSite(row, activeSiteId),
              );
            }
            try {
              await seedStudyPlaceholderAssignees({
                organizationId: userData.OrganizationId,
                studyId,
                templateCategory: categoryVal,
                siteIdRef: activeSiteId || null,
                createdBy: userData.StaffId,
              });
              phList = applyCroPlaceholderScope(
                await fetchStudyPlaceholdersForActiveSite({
                  organizationId: userData?.OrganizationId,
                  studyId: studyId,
                  templateCategory:
                    categoryVal || formData?.templateCategory?.value || "",
                  siteIdRef: activeSiteId || null,
                  createdBy: userData?.StaffId,
                  gateCode: activeGateCode,
                }),
              );
              if (activeSiteId) {
                phList = (phList || []).filter((row) =>
                  studyPlaceholderMatchesSite(row, activeSiteId),
                );
              }
            } catch {
              /* non-blocking */
            }
          } catch {
            /* non-blocking */
          }
        }

        enrichedFolderDocuments = enrichDocumentsWithPlaceholderLinks(
          folderDocuments,
          phList,
        );

        phList = applyCroPlaceholderScope(
          dedupeStudyPlaceholdersForTree(
            augmentPlaceholdersFromDocuments(phList, enrichedFolderDocuments),
            enrichedFolderDocuments,
          ),
        );
        if (activeSiteId) {
          phList = (phList || []).filter((row) =>
            studyPlaceholderMatchesSite(row, activeSiteId),
          );
        }

        if (
          !croViewer &&
          !skipRepair &&
          phList.length &&
          enrichedFolderDocuments.length &&
          studyId &&
          userData?.OrganizationId
        ) {
          try {
            const repaired = await syncBrokenPlaceholderDocumentLinks({
              organizationId: userData.OrganizationId,
              studyId,
              createdBy: userData.StaffId,
              placeholders: phList,
              documents: enrichedFolderDocuments,
            });
            if (repaired) {
              return getStudyData(overrideStudyId, overrideCategoryValue, overrideTemplateOption, {
                ...options,
                skipRepair: true,
              });
            }
          } catch {
            /* non-blocking */
          }
        }

        setStudyPlaceholders(phList);
      } catch {
        setStudyPlaceholders([]);
        enrichedFolderDocuments = enrichDocumentsWithPlaceholderLinks(
          folderDocuments,
          [],
        );
        phList = dedupeStudyPlaceholdersForTree(
          augmentPlaceholdersFromDocuments([], enrichedFolderDocuments),
          enrichedFolderDocuments,
        );
      }

      const pendingSign = pendingSignatureRefreshRef.current;
      if (pendingSign?.docId) {
        const pendingDoc = enrichedFolderDocuments.find(
          (row) =>
            documentIdentityKey(row) === documentIdentityKey(pendingSign.docId),
        );
        const signedOnServer =
          pendingDoc &&
          (pendingDoc.Signed === 1 ||
            pendingDoc.Signed === true ||
            pendingDoc.Signed === "1" ||
            parseSignerIdList(getSignedUserIdsRawFromNode(pendingDoc)).length >
              0);
        if (!signedOnServer) {
          enrichedFolderDocuments = patchDocumentRowsWithSignature(
            enrichedFolderDocuments,
            pendingSign.docId,
            pendingSign.staffId,
            pendingSign.staffName,
          );
        }
      }

      setDocuments(enrichedFolderDocuments);
      const tempTreeData = buildStudyDocumentTree(
        formattedData,
        phList,
        enrichedFolderDocuments,
      );
      setTreeData(tempTreeData);
      setTreeForceExpandedIds(collectMilestoneNodeIdsWithChildren(tempTreeData));
      setSelectedNode((prev) => {
        if (!prev) return prev;
        const freshDoc = enrichedFolderDocuments.find(
          (row) => documentIdentityKey(row) === documentIdentityKey(prev),
        );
        if (!freshDoc) return prev;
        return {
          ...prev,
          ...freshDoc,
          type: prev.type || "file",
        };
      });
      const studyRow = responseData.StaffStudyDetails?.[0];
      if (studyRow) {
        const enriched = await resolveStudyAssociatedSites(studyRow, userData);
        setStudyDetails((prev) => ({ ...prev, ...enriched }));
        setShowStudySection(true);
      }

      return response;
    } catch (error) {
      console.error("Error fetching study:", error);
      throw error;
    } finally {
      if (withLoader) hideLoader();
    }
  };

  const refreshStudyPlaceholders = async () => {
    await getStudyData(undefined, undefined, undefined, { withLoader: false });
  };

  // Update recent visit subtitle: show study name only (no GUID) in recent list / timeline
  useEffect(() => {
    const studyCode = studyDetails?.StudyNumber || formData?.study?.value;
    const studyName =
      studyDetails?.StudyName || formData?.study?.label || studyCode;
    if (studyName) {
      const isGuid =
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
          String(studyCode || "").trim(),
        );
      const subtitle = isGuid
        ? studyName
        : studyCode && studyCode !== studyName
          ? `${studyCode} - ${studyName}`
          : studyName;
      const path = location.pathname + (location.search || "");
      updateRecentVisitSubtitle(path, subtitle);
    }
  }, [
    studyDetails?.StudyName,
    studyDetails?.StudyNumber,
    formData?.study?.label,
    formData?.study?.value,
    location.pathname,
    location.search,
    updateRecentVisitSubtitle,
  ]);

  // Open ISF with study + category from URL when coming from Recents (or bookmark)
  useEffect(() => {
    const studyIdFromUrl = searchParams.get("studyId");
    const categoryFromUrl =
      lockedCategory || searchParams.get("category");

    if (justNavigatedFromGoRef.current) {
      justNavigatedFromGoRef.current = false;
      return;
    }

    if (!studyIdFromUrl) {
      setUrlBootstrapLoading(false);
      lastUrlStudyIdRef.current = null;
      appliedUrlParamsRef.current = false;
      return;
    }

    if (lastUrlStudyIdRef.current !== studyIdFromUrl) {
      lastUrlStudyIdRef.current = studyIdFromUrl;
      appliedUrlParamsRef.current = false;
      commentDeepLinkAppliedRef.current = null;
      setUrlBootstrapLoading(true);
    }

    if (appliedUrlParamsRef.current || !userData) {
      return;
    }

    const denyUrlAccess = (kind = "study") => {
      appliedUrlParamsRef.current = false;
      lastUrlStudyIdRef.current = null;
      setFormData((prev) => ({
        ...prev,
        study: null,
        templateCategory: "",
        haseISF: false,
        hasePSF: false,
      }));
      setActiveCategory(null);
      seteISF(false);
      setIsViewReady(false);
      setShowStudyListPopup(false);
      setUrlBootstrapLoading(false);
      setCroAccessGate({
        kind: kind === "all" ? "all" : "study",
      });
      navigate(binderHomePath, { replace: true });
    };

    const croSession = isSponsorCroSession(userData);

    // Wait for binder list only when it is still loading. An empty finished
    // list must not leave "Loading study…" forever (all grants removed).
    if (!studies.length && !options.length) {
      if (studiesLoading) return;

      appliedUrlParamsRef.current = true;
      (async () => {
        try {
          if (croSession) {
            const sessionUser = await refreshSponsorCroLiveGrants(userData);
            refreshUserData?.();
            const category = categoryFromUrl || "eISF";
            const grantedIds = getCroGrantedStudyIds(sessionUser);
            const hasAnyShare = Boolean(grantedIds && grantedIds.size > 0);
            if (
              !croHasLiveIsfAccessToStudy(sessionUser, studyIdFromUrl, {
                category,
              })
            ) {
              denyUrlAccess(hasAnyShare ? "study" : "all");
              return;
            }
          }
          // Study not in binder list — full-page gate for CRO.
          setUrlBootstrapLoading(false);
          appliedUrlParamsRef.current = false;
          navigate(binderHomePath, { replace: true });
          if (croSession) {
            setCroAccessGate({ kind: "study" });
            setShowStudyListPopup(false);
          } else {
            setShowStudyListPopup(true);
          }
        } catch (error) {
          console.error("Failed to bootstrap ISF from URL:", error);
          appliedUrlParamsRef.current = false;
          setUrlBootstrapLoading(false);
          setShowStudyListPopup(false);
          if (croSession) {
            setCroAccessGate({ kind: "all" });
          } else {
            setShowStudyListPopup(true);
          }
          navigate(binderHomePath, { replace: true });
        }
      })();
      return;
    }

    appliedUrlParamsRef.current = true;

    const studyIdNorm = String(studyIdFromUrl).toLowerCase();
    let studyOption = options.find(
      (o) => String(o.value).toLowerCase() === studyIdNorm,
    );
    const studyConfig = studies.find(
      (s) => String(s.StudyId).toLowerCase() === studyIdNorm,
    );

    if (!studyOption && studyConfig) {
      studyOption = {
        label: studyConfig.StudyName || studyConfig.StudyNumber || "Study",
        value: studyConfig.StudyId,
      };
    }

    if (!studyOption) {
      setUrlBootstrapLoading(false);
      setShowStudyListPopup(true);
      appliedUrlParamsRef.current = false;
      if (croSession) {
        navigate(binderHomePath, { replace: true });
      }
      return;
    }

    const hasISF = !!studyConfig?.HaseISFTemplate;
    const hasPSF = !!studyConfig?.HasePSFTemplate;
    const anyTemplate = hasISF || hasPSF;

    const categoryOption =
      categoryFromUrl === "eISF"
        ? { label: "eISF", value: "eISF" }
        : categoryFromUrl === "ePSF"
          ? { label: "ePSF", value: "ePSF" }
          : categoryFromUrl
            ? { label: categoryFromUrl, value: categoryFromUrl }
            : hasISF
              ? { label: "eISF", value: "eISF" }
              : hasPSF
                ? { label: "ePSF", value: "ePSF" }
                : { label: "eISF", value: "eISF" };

    (async () => {
      try {
        const resolvedCategory =
          categoryFromUrl || categoryOption?.value || "eISF";

        let sessionUser = userData;
        if (croSession) {
          sessionUser = await refreshSponsorCroLiveGrants(userData);
          refreshUserData?.();
          const grantedIds = getCroGrantedStudyIds(sessionUser);
          const hasAnyShare = Boolean(grantedIds && grantedIds.size > 0);
          if (
            !croHasLiveIsfAccessToStudy(sessionUser, studyIdFromUrl, {
              category: resolvedCategory,
            })
          ) {
            denyUrlAccess(hasAnyShare ? "study" : "all");
            return;
          }
        }

        const openCats = resolveIsfOpenBinderCategories(
          croSession ? sessionUser : userData,
          studyConfig || { StudyId: studyIdFromUrl },
        );
        const openCategory =
          (categoryFromUrl &&
            openCats.categoryOptions.find(
              (opt) =>
                String(opt.value).toLowerCase() ===
                String(categoryFromUrl).toLowerCase(),
            )) ||
          openCats.defaultCategory ||
          categoryOption;

        setTemplateCategoryOptions(openCats.categoryOptions);
        setFormData((prev) => ({
          ...prev,
          study: studyOption,
          templateCategory: openCategory,
          haseISF: openCats.hasEisf,
          hasePSF: openCats.hasEpsf,
        }));

        setActiveCategory(openCategory);
        seteISF(
          openCats.hasEisf ||
            openCats.hasEpsf ||
            (!croSession && anyTemplate),
        );
        setShowStudyListPopup(false);
        setCroAccessGate(null);

        const binderCategory =
          openCategory?.value || resolvedCategory || "eISF";

        const siteContext = await loadStudySiteContext(
          studyIdFromUrl,
          binderCategory,
        );

        if (
          isSponsorCroSession(sessionUser) &&
          siteContext?.defaultSiteId &&
          !croHasLiveIsfAccessToStudy(sessionUser, studyIdFromUrl, {
            category: binderCategory,
            siteId: siteContext.defaultSiteId,
          })
        ) {
          const grantedIds = getCroGrantedStudyIds(sessionUser);
          const hasAnyShare = Boolean(grantedIds && grantedIds.size > 0);
          denyUrlAccess(hasAnyShare ? "study" : "all");
          return;
        }

        await getStudyData(
          studyIdFromUrl,
          binderCategory,
          siteContext?.defaultTemplateOption,
          {
            siteIdRef: siteContext?.defaultSiteId ?? null,
            siteRows: siteContext?.rows,
          },
        );
        setIsViewReady(true);
      } catch (error) {
        console.error("Failed to bootstrap ISF from URL:", error);
        appliedUrlParamsRef.current = false;
        setShowStudyListPopup(true);
        navigate(binderHomePath, { replace: true });
      } finally {
        setUrlBootstrapLoading(false);
      }
    })();
  }, [
    options,
    studies,
    studiesLoading,
    searchParams,
    userData,
    navigate,
    showAlert,
    refreshUserData,
  ]);

  //

  const handleTreeDataFormat = (data, restrictedFolders = []) => {
    if (!Array.isArray(data)) return [];
    const safeRestricted = Array.isArray(restrictedFolders)
      ? restrictedFolders
      : [];

    const isSystemAdmin =
      userData?.Role === "System Administrator" ? true : false;

    const restrictedFolderIds = new Set(
      safeRestricted.map((rf) => rf.FolderIdRef?.toLowerCase()),
    );

    return data
      .filter((item) => {
        // ❌ Non-Admin → restricted folder remove
        if (!isSystemAdmin) {
          return !restrictedFolderIds.has(item.FolderId?.toLowerCase());
        }

        // ✅ System Admin → allow all
        return true;
      })
      .map((item) => ({
        id: stripGuid(item.FolderId),
        name: item.FolderName,
        type: "folder",
        parentId: item.ParentFolderIdRef ? stripGuid(item.ParentFolderIdRef) : null,
        // ✅ key/value ALWAYS present
        isRestricted: restrictedFolderIds.has(String(item.FolderId || "").toLowerCase()),
      }));
  };

  const { modalState, showConfirm, handleConfirm, handleCancel, hideConfirm } =
    useConfirmModal();
  const { toasts, showToast, removeToast } = useToast();

  const canManageStudyPlaceholdersFlag =
    !isCroReadOnlyViewer && canManageStudyPlaceholders(userData);
  const canCreateStudyAdHoc =
    userData?.Role === "System Administrator" ||
    canCreateStudyAdHocPlaceholder(permissions, userData);
  const canEditStudyAdHoc =
    userData?.Role === "System Administrator" ||
    canEditStudyAdHocPlaceholder(permissions, userData);

  const showHistoricalGateReadOnlyAlert = useCallback(() => {
    showAlert({
      title: isStudyArchivedReadOnlyFlag ? "Archived study" : "Read-only preview",
      message: isStudyArchivedReadOnlyFlag
        ? ARCHIVED_STUDY_READONLY_MESSAGE
        : HISTORICAL_GATE_READONLY_MESSAGE,
      type: "info",
    });
  }, [showAlert, isStudyArchivedReadOnlyFlag]);

  const handleApplyPlaceholdersToStudy = async () => {
    if (!canEditCurrentStudyGate) {
      showHistoricalGateReadOnlyAlert();
      return;
    }
    const studyId = studyDetails?.StudyId || formData?.study?.value;
    const templateId =
      formData?.selectedTemplateOption?.value || studySourceTemplate?.value || "";
    const category =
      formData?.templateCategoryCreation?.value ||
      formData?.templateCategory?.value ||
      activeCategory?.value ||
      "";
    if (!studyId || !userData?.OrganizationId) {
      showAlert({
        title: "Cannot apply placeholders",
        message: "Select a study before applying expected documents.",
        type: "warning",
      });
      return;
    }
    showConfirm({
      title: "Apply expected documents",
      message:
        "Add document placeholders from the template to this study? Existing files are not changed.",
      confirmText: "Apply",
      confirmButtonClass: "btn-primary",
      onConfirm: async () => {
        hideConfirm();
        try {
          showLoader("Applying placeholders...");
          let resolvedTemplateId =
            formData?.selectedTemplateOption?.value ||
            studySourceTemplate?.value ||
            "";
          if (!resolvedTemplateId && category) {
            const src = await fetchStudySourceTemplate({
              organizationId: userData.OrganizationId,
              studyId,
              templateCategory: category,
              createdBy: userData.StaffId,
              siteIdRef: selectedSiteId || null,
            });
            resolvedTemplateId = src?.TemplateId || "";
          }
          await ensureStudyPlaceholdersCloned({
            organizationId: userData.OrganizationId,
            studyId,
            templateId: resolvedTemplateId,
            templateCategory: category,
            siteIdRef: selectedSiteId || null,
            createdBy: userData.StaffId,
          });
          await getStudyData();
          showToast({ message: "Placeholders applied to study.", type: "success" });
        } catch (err) {
          showAlert({
            title: "Error",
            message: err?.message || "Failed to apply placeholders.",
            type: "danger",
          });
        } finally {
          hideLoader();
        }
      },
      onCancel: hideConfirm,
    });
  };

  //const [single, setSingle] = useState(null);
  // const [templateCategory, setTemplateCategory] = useState("eISF");

  // const handleSelect = (selectedStudy) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     study: selectedStudy?.value || "",
  //   }));

  //   seteISF(false);
  //   // console.log(getAllUser());
  //   if (selectedStudy.value === "Study1") {
  //     seteISF(true);
  //     setSingle(selectedStudy);
  //   } else {
  //     showConfirm({
  //       title: "Add eISF/ ePSF",
  //       message: `Selected study does not have the template. Do you want to add it`,
  //       confirmText: "Yes",
  //       confirmButtonClass: "btn-success",
  //       icon: "fas fa-plus text-success",
  //       onConfirm: () => {
  //         setSingle(selectedStudy);
  //         seteISF(true);
  //         hideConfirm();
  //         setShowModal1(true);
  //       },
  //       onCancel: () => {
  //         setSingle(null);
  //         seteISF(false);
  //         showToast({
  //           title: "Cancelled",
  //           message: "Template has not been added",
  //           type: "warning",
  //         });
  //       },
  //     });
  //   }
  // };

  //   showConfirm({
  //     title: "Delete User",
  //     message: `Are you sure you want to delete " This action cannot be undone.`,
  //     confirmText: "Delete",
  //     confirmButtonClass: "btn-danger",
  //     icon: "fas fa-trash text-danger",
  //     onConfirm: () => {
  //       hideConfirm();
  //       showAlert({
  //         title: "Success",
  //         message: `User  has been deleted successfully.`,
  //         type: "success",
  //       });
  //     },
  //     onCancel: () => {
  //       showToast({
  //         title: "Cancelled",
  //         message: "Delete operation was cancelled.",
  //         type: "info",
  //       });
  //     },
  //   });
  // };

  const [haseISF, seteISF] = useState(false);

  const [showCreatePanel, setShowCreatePanel] = useState(false);

  // Load templates only when create binder UI is shown (not on every study click)
  useEffect(() => {
    if (!showStudyListPopup || !showCreatePanel) return;
    getAllTemplates({ silent: true }).catch(() => {});
  }, [showStudyListPopup, showCreatePanel]);

  // When templateList loads after user already selected category, filter and show template options
  useEffect(() => {
    if (!showStudyListPopup || !showCreatePanel || !templateList.length) return;
    const cat = formData?.templateCategoryCreation?.value ?? "";
    if (!cat) {
      setTemplateOptions([]);
      return;
    }
    const filtered = templateList.filter(
      (t) =>
        String(t.TemplateCategory || t.Category || "").toLowerCase() ===
        String(cat).toLowerCase(),
    );
    setTemplateOptions(
      filtered.length
        ? utils.mapToOptions(filtered, "TemplateName", "TemplateId")
        : [],
    );
  }, [showStudyListPopup, showCreatePanel, templateList, formData?.templateCategoryCreation?.value]);

  useEffect(() => {
    if (
      !showStudyListPopup ||
      !showCreatePanel ||
      !formData.study?.value ||
      !userData?.OrganizationId
    ) {
      setCreateModalSites([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setCreateModalSitesLoading(true);
      try {
        const response = await fetchStudySitesForSetup({
          organizationId: userData.OrganizationId,
          studyId: formData.study.value,
          templateCategory: formData.templateCategoryCreation?.value || "",
          createdBy: userData.StaffId,
        });
        if (!cancelled) {
          const rows = await enrichStudySiteRowsFromLookup(
            normalizeStudySiteRows(response),
            userData,
          );
          setCreateModalSites(rows);
          setSameTemplateForAllSites(true);
          setPerSiteTemplateOptions({});
          setCreateReferenceDate((prev) =>
            hasReferenceDateValue(prev) ? prev : defaultReferenceDateValue(),
          );
          setPerSiteReferenceDates((prev) => {
            if (Object.keys(prev || {}).length) return prev;
            return buildInitialPerSiteReferenceDates(
              rows,
              defaultReferenceDateValue(),
            );
          });
        }
      } catch {
        if (!cancelled) setCreateModalSites([]);
      } finally {
        if (!cancelled) setCreateModalSitesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    showStudyListPopup,
    showCreatePanel,
    formData.study?.value,
    formData.templateCategoryCreation?.value,
    userData?.OrganizationId,
    userData?.StaffId,
  ]);

  const isMultiSiteCreateModal =
    createModalSites.filter((row) => row.SiteId).length > 1;

  //for tree view
  // Large tree view data

  const handleSaveTemplate = () => {
    setShowCreatePanel(false);
    showAlert({
      title: "Success",
      message: `Successfully created`,
      type: "success",
    });
  };

  const [isEditorLoaded, setIsEditorLoaded] = useState(false);
  const [selectedFile, setsSlectedFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [editorSessionKey, setEditorSessionKey] = useState(0);
  const docEditorRef = useRef(null);
  const editorMountId = `onlyoffice-editor-${editorSessionKey}`;
  const docApiUrl =
    "https://ascensionqapi.azurewebsites.net/aqdocumentapi/api/";
  const serverUrl = "https://documentserver.ukwest.cloudapp.azure.com/";
  //const docApiUrl = "http://192.168.29.194:44332/api/onlyoffice/";

  const getDocumentType = (filename) => {
    if (filename.endsWith(".docx") || filename.endsWith(".doc")) {
      return "word";
    }
    if (
      filename.endsWith(".xlsx") ||
      filename.endsWith(".xls") ||
      filename.endsWith(".csv")
    ) {
      return "cell";
    }
    if (filename.endsWith(".pptx") || filename.endsWith(".ppt")) {
      return "slide";
    }
    return "word";
  };

  const releaseOnlyOffice = () => {
    if (
      docEditorRef.current &&
      typeof docEditorRef.current.destroyEditor === "function"
    ) {
      try {
        docEditorRef.current.destroyEditor();
      } catch (error) {
        console.warn("Error destroying editor:", error);
      }
    }
    docEditorRef.current = null;
    setIsEditorLoaded(false);
    setsSlectedFile(null);
  };

  const cleanupEditor = () => {
    releaseOnlyOffice();
  };

  /** Destroy OnlyOffice first, then run selection after the editor node unmounts. */
  const afterEditorRelease = (applySelection) => {
    releaseOnlyOffice();
    setImagePreviewUrl(null);
    setEditorSessionKey((k) => k + 1);
    window.setTimeout(() => {
      applySelection();
    }, 50);
  };

  // OnlyOffice preview removed — shared DocumentPreviewPane handles preview UI.

  const initializeEditor = (node, fileURL, mountElementId) => {
    if (!node || !fileURL || !mountElementId || !window.DocsAPI) return;
    if (!document.getElementById(mountElementId)) return;

    if (
      docEditorRef.current &&
      typeof docEditorRef.current.destroyEditor === "function"
    ) {
      try {
        docEditorRef.current.destroyEditor();
      } catch (error) {
        console.warn("Error destroying editor:", error);
      }
      docEditorRef.current = null;
    }

    try {
      const docEditor = new window.DocsAPI.DocEditor(mountElementId, {
        document: {
          fileType: fileURL.split(".").pop(),
          key: `key_${node.id}_${Date.now()}`,
          title: node.name,
          url: fileURL,
          permissions: {
            edit: false,
            download: true,
            print: true,
            review: false,
            comment: false,
            chat: false,
          },
        },
        documentType: getDocumentType(fileURL),
        editorConfig: {
          mode: "read",
          customData: fileURL,
          lang: "en",
          callbackUrl: `${docApiUrl}onlyoffice/save?originalUrl=${encodeURIComponent(
            fileURL,
          )}`,
          autosave: true,
          forcesave: true,
          user: {
            id: "user1",
            name: "User",
          },
          customization: {
            autosave: false,
            forcesave: false,
            commentAuthorOnly: false,
            comments: false,
            compactHeader: true,
            compactToolbar: true,
            hideRightMenu: true,
            hideLeftMenu: true,
            integrationMode: "embed",
            macros: false,
            macrosMode: "warn",
            plugins: false,
            showReviewChanges: false,
            spellcheck: false,
            toolbarHideFileName: false,
            toolbarNoTabs: false,
            unit: "cm",
            zoom: 100,

            about: false,
            hideTopBar: true,
            leftMenu: false, // ✅ disables (i) info panel
            help: false, // ✅ removes help/about
            feedback: false, // ✅ removes feedback
          },
        },
        width: "100%",
        height: "100%",
        events: {
          onReady: () => {
            console.log("OnlyOffice editor is ready!");
            setIsEditorLoaded(true);
            //setEditorStatus("Active");
            //setApiStatus("Ready");

            setTimeout(() => {
              console.log("Editor ref current:", docEditorRef.current);
              console.log(
                "Available methods:",
                Object.keys(docEditorRef.current || {}),
              );
            }, 1000);
          },
          onDocumentReady: () => {
            console.log("Document is ready for editing");
            setIsEditorLoaded(true);
            //setEditorStatus("Active");
          },
          onDocumentStateChange: (event) => {
            console.log("Document state changed:", event.data);
          },
          onError: (event) => {
            console.error("OnlyOffice error:", event.data);
            //setEditorStatus("Error");
            setIsEditorLoaded(true);
          },
          onInfo: (event) => {
            console.log("OnlyOffice info:", event.data);
          },
          onGetDocumentData: (event) => {
            console.log("OnlyOffice info: GetDocument Data", event.data);
          },
        },
      });

      docEditorRef.current = docEditor;
    } catch (error) {
      console.error("Error initializing OnlyOffice editor:", error);
      //setEditorStatus("Error");
    }
  };

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showCapaCreateModal, setShowCapaCreateModal] = useState(false);
  const [capaCreateContext, setCapaCreateContext] = useState(null);
  const [showFolderCRUDModal, setShowFolderCRUDModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [fileData, setFileData] = useState({});
  const [selectedAction, setSelectedAction] = useState("");
  const [documentCodeError, setDocumentCodeError] = useState("");
  const [uploadFileNameError, setUploadFileNameError] = useState("");

  /** Sign menu + action: mandatory sign from document row or linked placeholder milestone roles */
  const isCurrentUserEligibleToSignTreeNode = useCallback(
    (node) => {
      if (!node || node.type !== "file") return false;
      const doc = documents.find(
        (d) =>
          String(d.DocumentIdRef || "").toLowerCase() ===
          String(node.id || "").toLowerCase(),
      );
      if (!doc) return false;

      let ids = parseSignerIdList(getMandatorySignUserIdsRaw(doc));
      if (!ids.length) {
        const ph = findStudyPlaceholderForDocument(doc, studyPlaceholders);
        if (ph && studyPlaceholderRequiresSignature(ph)) {
          ids = buildMandatorySignUsersFromPlaceholder(
            ph,
            staffList,
            studyRoleOptions,
          ).mandatorySignUsers
            .map((u) => normalizeSignerIdForCompare(u.value))
            .filter(Boolean);
        }
      }
      if (!ids.length) return false;

      const myId = normalizeSignerIdForCompare(userData?.StaffId);
      if (!myId || !ids.includes(myId)) return false;
      const signedIds = parseSignerIdList(getSignedUserIdsRawFromNode(doc));
      if (signedIds.includes(myId)) return false;
      return true;
    },
    [documents, studyPlaceholders, staffList, studyRoleOptions, userData?.StaffId, permissions],
  );

  const handleNodeSelect = (node, action, docOverride = null) => {
    if (node?.type === "placeholder") {
      const phRow =
        studyPlaceholders.find(
          (p) =>
            String(p.StudyPlaceholderId) === String(node.studyPlaceholderId),
        ) || {};
      const nextSelected = { ...node, ...phRow, type: "placeholder" };
      afterEditorRelease(() => {
        setSelectedNode(nextSelected);
        setSelectedId(node.id);
        if (!isPendingStudyPlaceholder(nextSelected, documents, studyPlaceholders)) {
          setTreeForceExpandedIds((prev) =>
            prev.includes(node.id) ? prev : [...prev, node.id],
          );
        }
        setShowStudyBasicInfo(true);
        setShowStudySection(true);
        setShowDocumentSection(false);
        releaseOnlyOffice();
        setImagePreviewUrl(null);
        setsSlectedFile(null);
      });
      return;
    }

    if (node?.type === "milestone") {
      afterEditorRelease(() => {
        setSelectedNode(node);
        setSelectedId(node.id);
        setShowStudyBasicInfo(true);
        setShowStudySection(true);
        setShowDocumentSection(false);
        releaseOnlyOffice();
        setImagePreviewUrl(null);
        setsSlectedFile(null);
      });
      return;
    }

    const docRow =
      docOverride || findStudyDocument(documents, node.id);

    if (node?.type === "folder") {
      afterEditorRelease(() => {
        setSelectedNode(docRow || node);
        setSelectedId(node.id);
        setShowStudyBasicInfo(true);
        setShowStudySection(true);
      });
      return;
    }

    const nextSelected = docRow
      ? { ...node, ...docRow, type: "file" }
      : { ...node, type: node.type || "file" };

    if (action !== "Selection" || node.type !== "file") {
      afterEditorRelease(() => {
        setSelectedNode(nextSelected);
        setSelectedId(String(node.id || "").toLowerCase());
      });
      return;
    }

    const docMeta = docRow || findStudyDocument(documents, node.id);
    const fileURL = docMeta?.FileUrl || "";

    let ext = "";
    if (docMeta?.FileName || node.name) {
      const name = (docMeta?.FileName || node.name || "").split("?")[0];
      const parts = name.split(".");
      ext = parts.length > 1 ? parts.pop().toLowerCase() : "";
    }
    if (!ext && fileURL) {
      const cleanUrl = fileURL.split("?")[0];
      const parts = cleanUrl.split(".");
      ext = parts.length > 1 ? parts.pop().toLowerCase() : "";
    }
    if (ext.startsWith(".")) {
      ext = ext.slice(1);
    }

    releaseOnlyOffice();
    setImagePreviewUrl(null);
    setSelectedNode(nextSelected);
    setSelectedId(String(node.id || "").toLowerCase());
    setShowStudySection(false);
    setShowDocumentSection(true);

    if (
      node?.type === "file" &&
      !node?.isPreviousVersion &&
      docMeta?.FileGroupIdRef
    ) {
      const groupKey = stripGuid(docMeta.FileGroupIdRef).toLowerCase();
      const hasOlderVersions = documents.some(
        (d) =>
          isSupersededStudyDocument(d) &&
          stripGuid(d.FileGroupIdRef).toLowerCase() === groupKey,
      );
      if (hasOlderVersions) {
        setTreeForceExpandedIds((prev) =>
          prev.includes(node.id) ? prev : [...prev, node.id],
        );
      }
    }

    // Shared PDF/image preview pane handles all file types (no OnlyOffice).
    setImagePreviewUrl(null);
    setEditorSessionKey((k) => k + 1);
    if (fileURL) {
      setsSlectedFile({
        ...nextSelected,
        FileUrl: fileURL,
        FileName: docMeta?.FileName || nextSelected?.name || nextSelected?.FileName,
        DocumentIdRef:
          docMeta?.DocumentIdRef ||
          nextSelected?.DocumentIdRef ||
          nextSelected?.id,
        DocumentExtension: ext,
      });
      return;
    }

    setsSlectedFile(null);
  };

  const handleNodeAction = async (node, action) => {
    const gateEditActions = new Set([
      "newFile",
      "replaceFile",
      "newFolder",
      "subFolder",
      "renameFolder",
      "lockFile",
      "unlockFile",
      "deleteFile",
      "deleteFolder",
      "moveNode",
      "signFile",
      "generateCertification",
      "createCapa",
      "restrictedFolder",
      "removeAdHocMilestone",
    ]);

    if (
      !canEditCurrentStudyGate &&
      gateEditActions.has(action)
    ) {
      showHistoricalGateReadOnlyAlert();
      return;
    }

    if (
      node?.isPreviousVersion &&
      action !== "auditLog" &&
      action !== "viewCertification"
    ) {
      showAlert({
        title: "Read-only",
        message:
          action === "generateCertification"
            ? "Certification cannot be generated for older document versions."
            : "Older versions can be viewed only.",
        type: "info",
      });
      return;
    }

    // 🚫 Permission check FIRST (permissions: admin = getAllPermissionKeys(), else backend string)
    if (!hasActionPermission(action, permissions)) {
      showAlert({
        title: "Warning",
        message: getPermissionMessage(ACTIONS[action]),
        type: "warning",
      });
      return; // ⛔ STOP execution
    }

    let tempnode = [];
    if (action !== "newFolder" && action !== "moveNode") {
      tempnode = documents.filter((item) => {
        return item.DocumentIdRef === node.id;
      });
    }
    setSelectedNode(tempnode[0]);

    if (tempnode.length === 0) {
      setSelectedNode(node);
    }

    setSelectedAction(action);
    if (action === "newFile") {
      fileUploadAction(node);
    } else if (action === "replaceFile") {
      const docRow =
        documents.find((item) => item.DocumentIdRef === node.id) || node;
      const folderId =
        stripGuid(docRow.FolderIdRef || node.parentId || node.FolderIdRef) ||
        "";
      setSelectedNode(docRow);
      fileUploadAction(null, {
        folderId,
        supersedeDocumentId: node.id || docRow.DocumentIdRef,
        sourceDocument: docRow,
      });
    } else if (
      action === "newFolder" ||
      action === "subFolder" ||
      action === "renameFolder"
    ) {
      setModaFolderName("");
      folderActionProcess();
      if (action === "renameFolder") {
        setModaFolderName(node.name);
      }
    } else if (
      action === "lockFile" ||
      action === "unlockFile" ||
      action === "deleteFile" ||
      action === "deleteFolder"
    ) {
      showConfirm({
        title:
          action === "lockFile"
            ? "Lock File"
            : action === "deleteFile"
              ? "Delete File"
              : "Delete Folder",
        message:
          action === "lockFile"
            ? "Are you sure that you want to lock this document?"
            : action === "unlockFile"
              ? `Are you sure that you want to unlock this document`
              : action === "deleteFile"
                ? `Are you sure that you want to delete this document?`
                : `Are you sure that you want to delete this folder?`,
        confirmText: "Yes",
        confirmButtonClass: "btn-success",
        icon: "fas fa-plus text-success",
        onConfirm: () => {
          if (action === "deleteFolder") {
            if (typeof node.children != "undefined") {
              showAlert({
                title: "Warning",
                message:
                  "Folder contain sub-folder/files, that must be moved or deleted prior to the folder deletion​.",
                type: "warning",
              });
              hideConfirm();
            } else {
              showAuth(action, node.id);
            }
          } else {
            showAuth(action);
          }
        },
        onCancel: () => {
          showToast({
            title: "Cancelled",
            message:
              action === "lockFile"
                ? "Document has not been locked"
                : `Document has not been deleted`,
            type: "warning",
          });
        },
      });
    } else if (action === "moveNode") {
      const newFolderId = node.destinationNode.id;

      const oldFolderId =
        node.sourceNode.type === "file"
          ? node.sourceNode.parentId
          : node.sourceNode.id;

      const documentId =
        node.sourceNode.type === "file" ? node.sourceNode.id : "";

      const actionType =
        node.sourceNode.type === "file" ? "UpdateDocumentFolder" : "moveall";
      try {
        const response = await apiClient.post(
          API_ENDPOINTS.ISF.FILE_FOLDER_MOVE,
          {
            ActionType: actionType,
            OldFolderId: oldFolderId,
            DocumentId: documentId,
            NewfolderId: newFolderId,
            StudyIdRef: studyDetails.StudyId,
            OrganizationIdRef: userData?.OrganizationId || "",
            CreatedBy: userData?.StaffId || "",
            SiteIdRef: getActiveSiteIdRef() || null,
          },
        );

        if (response.success) {
          getStudyData();
        }
      } catch (error) {
        console.error("Error fetching study:", error);
        throw error;
      } finally {
        hideLoader();
      }

      // URL=MoveDocumentToFolder
      {
      }
    } else if (action === "generateCertification") {
      const docId = node.id ?? node.DocumentIdRef;
      const doc = documents.find((d) => d.DocumentIdRef === docId) || node;
      setSelectedNode(doc);
      setCertificationWizardStep(0);
      setCertDeclarationAccepted(false);
      setCertDeclarationScrolledToEnd(false);
      setCertSignatureReason("");
      setCertDeclarationRefId(generateCertDeclarationRef());
      setCertGeneratedHash(generateSha256StyleHash());
      setCertClientIp("");
      setShowCertificationWizardModal(true);
    } else if (action === "viewCertification") {
      const docId = node.id ?? node.DocumentIdRef;
      const doc = documents.find((d) => d.DocumentIdRef === docId) || node;
      setSelectedNode(doc);
      setShowCertificationDetailModal(true);
    } else if (action === "signFile") {
      if (!isCurrentUserEligibleToSignTreeNode(node)) {
        showAlert({
          title: "Not allowed",
          message:
            "You are not assigned to sign this document, or you have already signed.",
          type: "warning",
        });
        return;
      }
      loadSignatureFromSession();
      setShowSignModal(true);
    } else if (action === "restrictedFolder") {
      try {
        showLoader("staff's are loading");
        const response = await apiClient.post(
          API_ENDPOINTS.ISF.GET_STUDY_FOLDERS,
          {
            FolderIdRef: node?.id,
            OrganizationIdRef: userData?.OrganizationId || "",
            ActionType: "GetRestrictedUserByFolderId",
          },
        );
        // Map backend user IDs to full staff objects from staffList
        const restrictedUserIds = (response.data.RestrictedUsers || []).map(
          (item) => item.UserIdRef?.toLowerCase(),
        );
        const mappedStaffData = staffList.filter((staff) =>
          restrictedUserIds.includes(staff.value.toLowerCase()),
        );
        setSelectedStaffList(mappedStaffData);
        // Also update ref for initial load
        currentSelectedStaffRef.current = mappedStaffData;
      } catch (error) {
        console.error("Error fetching study:", error);
        throw error;
      } finally {
        hideLoader();
      }
      setShowRestricFolderModal(true);
    } else if (action === "auditLog") {
      // Set the appropriate IDs based on node type
      if (node.type === "file") {
        setAuditLogDocumentId(node.id);
        setAuditLogFolderId(null);
      } else if (node.type === "folder") {
        setAuditLogFolderId(node.id);
        setAuditLogDocumentId(null);
      }
      setShowAuditLogModal(true);
    } else if (action === "createCapa") {
      const studyId =
        studyDetails?.StudyId || formData?.study?.value || "";
      const templateCategoryRaw =
        activeCategory?.value ||
        activeCategory?.label ||
        formData?.templateCategory?.value ||
        formData?.templateCategory?.label ||
        "";
      const docId = node?.id || node?.DocumentIdRef || "";
      const docLabel = node?.name || getDisplayDocumentId(node);
      setCapaCreateContext(
        buildCapaContextFromIsfDocument({
          docId,
          docLabel,
          studyId,
          studyLabel:
            studyDetails?.StudyName ||
            formData?.study?.label ||
            "",
          templateCategory: normalizeIsfTemplateCategoryForApi(templateCategoryRaw),
        }),
      );
      setShowCapaCreateModal(true);
    } else {
      alert(action);
      console.log(node);
    }
  };

  const closeCertificationWizard = () => {
    setShowCertificationWizardModal(false);
    setCertificationWizardStep(0);
    setCertDeclarationAccepted(false);
    setCertDeclarationScrolledToEnd(false);
    setCertGeneratedHash("");
    setCertClientIp("");
    setCertSignatureReason("");
  };

  const handleCopyCertificationHash = () => {
    const hash =
      selectedNode?.DocumentHash ||
      selectedNode?.CertificationDocumentHash ||
      "";
    if (hash && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(hash).then(
        () =>
          showToast({
            title: "Copied",
            message: "Hash copied to clipboard",
            type: "success",
          }),
        () =>
          showToast({
            title: "Error",
            message: "Could not copy",
            type: "warning",
          }),
      );
    }
  };

  const handleDownloadCertificationPdf = () => {
    if (!selectedNode) return;
    const docName =
      selectedNode?.CertificationDocumentName ||
      selectedNode?.FileName ||
      "Document";
    const certId = selectedNode?.CertificationId || "";
    const docId = getExternalDocumentIdDisplay(selectedNode);
    const certifiedBy =
      getCertifiedByDisplayName(selectedNode, userData) || "N/A";
    const dateTime =
      selectedNode?.CertifiedDateTime ||
      (selectedNode?.CertificationDateTime
        ? new Date(selectedNode.CertificationDateTime).toLocaleString()
        : "N/A");
    const standard =
      selectedNode?.RegulatoryStandard || "21 CFR Part 11 / ICH GCP E6(R3)";
    const hash =
      selectedNode?.DocumentHash ||
      selectedNode?.CertificationDocumentHash ||
      "";
    const declaration = `Declaration recorded: I, ${certifiedBy}, certify that this is an accurate and complete reproduction of the original document. This digital copy has been verified for legibility, completeness, and integrity in accordance with ICH GCP E6(R3) and 21 CFR Part 11.`;

    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 20;
      let y = 20;

      // Green header
      doc.setFillColor(25, 135, 84);
      doc.rect(0, 0, pageW, 36, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.text("Copy Certified Successfully", pageW / 2, 16, {
        align: "center",
      });
      doc.setFontSize(10);
      doc.text(
        "This document has been certified as an accurate and complete reproduction",
        pageW / 2,
        26,
        { align: "center" },
      );

      y = 48;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.text("CERTIFICATION RECORD", margin, y);
      y += 10;
      doc.setFont(undefined, "normal");
      doc.setFontSize(10);

      const row = (label, value) => {
        doc.setDrawColor(220, 220, 220);
        doc.line(margin, y, pageW - margin, y);
        y += 2;
        doc.text(label, margin, y);
        doc.text(String(value || "—"), margin + 55, y);
        y += 8;
      };

      row("Certification ID", certId);
      row("Document", docName);
      row("External Document Id", docId === "N/A" ? "" : docId);
      row("Certified By", certifiedBy);
      row("Date & Time", dateTime);
      row("Regulatory Standard", standard);
      y += 4;

      doc.setFont(undefined, "bold");
      doc.text("DOCUMENT HASH (SHA-256)", margin, y);
      y += 8;
      doc.setFont(undefined, "normal");
      doc.setDrawColor(240, 240, 240);
      doc.setFillColor(248, 249, 250);
      doc.rect(margin, y - 5, pageW - 2 * margin, 12, "FD");
      doc.text(hash || "—", margin + 2, y + 3, {
        maxWidth: pageW - 2 * margin - 4,
      });
      y += 20;

      doc.setFillColor(212, 237, 218);
      doc.setDrawColor(195, 230, 203);
      const declH =
        doc.getTextDimensions(declaration, { maxWidth: pageW - 2 * margin - 8 })
          .h + 10;
      doc.rect(margin, y, pageW - 2 * margin, declH, "FD");
      doc.setTextColor(21, 87, 36);
      doc.text(declaration, margin + 4, y + 8, {
        maxWidth: pageW - 2 * margin - 8,
      });
      doc.setTextColor(0, 0, 0);

      const fileName = `Certification_${(docName || "Document").replace(/[^a-zA-Z0-9._-]/g, "_")}.pdf`;
      doc.save(fileName);
      showToast({
        title: "Downloaded",
        message: "Certification PDF has been downloaded.",
        type: "success",
      });
    } catch (err) {
      console.error("PDF generation failed:", err);
      showToast({
        title: "Error",
        message: "Could not generate PDF. Please try again.",
        type: "error",
      });
    }
  };

  const submitCertificationWizard = async () => {
    const docId = selectedNode?.DocumentIdRef ?? selectedNode?.id;
    if (!docId || !certSignatureReason) {
      showAlert({
        title: "Validation",
        message: "Please select a reason for signing.",
        type: "warning",
      });
      return;
    }
    const res = await openAuthModal({ showDescription: false });
    if (res.Success !== 1) {
      if (!res.cancelled) {
        showAlert({
          title: "Authentication failed",
          message: "Please try again.",
          type: "danger",
        });
      }
      return;
    }
    const hashToSave = certGeneratedHash || generateSha256StyleHash();
    try {
      showLoader("Generating certification...");
      const response = await apiClient.post(
        API_ENDPOINTS.ISF.GENERATE_CERTIFICATION,
        {
          CreatedBy: userData?.StaffId || "",
          OrganizationId: userData?.OrganizationId || "",
          ActionType: "SaveCertification",
          DocumentIdRef: docId,
          StudyIdRef: studyDetails?.StudyId || formData?.study?.value,
          DocumentHash: hashToSave,
          SignatureReason: certSignatureReason,
          ClientIP: certClientIp || undefined,
        },
      );
      const ok =
        response?.success === 1 ||
        response?.data?.Success === 1 ||
        response?.data?.SuccessStory?.[0]?.Success === 1;
      const certRow = response?.data?.SuccessStory?.[0];
      const msg =
        response?.data?.message ||
        response?.data?.MSG ||
        response?.data?.SuccessStory?.[0]?.MSG;
      if (ok) {
        showAlert({
          title: "Success",
          message: msg || "Certification generated successfully",
          type: "success",
        });
        closeCertificationWizard();
        const certificationIdFromApi =
          certRow?.CertificationId ||
          response?.data?.CertificationId ||
          "certified";
        const certifiedByName =
          certRow?.CertifiedByName ??
          userData?.UserName ??
          userData?.EmailId ??
          "—";
        const certifiedDateTime =
          certRow?.CertifiedDateTime ?? new Date().toISOString();
        const updatedCertificationFields = {
          CertificationId: certificationIdFromApi,
          CertifiedBy: userData?.StaffId,
          CertifiedByName: certifiedByName,
          CertificationDateTime: certifiedDateTime,
          CertificationDocumentName:
            selectedNode?.FileName || selectedNode?.name,
          CertificationDocumentCode: (() => {
            const d = getDisplayDocumentId(selectedNode);
            if (d !== "N/A") return d;
            return (
              selectedNode?.DocumentCode || selectedNode?.DocumentIdRef || ""
            );
          })(),
          DocumentHash: certRow?.DocumentHash ?? hashToSave,
          RegulatoryStandard: "21 CFR Part 11 / ICH GCP E6(R3)",
        };
        const mapTreeWithCertification = (nodes, targetId, certFields) => {
          if (!Array.isArray(nodes)) return nodes;
          return nodes.map((n) => {
            if (
              n.type === "file" &&
              (n.id === targetId || n.DocumentIdRef === targetId)
            )
              return { ...n, ...certFields };
            if (n.children?.length)
              return {
                ...n,
                children: mapTreeWithCertification(
                  n.children,
                  targetId,
                  certFields,
                ),
              };
            return n;
          });
        };
        setDocuments((prev) =>
          prev.map((d) =>
            d.DocumentIdRef === docId || d.id === docId
              ? { ...d, ...updatedCertificationFields }
              : d,
          ),
        );
        setTreeData((prev) =>
          mapTreeWithCertification(prev, docId, updatedCertificationFields),
        );
        setSelectedNode((prev) => {
          if (!prev || (prev.DocumentIdRef !== docId && prev.id !== docId))
            return prev;
          return { ...prev, ...updatedCertificationFields };
        });
        setShowCertificationSection(true);
      } else {
        showAlert({
          title: "Error",
          message: msg || "Failed to generate certification",
          type: "danger",
        });
      }
    } catch (err) {
      console.error("Generate certification error:", err);
      showAlert({
        title: "Error",
        message:
          err?.response?.data?.message || "Failed to generate certification",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const fileUploadAction = (node, options = {}) => {
    if (!canEditCurrentStudyGate) {
      showHistoricalGateReadOnlyAlert();
      return;
    }
    const { studyPlaceholderId, folderId, supersedeDocumentId, sourceDocument } =
      options;
    if (folderId) {
      setUploadTargetFolderId(folderId);
    } else if (node?.type === "folder") {
      setUploadTargetFolderId(node.id);
    } else {
      setUploadTargetFolderId("");
    }
    setUploadPlaceholderId(studyPlaceholderId || "");
    setUploadPlaceholderLocked(!!studyPlaceholderId);
    setUploadSupersedeDocumentId(
      supersedeDocumentId ? String(supersedeDocumentId) : "",
    );
    setUploadFileKey((key) => key + 1);
    setFileData({});
    setUploadFileNameError("");
    setDocumentCodeError("");
    if (supersedeDocumentId && sourceDocument) {
      const extId = String(sourceDocument.ExtDocumentId || "").trim();
      setFormData((prev) => ({
        ...prev,
        uploadFileName: "",
        previousDoc: "",
        documentCode: extId && extId !== "N/A" ? extId : prev.documentCode,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        uploadFileName: "",
        previousDoc: "",
      }));
    }
    setShowFileUploadModal(true);
  };

  const clearUploadFileSelection = () => {
    setUploadFileKey((key) => key + 1);
    setFileData({});
    setUploadFileNameError("");
    setFormData((prev) => ({
      ...prev,
      uploadFileName: "",
    }));
  };

  const handleUploadToPlaceholder = (phNode) => {
    if (!canEditCurrentStudyGate) {
      showHistoricalGateReadOnlyAlert();
      return;
    }
    if (!phNode?.parentId) return;
    const phRow =
      studyPlaceholders.find(
        (p) =>
          String(p.StudyPlaceholderId) ===
          String(phNode.studyPlaceholderId || phNode.StudyPlaceholderId),
      ) || phNode;
    if (!isPendingStudyPlaceholder(phRow, documents, studyPlaceholders)) {
      showAlert({
        title: "Already fulfilled",
        message:
          "This expected document already has an uploaded file. Select the file in the folder tree to view it.",
        type: "info",
      });
      return;
    }
    if (!canUploadToPlaceholder(phNode)) {
      showAlert({
        title: "Upload not allowed",
        message:
          "Only the staff member assigned to this expected document can upload a file.",
        type: "warning",
      });
      return;
    }
    const placeholderNode = {
      ...phNode,
      ...phRow,
      type: "placeholder",
      id:
        phNode.id ||
        `ph-${phRow.StudyPlaceholderId || phRow.studyPlaceholderId}`,
    };
    const folderId =
      stripGuid(phRow.FolderIdRef || phNode.FolderIdRef) ||
      (phNode.parentId && !String(phNode.parentId).startsWith("study-ms-")
        ? stripGuid(phNode.parentId)
        : "");

    afterEditorRelease(() => {
      setSelectedNode(placeholderNode);
      setSelectedId(String(placeholderNode.id || "").toLowerCase());
      setShowStudyBasicInfo(true);
      setShowStudySection(true);
      setShowDocumentSection(false);
      releaseOnlyOffice();
      setImagePreviewUrl(null);
      setsSlectedFile(null);

      fileUploadAction(null, {
        studyPlaceholderId:
          phRow.StudyPlaceholderId || phRow.studyPlaceholderId,
        folderId,
      });
    });
  };

  const openWaivePlaceholderModal = (phNode) => {
    if (!canEditCurrentStudyGate) {
      showHistoricalGateReadOnlyAlert();
      return;
    }
    if (!studyPlaceholderCanBeWaived(phNode, documents, studyPlaceholders)) {
      showAlert({
        title: "Cannot waive",
        message:
          "Only optional, pending expected documents can be waived.",
        type: "warning",
      });
      return;
    }
    setWaiveTarget(phNode);
    setWaiveReason("");
    setShowWaiveModal(true);
  };

  const handleConfirmWaivePlaceholder = async () => {
    if (!canEditCurrentStudyGate) {
      showHistoricalGateReadOnlyAlert();
      return;
    }
    const ph = waiveTarget;
    const reason = waiveReason.trim();
    if (!ph || !studyPlaceholderCanBeWaived(ph, documents, studyPlaceholders)) {
      showAlert({
        title: "Cannot waive",
        message:
          "Only optional, pending expected documents can be waived.",
        type: "warning",
      });
      return;
    }
    if (!ph || !reason) {
      showAlert({
        title: "Reason required",
        message: "Enter a reason before waiving this expected document.",
        type: "warning",
      });
      return;
    }
    try {
      showLoader("Waiving expected document...");
      await waiveStudyPlaceholder({
        organizationId: userData.OrganizationId,
        studyId: studyDetails?.StudyId || formData?.study?.value,
        studyPlaceholderId: ph.studyPlaceholderId || ph.StudyPlaceholderId,
        waivedReason: reason,
        createdBy: userData.StaffId,
      });
      setShowWaiveModal(false);
      setWaiveTarget(null);
      setSelectedNode(null);
      setSelectedId("");
      await getStudyData();
      showToast({ message: "Expected document waived.", type: "success" });
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to waive expected document.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleCloseFileUpload = () => {
    isfTourUploadDemoRef.current = false;
    setShowFileUploadModal(false);
    setUploadPlaceholderId("");
    setUploadPlaceholderLocked(false);
    setUploadTargetFolderId("");
    setUploadSupersedeDocumentId("");
    setUploadFileKey((key) => key + 1);
    setFileData({});
    setDocumentCodeError("");
    setUploadFileNameError("");
    setFormData((prev) => ({
      ...prev,
      previousDoc: "",
      isMandatorySign: false,
      mandatorySignUsers: [],
      documentCode: "",
      uploadFileName: "",
      documentType: "",
      documentCategory: "",
      expiryDate: null,
      searchTags: "",
    }));
  };

  const fileUploadingProcess = async () => {
    if (!canEditCurrentStudyGate) {
      showHistoricalGateReadOnlyAlert();
      return;
    }
    const targetFolderId = stripGuid(
      uploadTargetFolderId ||
        (selectedNode?.type === "folder" ? selectedNode?.id : "") ||
        "",
    );
    const isNewVersionUpload = Boolean(uploadSupersedeDocumentId);
    const supersededSourceDoc = isNewVersionUpload
      ? findStudyDocument(documents, uploadSupersedeDocumentId)
      : null;
    const placeholderIdForLink =
      uploadPlaceholderId ||
      supersededSourceDoc?.StudyPlaceholderId ||
      supersededSourceDoc?.studyPlaceholderId ||
      "";
    const uploadPhForFolderName = placeholderIdForLink
      ? studyPlaceholders.find(
          (p) =>
            String(p.StudyPlaceholderId || "").toLowerCase() ===
            String(placeholderIdForLink).toLowerCase(),
        )
      : null;
    const targetFolderName =
      findStudyFolderNameInTree(treeData, targetFolderId) ||
      (selectedNode?.type === "folder" ? selectedNode?.name : "") ||
      String(uploadPhForFolderName?.FolderName || "").trim() ||
      String(supersededSourceDoc?.FolderName || "").trim();
    const documentDisplayName = String(
      formData?.uploadFileName || fileData?.name || "",
    ).trim();
    const activeUploadSiteId = stripGuid(getActiveSiteIdRef() || "") || null;

    if (!fileData?.data || !fileData?.name) {
      showAlert({
        title: "File required",
        message: "Please select a file to upload.",
        type: "warning",
      });
      return;
    }

    if (!targetFolderId) {
      showAlert({
        title: "Folder required",
        message: "Could not determine the target folder. Close the dialog and try again from the folder or expected document.",
        type: "warning",
      });
      return;
    }

    if (!targetFolderName) {
      showAlert({
        title: "Folder required",
        message:
          "Could not resolve the target folder name for this site. Refresh the binder and try again.",
        type: "warning",
      });
      return;
    }

    if (!formData?.study?.value) {
      showAlert({
        title: "Study required",
        message: "No study is selected.",
        type: "warning",
      });
      return;
    }

    const documentCodeTrimmed = String(formData?.documentCode || "").trim();
    if (!documentCodeTrimmed) {
      showAlert({
        title: "External document ID required",
        message: "Please enter an external document ID before uploading.",
        type: "warning",
      });
      return;
    }

    if (!documentDisplayName) {
      showAlert({
        title: "Document name required",
        message: "Please enter a document name before uploading.",
        type: "warning",
      });
      return;
    }

    const nameValidationError = validateUploadDocumentName(
      documentDisplayName,
      documents,
      targetFolderId,
      { checkDuplicate: false },
    );
    const extIdValidationError = validateUploadExternalDocumentId(
      documents,
      documentCodeTrimmed,
      { checkDuplicate: !uploadSupersedeDocumentId },
    );
    if (nameValidationError || extIdValidationError) {
      setUploadFileNameError(nameValidationError || "");
      setDocumentCodeError(extIdValidationError || "");
      return;
    }

    try {
      showLoader("File uploading...");
      const templateCategoryValue =
        formData?.templateCategory?.value ??
        formData?.templateCategoryCreation?.value ??
        (typeof activeCategory === "object" && activeCategory?.value
          ? activeCategory.value
          : typeof activeCategory === "string"
            ? activeCategory
            : "") ??
        "";
      const documentTypeValue =
        typeof formData?.documentType === "string"
          ? formData.documentType
          : (formData?.documentType?.value ?? "");
      const documentCategoryValue =
        typeof formData?.documentCategory === "string"
          ? formData.documentCategory
          : (formData?.documentCategory?.value ?? "");
      const previousDocumentIdRef = isNewVersionUpload
        ? stripGuid(
            supersededSourceDoc?.DocumentIdRef || uploadSupersedeDocumentId,
          )
        : formData?.previousDoc?.value != null &&
            formData.previousDoc.value !== ""
          ? String(formData.previousDoc.value)
          : "";
      const uploadPhRow = placeholderIdForLink
        ? studyPlaceholders.find(
            (p) =>
              String(p.StudyPlaceholderId) === String(placeholderIdForLink),
          )
        : null;
      const uploadSignature = uploadPhRow
        ? buildMandatorySignUsersFromPlaceholder(
            uploadPhRow,
            staffList,
            studyRoleOptions,
          )
        : { isMandatorySign: false, mandatorySignUsers: [] };
      const mandatoryUserIds = (uploadSignature.mandatorySignUsers || [])
        .map((u) => u?.value)
        .filter(Boolean)
        .join(",");
      const searchTagsTrimmed = String(formData?.searchTags || "").trim();
      /**
       * Save into the binder folder itself (ParentFolderIdRef + matching FolderName).
       * Do not mint a random child FolderId — that created blank SiteId NULL ghosts.
       */
      const uploadFolderIdRef = targetFolderId;

      const payload = {
        OrganizationIdRef: userData?.OrganizationId || "",
        ActionType: "SaveChildFolder",
        StudyIdRef: formData?.study?.value || "",
        FolderIdRef: uploadFolderIdRef,
        ParentFolderIdRef: targetFolderId,
        FolderName: targetFolderName,
        DocumentIdRef: null,
        FileName: documentDisplayName,
        FileUrl: "",
        StaffIdRef: String(userData?.StaffId || "").toUpperCase(),
        ActionName: "Save File",
        PreviousDocumentIdRef: previousDocumentIdRef || null,
        FileSize: fileData?.size ?? "",
        Base64String: fileData?.data || "",
        FileExtension: fileData?.extension || "",
        IsMandatorySign: uploadSignature.isMandatorySign ? 1 : 0,
        MandatorySignUserIds: mandatoryUserIds,
        DocumentCode: documentCodeTrimmed,
        ExpiryDate: formData?.expiryDate
          ? formData.expiryDate.toISOString()
          : "",
        DocumentType: documentTypeValue || null,
        DocumentCategory: documentCategoryValue || null,
        SearchTags: searchTagsTrimmed || null,
        TemplateCategory: templateCategoryValue,
        StudyPlaceholderId: placeholderIdForLink || null,
        SiteIdRef: activeUploadSiteId,
      };

      const response = await apiClient.post(
        API_ENDPOINTS.ISF.SAVE_FOLDER_DOCUMENTS,
        payload,
      );

      const responseRows = Array.isArray(response?.data)
        ? response.data
        : response?.data?.SuccessStory ?? (response?.data ? [response.data] : []);
      const responseRow = responseRows[0] ?? null;
      const rowSuccess =
        responseRow?.Success === 1 ||
        responseRow?.Success === "1" ||
        responseRow?.success === 1;
      const newDocId =
        responseRow?.DocumentIdRef ??
        responseRow?.documentIdRef ??
        "";

      if (!rowSuccess) {
        showAlert({
          title: "Upload failed",
          message:
            responseRow?.MSG ||
            responseRow?.Msg ||
            response?.message ||
            utils.getApiResponseMessage(
              response,
              "The file could not be uploaded. Please try again or contact your system administrator.",
            ),
          type: "danger",
        });
        return;
      }

      cleanupEditor();
      setFormData((prev) => ({
        ...prev,
        previousDoc: "",
        isMandatorySign: false,
        mandatorySignUsers: [],
        documentCode: "",
        uploadFileName: "",
        documentType: "",
        documentCategory: "",
        expiryDate: null,
        searchTags: "",
      }));
      setDocumentCodeError("");
      setUploadFileNameError("");
      setShowFileUploadModal(false);
      setUploadPlaceholderId("");
      setUploadPlaceholderLocked(false);
      setUploadTargetFolderId("");
      setUploadSupersedeDocumentId("");
      setUploadFileKey((key) => key + 1);
      setFileData({});

      showToast({
        message: isNewVersionUpload
          ? "New document version uploaded successfully."
          : "Document uploaded successfully.",
        type: "success",
      });

      const refreshed = await getStudyData(
        formData?.study?.value,
        templateCategoryValue ||
          formData?.templateCategory?.value ||
          formData?.templateCategoryCreation?.value ||
          "",
      );

      const refreshedData = normalizeStudyFolderResponseData(refreshed?.data ?? {});
      const refreshedDocs = Array.isArray(refreshedData.FolderDocuments)
        ? refreshedData.FolderDocuments
        : [];

      let latestDocs = refreshedDocs;

      const resolvedDocId = isNewVersionUpload
        ? resolveDocumentIdAfterSupersedeUpload(
            responseRow,
            refreshedDocs,
            supersededSourceDoc,
            {
              fileName: documentDisplayName,
              folderId: targetFolderId,
              fallbackId: newDocId,
            },
          )
        : resolveUploadedStudyDocumentId(responseRow, refreshedDocs, {
            fileName: documentDisplayName,
            folderId: targetFolderId,
            fallbackId: newDocId,
          });

      if (placeholderIdForLink && resolvedDocId && userData?.OrganizationId) {
        try {
          await linkStudyPlaceholderDocument({
            organizationId: userData.OrganizationId,
            studyId: formData?.study?.value || "",
            studyPlaceholderId: placeholderIdForLink,
            documentIdRef: resolvedDocId,
            createdBy: userData.StaffId,
          });
          const linkedRefresh = await getStudyData(
            formData?.study?.value,
            templateCategoryValue ||
              formData?.templateCategory?.value ||
              formData?.templateCategoryCreation?.value ||
              "",
          );
          const linkedData = normalizeStudyFolderResponseData(
            linkedRefresh?.data ?? {},
          );
          latestDocs = Array.isArray(linkedData.FolderDocuments)
            ? linkedData.FolderDocuments
            : refreshedDocs;
        } catch {
          /* non-blocking — tree repair may still nest via heuristics */
        }
      }

      if (resolvedDocId && latestDocs.length) {
        const uploadedDoc = findStudyDocument(latestDocs, resolvedDocId);
        if (uploadedDoc) {
          const placeholderParentId = placeholderIdForLink
            ? getStudyPlaceholderTreeNodeId({
                StudyPlaceholderId: placeholderIdForLink,
              })
            : null;
          const treeNode = {
            id: uploadedDoc.DocumentIdRef,
            name: uploadedDoc.FileName,
            type: "file",
            parentId:
              placeholderParentId || stripGuid(uploadedDoc.FolderIdRef),
            studyPlaceholderId: placeholderIdForLink || null,
            VersionNo: uploadedDoc.VersionNo,
            isMandatorySign:
              uploadedDoc.IsMandatorySign === 1 ||
              uploadedDoc.IsMandatorySign === true ||
              (uploadPhRow && studyPlaceholderRequiresSignature(uploadPhRow)),
          };
          setSelectedId(String(uploadedDoc.DocumentIdRef).toLowerCase());
          handleNodeSelect(treeNode, "Selection", uploadedDoc);
        }
      } else if (resolvedDocId) {
        setSelectedId(String(resolvedDocId).toLowerCase());
      }
    } catch (error) {
      console.error("Document upload failed:", error);
      showAlert({
        title: "Upload failed",
        message: utils.sanitizeUserFacingMessage(
          error?.message,
          "The file could not be uploaded. Please try again or contact your system administrator.",
        ),
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const [modalFolderName, setModaFolderName] = useState("");

  const folderActionProcess = () => {
    setShowFolderCRUDModal(true);
  };

  const folderCRUDProcess = async () => {
    try {
      showLoader("Request Processing...");
      const response = await apiClient.post(
        API_ENDPOINTS.ISF.SAVE_FOLDER_DOCUMENTS,
        {
          OrganizationIdRef: userData?.OrganizationId || "",
          ActionType:
            selectedAction === "newFolder"
              ? "SaveParentFolder"
              : "SaveChildFolder",
          StudyIdRef: formData?.study?.value || "",
          FolderIdRef: "",
          ParentFolderIdRef: selectedNode?.id || "",
          FolderName: modalFolderName,
          DocumentIdRef: "",
          FileName: "",
          FileUrl: "",
          StaffIdRef: userData?.StaffId.toUpperCase() || "",
          ActionName:
            selectedAction === "subFolder"
              ? "Save Sub Folder"
              : selectedAction === "renameFolder"
                ? "Update Folder Name"
                : "",
          PreviousDocumentIdRef: "",
          FileSize: "",
          Base64String: "",
          FileExtension: "",
          TemplateCategory: formData.templateCategory.value,
          SiteIdRef: getActiveSiteIdRef() || null,
        },
      );
      let msg = response.data[0];
      showAlert({
        title: msg.Success === 1 ? "success" : "warning",
        message: msg.MSG,
        type: msg.Success === 1 ? "success" : "warning",
      });
      if (msg.Success === 1) {
        setModaFolderName("");
        if (selectedAction != "renameFolder")
          setSelectedId(response.data[0].FolderIdRef?.toLowerCase());
        setShowFolderCRUDModal(false);
        getStudyData();
      }
    } catch (error) {
      console.error("Error fetching study:", error);
      throw error;
    } finally {
      hideLoader();
    }
  };

  //console.log(userData);
  const [openSide, setOpenSide] = useState(false);

  const showAuth = async (action, FolderId) => {
    let actionType = "";
    if (action === "deleteFile") {
      actionType = "DeleteDocument";
    } else if (action === "deleteFolder") {
      actionType = "DeleteFolder";
    } else if (action === "lockFile") {
      actionType = "DocumentLock";
    } else if (action === "unlockFile") {
      actionType = "DocumentUnLock";
    }
    const res = await openAuthModal({ showDescription: true });
    if (res.Success === 1) {
      hideConfirm();
      try {
        showLoader("Request Processing...");
        const response = await apiClient.post(
          action === "deleteFolder"
            ? API_ENDPOINTS.ISF.FOLDER_DELETE
            : API_ENDPOINTS.ISF.FILE_DELETE_LOCK,
          {
            ActionType: actionType,
            DocumentId: selectedNode?.DocumentIdRef,
            FolderId:
              action === "deleteFolder" ? FolderId : selectedNode.FolderIdRef,
            EmailId: userData.EmailId,
            Password: res.Password,
            AccessCodeIndex0: res.AccessCodeIndex0,
            AccessCodeIndex1: res.AccessCodeIndex1,
            AccessCodeIndex2: res.AccessCodeIndex2,
            AccessCodeValue0: res.AccessCodeValue0,
            AccessCodeValue1: res.AccessCodeValue1,
            AccessCodeValue2: res.AccessCodeValue2,
            StudyRefId: studyDetails.StudyId,
            Reason: res.Description,
            NewFolderId: "",
            OrganizationIdRef: userData?.OrganizationId || "",
            CreatedBy: userData?.StaffId || "",
          },
        );
        showAlert({
          title: response.success === 1 ? "Success" : "Error",
          message: response.message,
          type: response.success === 1 ? "success" : "danger",
        });
        setSelectedNode(null);
        cleanupEditor();
        getStudyData();
      } catch (error) {
        console.error("Error fetching study:", error);
        throw error;
      } finally {
        hideLoader();
      }
    }
  };
  const [selectedId, setSelectedId] = useState("");
  const [showSignModal, setShowSignModal] = useState(false);
  const [sessionSignature, setSessionSignature] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  // Certification wizard: step 0=confirm, 1=reviewed?, 2=document info, 3=declaration, 4=sign & certify
  const [showCertificationWizardModal, setShowCertificationWizardModal] =
    useState(false);
  const [certificationWizardStep, setCertificationWizardStep] = useState(0);
  const [certDeclarationAccepted, setCertDeclarationAccepted] = useState(false);
  const [certDeclarationScrolledToEnd, setCertDeclarationScrolledToEnd] =
    useState(false);
  const certDeclarationScrollRef = useRef(null);
  const [certSignatureReason, setCertSignatureReason] = useState("");
  const CERTIFICATION_STEPS = [
    "Review Document",
    "Confirm Completeness",
    "Declaration",
    "Sign & Certify",
  ];
  const SIGNATURE_REASONS = [
    { value: "certification_true_copy", label: "Certification of true copy" },
    { value: "document_certification", label: "Document certification" },
    { value: "compliance_attestation", label: "Compliance attestation" },
    { value: "other", label: "Other" },
  ];
  const [certDeclarationRefId, setCertDeclarationRefId] = useState("");
  const [certGeneratedHash, setCertGeneratedHash] = useState("");
  const [certClientIp, setCertClientIp] = useState("");
  useEffect(() => {
    if (!showCertificationWizardModal) return;
    let cancelled = false;
    const fetchIp = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const res = await fetch("https://api.ipify.org?format=json", {
          method: "GET",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        const data = await res.json();
        if (data?.ip) setCertClientIp(data.ip);
      } catch (e) {
        if (!cancelled) setCertClientIp("");
      }
    };
    fetchIp();
    return () => {
      cancelled = true;
    };
  }, [showCertificationWizardModal]);

  const generateSha256StyleHash = () => {
    const bytes = new Uint8Array(32);
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < 32; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  // Require user to scroll declaration to end before enabling confirm + Accept button
  useEffect(() => {
    if (certificationWizardStep === 3) {
      setCertDeclarationScrolledToEnd(false);
    }
  }, [certificationWizardStep]);
  const handleDeclarationScroll = (e) => {
    const el = e.target;
    const threshold = 20;
    const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    if (atEnd) setCertDeclarationScrolledToEnd(true);
  };
  const generateCertDeclarationRef = () =>
    "CERT-" +
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().slice(0, 8).toUpperCase()
      : Date.now().toString(36).toUpperCase().slice(-8));
  const [signatureMode, setSignatureMode] = useState("draw"); // "draw" | "type"
  const [typedSignature, setTypedSignature] = useState("");
  const signatureCanvasRef = useRef(null);
  const [isPRotocolSelected, setIsProtocolSelected] = useState([false]);
  const handleCheckboxChange = () => {
    setIsProtocolSelected([!isPRotocolSelected[0]]);
  };

  const loadSignatureFromSession = () => {
    try {
      let stored = sessionStorage.getItem("SignatureBase64");
      if (!stored && userData?.SignatureBase64) {
        stored = userData.SignatureBase64;
        sessionStorage.setItem("SignatureBase64", stored);
      }
      setSessionSignature(stored || null);
    } catch (e) {
      console.error("Error loading signature from session:", e);
      setSessionSignature(null);
    }
  };

  useEffect(() => {
    loadSignatureFromSession();
  }, [userData?.SignatureBase64]);

  const typedSignatureToDataUrl = (text) => {
    if (!text || !text.trim()) return null;
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 160;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#f8f9fa";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#1a1a1a";
    ctx.font =
      'italic 42px "Dancing Script", "Brush Script MT", "Segoe Script", cursive';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text.trim(), canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL("image/png");
  };

  const saveSignatureForCurrentUser = async (signatureDataUrl) => {
    if (!signatureDataUrl || !userData?.StaffId || !userData?.OrganizationId) {
      return false;
    }
    try {
      showLoader("Saving signature...");
      const response = await apiClient.post(
        API_ENDPOINTS.USER.SAVE_ESIGNATURE,
        {
          StaffId: userData.StaffId,
          OrganizationId: userData.OrganizationId,
          SignatureBase64: signatureDataUrl,
          CreatedBy: userData.StaffId,
          Action: "UpdateSignature",
        },
      );
      const success = response?.success === 1 || response?.data?.success === 1;
      if (success) {
        showAlert({
          title: "Success",
          message: "Signature saved successfully.",
          type: "success",
        });
        try {
          sessionStorage.setItem("SignatureBase64", signatureDataUrl);
        } catch (e) {
          console.error("Error storing signature in session:", e);
        }
        setSessionSignature(signatureDataUrl);
      }
      return !!success;
    } catch (error) {
      console.error("Error saving signature:", error);
      showAlert({
        title: "Error",
        message: "Failed to save signature. Please try again.",
        type: "error",
      });
      return false;
    } finally {
      hideLoader();
    }
  };

  const signProcess = async () => {
    const res = await openAuthModal({
      showDescription: true,
      requireDescription: true,
      useMfa: true,
      descriptionLabel: "Reason for signing",
      descriptionPlaceholder: "Enter why you are signing this document…",
    });
    if (res.Success !== 1) {
      if (!res.cancelled) {
        showAlert({
          title: "Authentication required",
          message:
            "Multi-factor authentication failed or was cancelled. Document was not signed.",
          type: "warning",
        });
      }
      return;
    }

    const signatureReason = String(res.Description || "").trim();
    if (!signatureReason) {
      showAlert({
        title: "Reason required",
        message: "A reason for signing is required.",
        type: "warning",
      });
      return;
    }

    const studyIdForRefresh = formData?.study?.value;
    const categoryForRefresh =
      formData?.templateCategoryCreation?.value ??
      formData?.templateCategory?.value ??
      "";
    const docId = selectedNode?.DocumentIdRef || selectedNode?.id;
    const signerId = userData?.StaffId;
    const signerName =
      userData?.UserName ||
      [userData?.FirstName, userData?.LastName].filter(Boolean).join(" ") ||
      userData?.EmailId ||
      "Signer";

    setIsProtocolSelected([!isPRotocolSelected[0]]);
    try {
      showLoader("Request Processing...");
      const response = await apiClient.post(
        API_ENDPOINTS.ISF.FILE_SAVE_SIGN,
        {
          DocumentIdRef: docId,
          EmailId: userData.EmailId,
          Password: res.Password,
          AccessCodeIndex0: res.AccessCodeIndex0,
          AccessCodeIndex1: res.AccessCodeIndex1,
          AccessCodeIndex2: res.AccessCodeIndex2,
          AccessCodeValue0: res.AccessCodeValue0,
          AccessCodeValue1: res.AccessCodeValue1,
          AccessCodeValue2: res.AccessCodeValue2,
          OrganizationIdRef: userData?.OrganizationId || "",
          StaffIdRef: signerId || "",
          IsProtocolSign: isPRotocolSelected[0],
          SignatureReason: signatureReason,
        },
      );

      const signOk = response.success === 1;
      showAlert({
        title: signOk ? "Success" : "Error",
        message: response.message,
        type: signOk ? "success" : "danger",
      });

      if (signOk && docId && signerId) {
        pendingSignatureRefreshRef.current = {
          docId,
          staffId: signerId,
          staffName: signerName,
        };
        setDocuments((prev) =>
          patchDocumentRowsWithSignature(prev, docId, signerId, signerName),
        );
        setTreeData((prev) =>
          patchTreeNodesWithSignature(prev, docId, signerId, signerName),
        );
        setSelectedNode((prev) => {
          if (!prev || documentIdentityKey(prev) !== documentIdentityKey(docId)) {
            return prev;
          }
          return {
            ...prev,
            ...buildDocumentSignatureStatePatch(prev, signerId, signerName),
            type: prev.type || "file",
          };
        });
      }

      setShowSignModal(false);
      setIsProtocolSelected([false]);

      if (signOk) {
        await getStudyData(studyIdForRefresh, categoryForRefresh, undefined, {
          withLoader: false,
        });
        pendingSignatureRefreshRef.current = null;
      }
    } catch (error) {
      console.error("Error signing document:", error);
      throw error;
    } finally {
      hideLoader();
    }
  };

  const [showRestricFolderModal, setShowRestricFolderModal] = useState(false);
  const [selectedStaffList, setSelectedStaffList] = useState([]);
  const currentSelectedStaffRef = useRef([]); // Store current selection without causing re-renders
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [auditLogFolderId, setAuditLogFolderId] = useState(null);
  const [auditLogDocumentId, setAuditLogDocumentId] = useState(null);

  const fetchSelectedItemsfunc = useCallback(async () => {
    // Return array of selected user IDs (from backend initial load)
    // This is only called during DualListBox initialization
    const selectedIds =
      selectedStaffList?.map((s) => s.value.toLowerCase()) || [];
    return selectedIds;
  }, [selectedStaffList]);

  const handleRestrictedUserSave = async () => {
    try {
      showLoader("Records updating");
      // Get current selection from ref (has all user selections)
      const currentSelected = currentSelectedStaffRef.current || [];
      // Extract value property from each selected item
      const restrictedUserIds = currentSelected
        .map((item) => item.value)
        .join(",");

      const response = await apiClient.post(
        API_ENDPOINTS.ISF.GET_STUDY_FOLDERS,
        {
          FolderIdRef: selectedNode?.id,
          OrganizationIdRef: userData?.OrganizationId || "",
          ActionType: "RestrictUserFromFolder",
          RestrictedUserIds: restrictedUserIds,
        },
      );

      if (response.data.SuccessStory[0].Success === 1) {
        showAlert({
          title: "Success",
          message:
            response.data.SuccessStory[0].MSG ||
            "Users restricted successfully",
          type: "success",
        });
        setShowRestricFolderModal(false);
        getStudyData(); // Refresh the tree data
      } else {
        showAlert({
          title: "Error",
          message:
            response.data.SuccessStory[0].MSG || "Failed to restrict users",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error updating records:", error);
      showAlert({
        title: "Error",
        message: "Failed to update restricted users",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };
  const [formRestrictedUser, setFormRestrictedUser] = useState([]);

  // const handleChangeRestrictedUser = (selected) => {
  //   let temp = selected.map((item) => {
  //     return {
  //       id: item.id,
  //     };
  //   });
  //   setFormRestrictedUser(selected);
  // };

  const isDocumentIdMissing =
    !formData.documentCode || !String(formData.documentCode).trim();
  const isUploadFileNameMissing =
    !formData.uploadFileName || !String(formData.uploadFileName).trim();
  const isFileMissing = !fileData?.data || !fileData?.name;
  const showUploadFileNameValidation = !!fileData?.name && !!uploadFileNameError;
  const isUploadDisabled =
    !!documentCodeError ||
    !!uploadFileNameError ||
    isDocumentIdMissing ||
    isUploadFileNameMissing ||
    isFileMissing;

  // Collapsible sections inside Study Info
  const [showStudySection, setShowStudySection] = useState(false);
  const [showDocumentSection, setShowDocumentSection] = useState(true);
  const [showMilestoneSection, setShowMilestoneSection] = useState(false);
  const [showCertificationSection, setShowCertificationSection] =
    useState(false);
  const [showCertificationDetailModal, setShowCertificationDetailModal] =
    useState(false);

  // ----- Derived milestone / signature metadata (milestone, placeholder, or file) -----
  const activeMilestonePlaceholder = useMemo(
    () =>
      resolveMilestonePlaceholderForSelection(
        selectedNode,
        studyPlaceholders,
        documents,
      ),
    [selectedNode, studyPlaceholders, documents],
  );

  const activeMilestoneDocument = useMemo(
    () =>
      resolveMilestoneLinkedDocument(
        activeMilestonePlaceholder,
        documents,
        selectedNode,
      ),
    [activeMilestonePlaceholder, documents, selectedNode],
  );

  const selectedPlaceholderRow = useMemo(() => {
    if (selectedNode?.type !== "placeholder") return null;
    return resolveStudyPlaceholderRow(
      selectedNode,
      studyPlaceholders,
      documents,
    );
  }, [selectedNode, studyPlaceholders, documents]);

  const selectedPlaceholderDisplayStatus = useMemo(() => {
    if (selectedNode?.type !== "placeholder") return null;
    return getStudyPlaceholderDisplayStatus(
      selectedPlaceholderRow || selectedNode,
      documents,
      studyPlaceholders,
    );
  }, [
    selectedNode,
    selectedPlaceholderRow,
    documents,
    studyPlaceholders,
  ]);

  const selectedPlaceholderLinkedDoc = useMemo(() => {
    if (!selectedPlaceholderRow) return null;
    const found = findDocumentForStudyPlaceholder(
      selectedPlaceholderRow,
      documents,
      studyPlaceholders,
    );
    if (found) return found;
    // Server already resolved the fulfilling file on the placeholder row
    // (FulfilledDocumentName + DocumentIdRef). Fall back to it so a fulfilled
    // slot never shows "no linked file" just because the document is missing
    // from the locally loaded folder list at render time.
    const docId = stripGuid(selectedPlaceholderRow.DocumentIdRef);
    const fileName = selectedPlaceholderRow.FulfilledDocumentName;
    if (docId && fileName) {
      return {
        DocumentIdRef: docId,
        FileName: fileName,
        FolderIdRef: selectedPlaceholderRow.FolderIdRef,
      };
    }
    return null;
  }, [selectedPlaceholderRow, documents, studyPlaceholders]);

  const selectedPlaceholderOrphanDocs = useMemo(() => {
    if (!selectedPlaceholderRow) return [];
    if (selectedPlaceholderLinkedDoc) return [];
    if (String(selectedPlaceholderRow.Status || "").toLowerCase() !== "fulfilled") {
      return [];
    }
    return findLinkableFolderDocuments(selectedPlaceholderRow, documents);
  }, [
    selectedPlaceholderRow,
    selectedPlaceholderLinkedDoc,
    documents,
    studyPlaceholders,
  ]);

  const handleLinkOrphanDocumentToPlaceholder = async (doc) => {
    if (!canEditCurrentStudyGate) {
      showHistoricalGateReadOnlyAlert();
      return;
    }
    if (!selectedPlaceholderRow?.StudyPlaceholderId || !doc?.DocumentIdRef) return;
    try {
      showLoader("Linking document...");
      await linkStudyPlaceholderDocument({
        organizationId: userData?.OrganizationId,
        studyId: formData?.study?.value || "",
        studyPlaceholderId: selectedPlaceholderRow.StudyPlaceholderId,
        documentIdRef: doc.DocumentIdRef,
        createdBy: userData?.StaffId,
      });
      await getStudyData(formData?.study?.value, formData?.templateCategory?.value || "");
      showToast({ message: "Document linked to placeholder.", type: "success" });
    } catch (error) {
      showAlert({
        title: "Link failed",
        message:
          error?.message ||
          "Could not link the file to this placeholder. Please try again.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const openPlaceholderLinkedDocument = (doc) => {
    const docRow = doc || selectedPlaceholderLinkedDoc;
    if (!docRow?.DocumentIdRef) return;
    const treeNode = {
      id: docRow.DocumentIdRef,
      name: docRow.FileName,
      type: "file",
      parentId: stripGuid(docRow.FolderIdRef),
      studyPlaceholderId:
        selectedPlaceholderRow?.StudyPlaceholderId ||
        selectedNode?.studyPlaceholderId,
      VersionNo: docRow.VersionNo,
      isMandatorySign:
        docRow.IsMandatorySign === 1 ||
        docRow.IsMandatorySign === true ||
        (selectedPlaceholderRow &&
          studyPlaceholderRequiresSignature(selectedPlaceholderRow)),
    };
    handleNodeSelect(treeNode, "Selection", docRow);
  };

  // Deep link from mention email / Sponsor-CRO Comments tab:
  // /isf?studyId=...&category=...&documentId=...&openComments=1
  useEffect(() => {
    if (!isViewReady || showStudyListPopup) return;

    const documentId = String(
      searchParams.get("documentId") ||
        location.state?.openDocumentId ||
        "",
    ).trim();
    if (!documentId) return;

    const openCommentsRaw =
      searchParams.get("openComments") ??
      (location.state?.openComments != null
        ? String(location.state.openComments)
        : "");
    const shouldOpenComments =
      openCommentsRaw === "" ||
      openCommentsRaw === "1" ||
      openCommentsRaw === "true" ||
      location.state?.openComments === true;

    const applyKey = `${String(formData?.study?.value || "").toLowerCase()}|${documentId.toLowerCase()}|${shouldOpenComments ? "1" : "0"}`;
    if (commentDeepLinkAppliedRef.current === applyKey) return;

    const docRow = findStudyDocument(documents, documentId);
    if (!docRow?.DocumentIdRef) return;

    commentDeepLinkAppliedRef.current = applyKey;

    const folderId = stripGuid(docRow.FolderIdRef);
    const expandIds = new Set();
    if (folderId) expandIds.add(folderId.toLowerCase());
    expandIds.add(String(docRow.DocumentIdRef).toLowerCase());

    const walkFindPath = (nodes, target, path = []) => {
      for (const n of nodes || []) {
        const id = String(n?.id || "").toLowerCase();
        if (!id) continue;
        const next = [...path, id];
        if (id === target) return next;
        const found = walkFindPath(n.children, target, next);
        if (found) return found;
      }
      return null;
    };
    const pathIds = walkFindPath(
      treeData,
      String(docRow.DocumentIdRef).toLowerCase(),
    );
    (pathIds || []).forEach((id) => expandIds.add(id));

    if (expandIds.size) {
      setTreeForceExpandedIds((prev) => {
        const next = new Set(
          (prev || []).map((id) => String(id || "").toLowerCase()),
        );
        expandIds.forEach((id) => next.add(id));
        return [...next];
      });
    }

    const treeNode = {
      id: docRow.DocumentIdRef,
      name: docRow.FileName,
      type: "file",
      parentId: folderId,
      VersionNo: docRow.VersionNo,
      isMandatorySign:
        docRow.IsMandatorySign === 1 || docRow.IsMandatorySign === true,
    };
    handleNodeSelect(treeNode, "Selection", docRow);

    if (shouldOpenComments) {
      setOpenSide(true);
    }

    // Drop deep-link query/state so closing the panel does not reopen from URL.
    const nextParams = new URLSearchParams(searchParams);
    let paramsChanged = false;
    if (nextParams.has("documentId")) {
      nextParams.delete("documentId");
      paramsChanged = true;
    }
    if (nextParams.has("openComments")) {
      nextParams.delete("openComments");
      paramsChanged = true;
    }
    const hasCommentState =
      location.state?.openDocumentId != null ||
      location.state?.openComments != null;
    if (paramsChanged || hasCommentState) {
      const qs = nextParams.toString();
      navigate(
        {
          pathname: location.pathname || "/isf",
          search: qs ? `?${qs}` : "",
        },
        { replace: true, state: null },
      );
    }
  }, [
    isViewReady,
    showStudyListPopup,
    documents,
    treeData,
    searchParams,
    location.state,
    location.pathname,
    formData?.study?.value,
    navigate,
  ]);

  const signSource = activeMilestoneDocument || selectedNode;

  /** Tree folders use type "folder"; documents are API rows or type "file" */
  const isSelectedFolder = selectedNode?.type === "folder";
  const isSelectedPlaceholder = selectedNode?.type === "placeholder";
  const isSelectedMilestone = selectedNode?.type === "milestone";
  const showDocumentInfoAndPreview =
    !!selectedNode &&
    !isSelectedFolder &&
    !isSelectedPlaceholder &&
    !isSelectedMilestone;
  const isReadOnlyDocumentView =
    showDocumentInfoAndPreview &&
    (selectedNode?.isPreviousVersion === true ||
      selectedNode?.readOnly === true ||
      isSupersededStudyDocument(selectedNode));

  const linkedDocPlaceholder = activeMilestonePlaceholder;

  const placeholderSignConfig = useMemo(() => {
    if (!linkedDocPlaceholder) return null;
    return buildMandatorySignUsersFromPlaceholder(
      linkedDocPlaceholder,
      staffList,
      studyRoleOptions,
    );
  }, [linkedDocPlaceholder, staffList, studyRoleOptions]);

  const isMandatoryForSelectedDoc = linkedDocPlaceholder
    ? studyPlaceholderRequiresSignature(linkedDocPlaceholder)
    : !!selectedNode &&
      (selectedNode.IsMandatorySign === 1 ||
        selectedNode.IsMandatorySign === true ||
        String(selectedNode.IsMandatorySign || "")
          .toLowerCase()
          .trim() === "true");

  const mandatorySignerIdsFromDoc = parseSignerIdList(
    getMandatorySignUserIdsRaw(signSource),
  );
  const mandatorySignerIds =
    mandatorySignerIdsFromDoc.length > 0
      ? mandatorySignerIdsFromDoc
      : (placeholderSignConfig?.mandatorySignUsers || [])
          .map((u) => normalizeSignerIdForCompare(u.value))
          .filter(Boolean);
  const mandatoryIdSet = new Set(mandatorySignerIds);

  const mandatorySignerList = Array.isArray(staffList)
    ? staffList.filter((staff) =>
        mandatoryIdSet.has(normalizeSignerIdForCompare(staff.value)),
      )
    : [];

  const signedIds = parseSignerIdList(getSignedUserIdsRawFromNode(signSource));
  const signedIdSet = new Set(signedIds);

  const pendingStaffList = mandatorySignerList.filter(
    (staff) => !signedIdSet.has(normalizeSignerIdForCompare(staff.value)),
  );

  const canSignSelectedDocument = useMemo(() => {
    if (
      !showDocumentInfoAndPreview ||
      isReadOnlyDocumentView ||
      !canEditCurrentStudyGate ||
      !isMandatoryForSelectedDoc
    ) {
      return false;
    }
    const docId =
      signSource?.DocumentIdRef ||
      signSource?.id ||
      selectedNode?.DocumentIdRef ||
      selectedNode?.id;
    if (!docId) return false;
    return isCurrentUserEligibleToSignTreeNode({
      id: docId,
      type: "file",
    });
  }, [
    showDocumentInfoAndPreview,
    isReadOnlyDocumentView,
    canEditCurrentStudyGate,
    isMandatoryForSelectedDoc,
    signSource,
    selectedNode,
    isCurrentUserEligibleToSignTreeNode,
  ]);

  const triggerSignDocument = (docRow) => {
    if (!docRow?.DocumentIdRef) return;
    const ph =
      selectedPlaceholderRow ||
      linkedDocPlaceholder ||
      findStudyPlaceholderForDocument(docRow, studyPlaceholders);
    const treeNode = {
      id: docRow.DocumentIdRef,
      type: "file",
      parentId: stripGuid(docRow.FolderIdRef),
      studyPlaceholderId: ph?.StudyPlaceholderId ?? null,
      isMandatorySign:
        docRow.IsMandatorySign === 1 ||
        docRow.IsMandatorySign === true ||
        (ph && studyPlaceholderRequiresSignature(ph)),
    };
    handleNodeAction(treeNode, "signFile");
  };

  const milestoneCodeDisplay =
    linkedDocPlaceholder?.MilestoneCode ||
    linkedDocPlaceholder?.milestoneCode ||
    selectedNode?.milestoneCode ||
    "";
  const milestoneNameDisplay =
    linkedDocPlaceholder?.MilestoneName ||
    linkedDocPlaceholder?.milestoneName ||
    selectedNode?.milestoneName ||
    "";
  const milestoneLabel = buildMilestoneDisplayLabel(
    milestoneCodeDisplay,
    milestoneNameDisplay,
    isSelectedMilestone ? selectedNode?.name || "" : "",
  );
  const milestoneDueRuleDisplay = linkedDocPlaceholder
    ? formatStudyPlaceholderDueOffset(linkedDocPlaceholder)
    : isSelectedMilestone
      ? formatStudyPlaceholderDueOffset(selectedNode) ||
        selectedNode?.dueAnchorRule ||
        ""
      : "";

  const activeMilestoneRef = useMemo(
    () => resolveMilestoneRefForSelection(selectedNode, studyPlaceholders),
    [selectedNode, studyPlaceholders],
  );

  const activeMilestonePlaceholders = useMemo(() => {
    if (activeMilestoneRef) {
      const linked = findStudyPlaceholdersForMilestone(
        activeMilestoneRef,
        studyPlaceholders,
      );
      if (linked.length) return linked;
    }
    return activeMilestonePlaceholder ? [activeMilestonePlaceholder] : [];
  }, [
    activeMilestoneRef,
    activeMilestonePlaceholder,
    studyPlaceholders,
  ]);

  const milestoneCompleteness = useMemo(
    () =>
      computeMilestoneConsolidatedCompleteness(activeMilestonePlaceholders, {
        staffList,
        roleOptions: studyRoleOptions,
        documents,
      }),
    [activeMilestonePlaceholders, staffList, studyRoleOptions, documents],
  );

  const milestonePrimaryPlaceholder =
    activeMilestonePlaceholders[0] || linkedDocPlaceholder;

  const milestoneFocusPlaceholder = useMemo(
    () =>
      resolveMilestonePlaceholderForSelection(
        selectedNode,
        studyPlaceholders,
        documents,
      ) || milestonePrimaryPlaceholder,
    [selectedNode, studyPlaceholders, documents, milestonePrimaryPlaceholder],
  );

  const uploadRoleGroups = useMemo(() => {
    if (!milestoneFocusPlaceholder) {
      return {
        done: [],
        pending: [],
        uploadDoneCount: 0,
        uploadTotalCount: 0,
      };
    }
    const doc = resolveMilestoneLinkedDocument(
      milestoneFocusPlaceholder,
      documents,
    );
    const uploadComplete = isPlaceholderUploadComplete(
      milestoneFocusPlaceholder,
      doc,
    );
    return buildPlaceholderUploadRoleGroups(
      milestoneFocusPlaceholder,
      studyRoleOptions,
      uploadComplete,
    );
  }, [milestoneFocusPlaceholder, studyRoleOptions, documents]);

  const signatureAvatarGroups = useMemo(
    () =>
      milestoneFocusPlaceholder
        ? buildPlaceholderSignatureAvatarGroups(
            milestoneFocusPlaceholder,
            staffList,
            studyRoleOptions,
            documents,
          )
        : {
            done: [],
            pending: [],
            roleAvatars: [],
            roleLabels: [],
            signedCount: 0,
            totalSigners: 0,
            requiresSignature: false,
          },
    [milestoneFocusPlaceholder, staffList, studyRoleOptions, documents],
  );

  const selectedPlaceholderSignatureGroups = useMemo(() => {
    if (!selectedPlaceholderRow) return null;
    return buildPlaceholderSignatureAvatarGroups(
      selectedPlaceholderRow,
      staffList,
      studyRoleOptions,
      documents,
    );
  }, [selectedPlaceholderRow, staffList, studyRoleOptions, documents]);

  const canSignPlaceholderDocument = useMemo(() => {
    if (!selectedPlaceholderLinkedDoc || !canEditCurrentStudyGate) return false;
    if (!selectedPlaceholderSignatureGroups?.requiresSignature) return false;
    const myId = normalizeSignerIdForCompare(userData?.StaffId);
    if (!myId) return false;
    return (selectedPlaceholderSignatureGroups.pending || []).some(
      (entry) => normalizeSignerIdForCompare(entry.staffId) === myId,
    );
  }, [
    selectedPlaceholderLinkedDoc,
    canEditCurrentStudyGate,
    selectedPlaceholderSignatureGroups,
    userData?.StaffId,
  ]);

  const showMilestoneInfoPanel =
    !!selectedNode &&
    !isSelectedFolder &&
    (Boolean(activeMilestonePlaceholder) || isSelectedMilestone);

  useEffect(() => {
    if (showMilestoneInfoPanel) {
      setShowMilestoneSection(true);
    }
  }, [showMilestoneInfoPanel, selectedNode?.id]);

  const canGenerateCertificationForSelected =
    selectedNode?.VersionNo &&
    !selectedNode?.CertificationId &&
    !isReadOnlyDocumentView &&
    hasActionPermission("generateCertification", permissions);

  useEffect(() => {
    if (isReadOnlyDocumentView) {
      setOpenSide(false);
    }
  }, [isReadOnlyDocumentView, selectedNode?.id]);

  const showPlaceholderPanel = isSelectedPlaceholder;
  const showMilestoneDashboard = isSelectedMilestone && Boolean(activeMilestoneRef);
  const showStudyFolderManagePanel = isSelectedFolder;
  /** Placeholder study-info already shows sign workflow; avoid duplicating it in Milestone information. */
  const showMilestoneSignatureInStudyInfo =
    signatureAvatarGroups.requiresSignature && !showPlaceholderPanel;
  const showSiteOverviewPanel =
    !showDocumentInfoAndPreview &&
    !showPlaceholderPanel &&
    !showStudyFolderManagePanel &&
    !showMilestoneDashboard;

  const siteOverviewSnapshot = useMemo(
    () =>
      buildSiteOverviewSnapshot({
        placeholders: studyPlaceholders,
        documents,
        userData,
        staffList,
        roleOptions: studyRoleOptions,
      }),
    [studyPlaceholders, documents, userData, staffList, studyRoleOptions],
  );

  const handleSiteOverviewTask = useCallback(
    (task) => {
      if (!task?.treeNode) return;
      if (!canEditCurrentStudyGate && (task.type === "upload" || task.type === "sign")) {
        showHistoricalGateReadOnlyAlert();
        return;
      }
      if (task.type === "upload") {
        handleUploadToPlaceholder(task.treeNode);
        return;
      }
      if (task.type === "sign") {
        handleNodeSelect(task.treeNode, "Selection", task.document);
        handleNodeAction(task.treeNode, "signFile");
        return;
      }
      handleNodeSelect(task.treeNode, "Selection");
    },
    [handleNodeSelect, handleNodeAction, handleUploadToPlaceholder, canEditCurrentStudyGate, showHistoricalGateReadOnlyAlert],
  );

  const returnToSiteOverview = useCallback(() => {
    if (!selectedNode && !selectedId) return;
    cleanupEditor();
    setEditorSessionKey((k) => k + 1);
    setImagePreviewUrl(null);
    setsSlectedFile(null);
    releaseOnlyOffice();
    setSelectedNode(null);
    setSelectedId("");
  }, [selectedNode, selectedId]);

  const isfTourActions = useMemo(
    () => ({
      showOverview: () => returnToSiteOverview(),
      openStudyInfo: () => {
        setShowStudyBasicInfo(true);
        setShowMilestoneSection(true);
      },
      selectFirstMilestone: () => {
        const node = findFirstTreeNode(
          treeData,
          (item) => item?.type === "milestone",
        );
        if (node) handleNodeSelect(node, "Selection");
      },
      selectFirstPlaceholder: () => {
        const placeholderNode = findFirstTreeNode(
          treeData,
          (item) => item?.type === "placeholder",
        );
        if (placeholderNode) {
          handleNodeSelect(placeholderNode, "Selection");
          return;
        }
        const milestoneNode = findFirstTreeNode(
          treeData,
          (item) => item?.type === "milestone",
        );
        if (milestoneNode) handleNodeSelect(milestoneNode, "Selection");
      },
      openUploadDemo: () => {
        isfTourUploadDemoRef.current = true;
        const folderNode = findFirstTreeNode(
          treeData,
          (item) => item?.type === "folder",
        );
        setUploadTargetFolderId(
          folderNode?.id ||
            uploadTargetFolderId ||
            selectedNode?.id ||
            "",
        );
        setUploadPlaceholderId("");
        setUploadPlaceholderLocked(false);
        setShowFileUploadModal(true);
      },
      closeUploadDemo: () => {
        if (!isfTourUploadDemoRef.current) return;
        isfTourUploadDemoRef.current = false;
        setShowFileUploadModal(false);
        setUploadPlaceholderId("");
        setUploadPlaceholderLocked(false);
        setFileData({});
      },
      cleanupTour: () => {
        if (isfTourUploadDemoRef.current) {
          isfTourUploadDemoRef.current = false;
          setShowFileUploadModal(false);
          setUploadPlaceholderId("");
          setUploadPlaceholderLocked(false);
          setFileData({});
        }
        returnToSiteOverview();
      },
    }),
    [
      treeData,
      uploadTargetFolderId,
      selectedNode?.id,
      returnToSiteOverview,
      handleNodeSelect,
    ],
  );

  const handleIsfTourEnd = useCallback(() => {
    isfTourActions.cleanupTour();
  }, [isfTourActions]);

  const milestoneFolderName = useMemo(() => {
    const ph = activeMilestonePlaceholders[0];
    if (ph?.FolderName || ph?.folderName) {
      return ph.FolderName || ph.folderName;
    }
    const folderId = stripGuid(activeMilestoneRef?.parentId)?.toLowerCase();
    if (!folderId || !treeData?.length) return "";

    const findFolder = (nodes) => {
      for (const node of nodes || []) {
        if (
          node.type === "folder" &&
          stripGuid(node.id).toLowerCase() === folderId
        ) {
          return node.name || "";
        }
        if (node.children?.length) {
          const nested = findFolder(node.children);
          if (nested) return nested;
        }
      }
      return "";
    };

    return findFolder(treeData);
  }, [activeMilestonePlaceholders, activeMilestoneRef, treeData]);

  const milestoneSignatureOverview = useMemo(() => {
    const rows = buildMilestoneSignerStaffStatusRows(
      activeMilestonePlaceholders,
      staffList,
      studyRoleOptions,
      documents,
    );
    return {
      done: rows
        .filter((row) => row.status === "Completed")
        .map((row) => ({
          staffId: row.staffId,
          displayName: row.staffName,
          label: row.staffName,
        })),
      pending: rows
        .filter((row) => row.status !== "Completed")
        .map((row) => ({
          staffId: row.staffId,
          displayName: row.staffName,
          label: row.staffName,
        })),
    };
  }, [
    activeMilestonePlaceholders,
    staffList,
    studyRoleOptions,
    documents,
  ]);
  const activeTemplateCategory =
    formData?.templateCategoryCreation?.value ||
    formData?.templateCategory?.value ||
    activeCategory?.value ||
    "";

  const studyGateProgress = useMemo(() => {
    const gates = buildStudyGateOptions(studyGateList);
    const actualStudyGateCode = normalizeGateCode(
      studyGate?.CurrentGateCode ||
        studyGate?.currentGateCode ||
        "PRE_STUDY",
    );
    const currentIndex = gates.findIndex(
      (gate) => normalizeGateCode(gate.code) === actualStudyGateCode,
    );
    return {
      gates,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
      actualStudyGateCode,
    };
  }, [studyGateList, studyGate]);

  const effectiveViewGateCode =
    viewGateCode || studyGateProgress.actualStudyGateCode;

  const loadStudyMilestones = useCallback(async () => {
    const studyId = studyDetails?.StudyId || formData?.study?.value;
    const siteId = getActiveSiteIdRef();
    if (!studyId || !userData?.OrganizationId) {
      setStudyMilestones([]);
      return;
    }
    try {
      const rows = await fetchStudyMilestones({
        organizationId: userData.OrganizationId,
        studyId,
        templateCategory: activeTemplateCategory,
        siteIdRef: siteId || null,
        createdBy: userData.StaffId,
        gateCode: effectiveViewGateCode,
      });
      setStudyMilestones(rows);
    } catch {
      setStudyMilestones([]);
    }
  }, [
    studyDetails?.StudyId,
    formData?.study?.value,
    userData?.OrganizationId,
    userData?.StaffId,
    activeTemplateCategory,
    getActiveSiteIdRef,
    effectiveViewGateCode,
  ]);

  useEffect(() => {
    loadStudyMilestones();
  }, [loadStudyMilestones]);

  const activeSiteReferenceDate = useMemo(() => {
    const rowWithRef = (studyMilestones || []).find((row) =>
      hasReferenceDateValue(row.siteReferenceDate),
    );
    if (!rowWithRef?.siteReferenceDate) return "";
    return (
      formatReferenceDateForApi(rowWithRef.siteReferenceDate) ||
      rowWithRef.siteReferenceDate
    );
  }, [studyMilestones]);

  useEffect(() => {
    if (activeSiteReferenceDate) {
      setOptimisticSiteReferenceDate("");
    }
  }, [activeSiteReferenceDate]);

  const effectiveSiteReferenceDate =
    activeSiteReferenceDate || optimisticSiteReferenceDate;

  const milestoneAnchorDates = useMemo(() => {
    if (!studyMilestones?.length) return {};
    const byCode = buildMilestoneCodeMap(studyMilestones);
    const map = {};
    studyMilestones.forEach((row) => {
      const code = String(row.milestoneCode || "").trim();
      if (!code) return;
      const resolved =
        row.actualDate || computeSuggestedMilestoneDate(row, byCode);
      if (!resolved) return;
      map[code] = formatReferenceDateForApi(resolved) || resolved;
    });
    return map;
  }, [studyMilestones]);

  useEffect(() => {
    const pendingDate = location.state?.pendingReferenceDate;
    const pendingSiteId = location.state?.pendingReferenceSiteId;
    const studyId = studyDetails?.StudyId || formData?.study?.value;
    if (
      !pendingDate ||
      !studyId ||
      !userData?.OrganizationId ||
      pendingReferenceSaveRef.current
    ) {
      return undefined;
    }

    pendingReferenceSaveRef.current = true;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });

    let cancelled = false;
    (async () => {
      const siteId = pendingSiteId || getActiveSiteIdRef();
      try {
        const response = await saveStudyReferenceDates({
          organizationId: userData.OrganizationId,
          studyId,
          templateCategory: activeTemplateCategory,
          createdBy: userData.StaffId,
          siteIdRef: siteId || null,
          referenceDate: pendingDate,
        });
        if (cancelled) return;
        if (!isEisfApiSuccess(response)) {
          showAlert({
            title: "Reference date not saved",
            message: utils.getApiResponseMessage(
              response,
              "Could not save the reference date after binder create.",
            ),
            type: "warning",
          });
          return;
        }
        await loadStudyMilestones();
        await getStudyData(undefined, undefined, undefined, {
          withLoader: false,
          siteIdRef: siteId,
          skipRepair: true,
        });
      } catch (refErr) {
        if (!cancelled) {
          showAlert({
            title: "Reference date not saved",
            message: utils.sanitizeUserFacingMessage(
              refErr?.message,
              "Could not save the reference date after binder create.",
            ),
            type: "warning",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.state,
    location.pathname,
    location.search,
    studyDetails?.StudyId,
    formData?.study?.value,
    userData?.OrganizationId,
    userData?.StaffId,
    activeTemplateCategory,
    getActiveSiteIdRef,
    loadStudyMilestones,
    getStudyData,
    navigate,
    showAlert,
  ]);

  const activeStudyMilestoneRow = useMemo(
    () => findStudyMilestoneForCode(studyMilestones, milestoneCodeDisplay),
    [studyMilestones, milestoneCodeDisplay],
  );

  const canShowGenerateCertificationForNode = (node) => {
    if (!node || node.type !== "file" || node.isPreviousVersion) return false;
    const doc = documents.find((d) => d.DocumentIdRef === node.id);
    const alreadyCertified = node.CertificationId || doc?.CertificationId;
    if (alreadyCertified) return false;
    return hasActionPermission("generateCertification", permissions);
  };

  const canShowCreateCapaForNode = (node) => {
    if (!node || node.type !== "file") return false;
    return canCreateCapa;
  };

  const isfTourActionsRef = useRef(isfTourActions);
  isfTourActionsRef.current = isfTourActions;

  const stableIsfTourActions = useMemo(
    () => ({
      showOverview: () => isfTourActionsRef.current.showOverview(),
      openStudyInfo: () => isfTourActionsRef.current.openStudyInfo(),
      selectFirstMilestone: () => isfTourActionsRef.current.selectFirstMilestone(),
      selectFirstPlaceholder: () => isfTourActionsRef.current.selectFirstPlaceholder(),
      openUploadDemo: () => isfTourActionsRef.current.openUploadDemo(),
      closeUploadDemo: () => isfTourActionsRef.current.closeUploadDemo(),
      cleanupTour: () => isfTourActionsRef.current.cleanupTour(),
    }),
    [],
  );

  const isfTourSteps = useMemo(
    () =>
      buildIsfPageTourSteps({
        hasSiteSelector: Boolean(
          configuredSiteOptions.length || selectedSiteId,
        ),
        hasTree: treeData.length > 0,
        hasMilestoneInTree: treeHasNodeType(treeData, "milestone"),
        hasStudyGate: Boolean(studyGate),
        actions: stableIsfTourActions,
      }),
    [
      configuredSiteOptions.length,
      selectedSiteId,
      treeData,
      studyGate,
      stableIsfTourActions,
    ],
  );

  const studyBreadcrumbLabel = useMemo(() => {
    const name =
      studyDetails?.StudyName || formData?.study?.label || "Study";
    const number = studyDetails?.StudyNumber;
    const color = studyDetails?.StudySchColor || studyDetails?.StudyColor;
    return (
      <span className="isf-breadcrumb-study d-inline-flex align-items-center gap-1">
        <StudyNumberBadge number={number} color={color} />
        <span>{name}</span>
      </span>
    );
  }, [
    studyDetails?.StudyName,
    studyDetails?.StudyNumber,
    studyDetails?.StudySchColor,
    studyDetails?.StudyColor,
    formData?.study?.label,
  ]);

  const handleViewGateChange = useCallback(
    async (code) => {
      const normalized = normalizeGateCode(code);
      setViewGateCode(normalized);
      try {
        showLoader("Loading study phase…");
        await getStudyData(undefined, undefined, undefined, {
          withLoader: false,
          viewGateCode: normalized,
        });
      } catch (error) {
        console.error("Error switching study phase:", error);
        showAlert({
          title: "Could not switch phase",
          message: utils.sanitizeUserFacingMessage(
            error?.message,
            "Study folders could not be reloaded for the selected phase.",
          ),
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [getStudyData, showLoader, hideLoader, showAlert],
  );

  const handleStudyGatePickerChange = useCallback(
    (code) => {
      const mode = getGatePickerSelectionMode(
        code,
        studyGateProgress.actualStudyGateCode,
        studyGateList,
      );
      if (mode === "blocked") {
        showAlert({
          title: "Cannot skip phases",
          message:
            "Study phases advance one step at a time. Complete the current phase before moving to a later one.",
          type: "warning",
        });
        return;
      }
      if (mode === "advance") {
        if (isStudyArchivedReadOnlyFlag) {
          showHistoricalGateReadOnlyAlert();
          return;
        }
        setShowGateAdvanceModal(true);
        return;
      }
      if (mode === "current") {
        setViewGateCode(null);
        getStudyData(undefined, undefined, undefined, {
          withLoader: false,
          viewGateCode: null,
        }).catch(() => {});
        return;
      }
      handleViewGateChange(code);
    },
    [
      studyGateProgress.actualStudyGateCode,
      studyGateList,
      showAlert,
      handleViewGateChange,
      getStudyData,
      isStudyArchivedReadOnlyFlag,
      showHistoricalGateReadOnlyAlert,
    ],
  );

  const handleStudyGateAdvanced = useCallback(async () => {
    setShowGateAdvanceModal(false);
    setViewGateCode(null);
    try {
      await getStudyData(undefined, undefined, undefined, {
        withLoader: true,
        viewGateCode: null,
      });
    } catch (error) {
      console.error("Error refreshing study after phase advance:", error);
    }
  }, [getStudyData]);

  useEffect(() => {
    if (!isViewReady || !userData?.StaffId || showStudyListPopup) return undefined;
    if (hasCompletedIsfTour(userData.StaffId)) return undefined;
    const timer = window.setTimeout(() => setShowIsfTour(true), 900);
    return () => window.clearTimeout(timer);
  }, [isViewReady, userData?.StaffId, showStudyListPopup]);

  const CrumbBar =
    lockedCategory === "ePSF" ? EpsfPageBreadcrumb : PageBreadcrumb;

  return (
    <div className="container-fluid isf-page">
      {isViewReady ? ( // 🔹 Popup open hai → BLANK
        <>
          <CrumbBar
            items={[
              {
                label: "Home",
                path: isCroReadOnlyViewer
                  ? resolveSponsorCroLandingRoute(userData)
                  : lockedCategory === "ePSF"
                    ? "/epsf/imp/inventory"
                    : "/notification-center",
              },
              {
                label: studyBreadcrumbLabel,
              },
              {
                label:
                  activeCategory?.value ||
                  activeCategory?.label ||
                  formData?.templateCategory?.value ||
                  formData?.templateCategory?.label ||
                  "eISF",
              },
            ]}
            actions={[
              {
                icon: "fas fa-exchange-alt",
                label: "Change Study",
                onClick: () => {
                  if (
                    isSponsorCroSession(userData) &&
                    formData?.study?.value
                  ) {
                    const studyRow =
                      studies.find(
                        (row) =>
                          String(row.StudyId).toLowerCase() ===
                          String(formData.study.value).toLowerCase(),
                      ) || {
                        StudyId: formData.study.value,
                        StudyName: formData.study.label,
                      };
                    syncOpenBinderCategoriesForStudy(studyRow, userData);
                  }
                  setShowStudyListPopup(true);
                },
              },
              ...(canManageStudyPlaceholdersFlag &&
              canEditCurrentStudyGate &&
              !studyPlaceholders.length
                ? [
                    {
                      icon: "fas fa-tasks",
                      label: "Apply placeholders",
                      onClick: handleApplyPlaceholdersToStudy,
                    },
                  ]
                : []),
            ]}
            preActionsExtras={
              studyGate ? (
                <IsfStudyGatePicker
                  gates={studyGateList}
                  value={effectiveViewGateCode}
                  actualGateCode={studyGateProgress.actualStudyGateCode}
                  onChange={handleStudyGatePickerChange}
                  onAdvanceRequest={() => {
                    if (isStudyArchivedReadOnlyFlag) {
                      showHistoricalGateReadOnlyAlert();
                      return;
                    }
                    setShowGateAdvanceModal(true);
                  }}
                />
              ) : null
            }
            toolbarExtras={
              !showSiteOverviewPanel ? (
                <button
                  type="button"
                  className="isf-tour-help-btn"
                  onClick={returnToSiteOverview}
                  title="Back to site overview"
                >
                  <i className="fas fa-grip-horizontal" aria-hidden="true" />
                  Site overview
                </button>
              ) : null
            }
            trailingExtras={
              <button
                type="button"
                className="isf-tour-help-btn"
                data-tour="isf-page-guide"
                onClick={() => setShowIsfTour(true)}
                title="Show page guide"
              >
                <i className="fas fa-route" aria-hidden="true" />
                Guide
              </button>
            }
          />
          <div className="row  g-3" style={{ height: "calc(100vh - 9rem)" }}>
            {/* Left Panel - 30% */}
            {/* Left Panel - Resizable */}
            <ResizableDiv
              direction="horizontal" // Resize width
              initialSize={25} // 25% of screen
              minSize={8} // Min 15%
              maxSize={37} // Max 40%
              usePercentage={true} // Use percentage
              storageKey="isf-tree-view-sidebar-width" // Save to localStorage
            >
              <div
                className="isf-left-panel"
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "10px",
                  padding: "15px",
                  height: "calc(100vh - 9rem)",
                }}
              >
                {(configuredSiteOptions.length > 0 || selectedSiteId) && (
                  <IsfSiteSelector
                    options={configuredSiteOptions}
                    siteRows={studySiteRows}
                    selectedSiteId={selectedSiteId}
                    onSelect={handleSiteChange}
                    onOverviewClick={returnToSiteOverview}
                    showOverviewActive={showSiteOverviewPanel}
                  />
                )}
                <div
                  className="isf-left-tree overflow-auto"
                  data-tour="isf-folder-tree"
                >
                  <TreeView
                    data={treeData}
                    onAction={(node, action) => handleNodeAction(node, action)}
                    onSelect={(node, action) => handleNodeSelect(node, action)}
                    selectedNodeId={selectedId}
                    canMove={canEditCurrentStudyGate}
                    treeToolBar={true}
                    showFolderNewFile={false}
                    forceExpandedIds={treeForceExpandedIds}
                    canShowGenerateCertification={
                      canShowGenerateCertificationForNode
                    }
                    canShowSignMenu={isCurrentUserEligibleToSignTreeNode}
                    canShowCreateCapa={canShowCreateCapaForNode}
                    siteReferenceDate={effectiveSiteReferenceDate}
                    milestoneAnchorDates={milestoneAnchorDates}
                  />
                </div>
              </div>
            </ResizableDiv>

            {/* Right Panel - Breadcrumb on top, then box */}
            <div className="isf-right-panel" data-tour="isf-preview-panel">
              <div
                className={`isf-right-panel-box overflow-auto${
                  showSiteOverviewPanel ? " isf-right-panel-box--overview" : ""
                }`}
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "10px",
                  padding: "15px",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {!canEditCurrentStudyGate ? (
                  <div
                    className={`isf-gate-preview-banner${
                      isStudyArchivedReadOnlyFlag
                        ? " isf-gate-preview-banner--archived"
                        : ""
                    }`}
                    data-tour="isf-gate-preview-banner"
                    role="status"
                    aria-live="polite"
                  >
                    <i
                      className={`fas ${
                        isStudyArchivedReadOnlyFlag ? "fa-archive" : "fa-eye"
                      }`}
                      aria-hidden="true"
                    />
                    <div>
                      <strong>
                        {isStudyArchivedReadOnlyFlag
                          ? "Archived study"
                          : "Read-only preview"}
                      </strong>
                      <span>
                        {" "}
                        {isStudyArchivedReadOnlyFlag
                          ? ARCHIVED_STUDY_READONLY_MESSAGE
                          : `Viewing ${gateNameForCode(effectiveViewGateCode)} phase. Switch to ${gateNameForCode(studyGateProgress.actualStudyGateCode)} to upload documents or edit placeholders.`}
                      </span>
                    </div>
                  </div>
                ) : null}
                {showDocumentInfoAndPreview &&
                  selectedNode?.VersionNo &&
                  !isReadOnlyDocumentView && (
                  <div
                    className="floating-comment-button"
                    onClick={() => {
                      setOpenSide(true);
                    }}
                  >
                    <span className="fas fa-comment"></span>
                  </div>
                )}
                <div className="isf-study-preview-row">
                  <div
                    className={`study-basic-info-panel ${showStudyBasicInfo ? "" : "collapsed"}`}
                  >
                    {showStudyBasicInfo && (
                      <div className="study-basic-info mb-2">
                        {/* Study Section */}
                        <div className="accordion-section">
                          <div
                            className="accordion-header d-flex justify-content-between align-items-center"
                            onClick={() => setShowStudySection((prev) => !prev)}
                            style={{ cursor: "pointer" }}
                          >
                            <span className="fw-semibold small">
                              Study information
                            </span>
                            <i
                              className={`fas ${
                                showStudySection
                                  ? "fa-chevron-up"
                                  : "fa-chevron-down"
                              }`}
                              style={{ fontSize: "0.7rem" }}
                            />
                          </div>
                          {showStudySection && (
                            <div className="mt-1">
                              <div className="basic-details-key-value border-bottom">
                                <label className="small text-secondary">
                                  Study Name
                                </label>
                                <div className="valuediv d-flex align-items-center gap-1 flex-wrap">
                                  <StudyNumberBadge
                                    number={studyDetails.StudyNumber}
                                    color={
                                      studyDetails.StudySchColor ||
                                      studyDetails.StudyColor
                                    }
                                  />
                                  <span
                                    className="badge badge-study"
                                    title={studyDetails.StudyName}
                                  >
                                    {studyDetails.StudyName}
                                  </span>
                                </div>
                              </div>
                              <div className="basic-details-key-value border-bottom">
                                <label className="small text-secondary">
                                  Template Category
                                </label>
                                <div className="valuediv">
                                  <span className="badge badge-study">
                                    {activeCategory?.value ||
                                      activeCategory?.label ||
                                      "eISF"}
                                  </span>
                                </div>
                              </div>
                              <div className="basic-details-key-value border-bottom">
                                <label className="small text-secondary">
                                  Active site
                                </label>
                                <div className="valuediv">
                                  <span
                                    className="badge badge-study"
                                    title={
                                      activeSiteLabel ||
                                      getStudyAssociatedSitesLabel(studyDetails)
                                    }
                                  >
                                    {activeSiteLabel ||
                                      getStudyAssociatedSitesLabel(studyDetails) ||
                                      "N/A"}
                                  </span>
                                </div>
                              </div>
                              <div className="basic-details-key-value border-bottom">
                                <label
                                  className="small text-secondary"
                                  title="Principal Investigator"
                                >
                                  Principal Investigator
                                </label>
                                <div className="valuediv">
                                  <span className="badge badge-study">
                                    {studyDetails.PIName || "N/A"}
                                  </span>
                                </div>
                              </div>
                              <div className="basic-details-key-value border-bottom">
                                <label className="small text-secondary">
                                  Study Status
                                </label>
                                <div className="valuediv">
                                  <span className="badge bg-primary">
                                    {studyDetails.StudyStatus}
                                  </span>
                                </div>
                              </div>
                              <div className="basic-details-key-value border-bottom">
                                <label className="small text-secondary">
                                  Study gates
                                </label>
                                <div className="valuediv">
                                  <StudyGateProgressStrip
                                    studyGateProgress={studyGateProgress}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Document placeholder (study level) */}
                        {showPlaceholderPanel && (
                          <div className="accordion-section mt-2">
                            <div className="accordion-header">
                              <span className="fw-semibold small">
                                Document placeholder
                              </span>
                            </div>
                            <div className="mt-1">
                              <div className="basic-details-key-value border-bottom">
                                <label className="small text-secondary">
                                  Document placeholder name
                                </label>
                                <div className="valuediv">
                                  <span className="badge badge-study">
                                    {formatStudyPlaceholderTreeLabel(selectedNode) ||
                                      selectedNode?.name}
                                  </span>
                                </div>
                              </div>
                              <div className="basic-details-key-value border-bottom">
                                <label className="small text-secondary">
                                  Required
                                </label>
                                <div className="valuediv">
                                  <span
                                    className={`badge ${
                                      selectedNode?.IsRequired === false ||
                                      selectedNode?.isRequired === false
                                        ? "bg-secondary"
                                        : "bg-primary"
                                    }`}
                                  >
                                    {selectedNode?.IsRequired === false ||
                                    selectedNode?.isRequired === false
                                      ? "Optional"
                                      : "Required"}
                                  </span>
                                </div>
                              </div>
                              <div className="basic-details-key-value border-bottom">
                                <label className="small text-secondary">
                                  Status
                                </label>
                                <div className="valuediv">
                                  <span
                                    className={`badge ${
                                      studyPlaceholderAwaitingMilestoneDates(
                                        selectedNode,
                                      )
                                        ? "bg-info"
                                        : selectedNode?.placeholderStatus ===
                                              "Overdue" ||
                                            selectedNode?.Status === "Overdue"
                                          ? "bg-danger"
                                          : "bg-secondary"
                                    }`}
                                  >
                                    {selectedPlaceholderDisplayStatus ||
                                      selectedNode?.placeholderStatus ||
                                      selectedNode?.Status ||
                                      "Pending"}
                                  </span>
                                </div>
                              </div>
                              {(selectedNode?.Description ||
                                selectedNode?.description) && (
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Description
                                  </label>
                                  <div className="valuediv small">
                                    {selectedNode?.Description ||
                                      selectedNode?.description}
                                  </div>
                                </div>
                              )}
                              {(selectedNode?.milestoneCode ||
                                selectedNode?.MilestoneCode) && (
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Milestone
                                  </label>
                                  <div className="valuediv">
                                    <InfoBadge
                                      className="badge-study"
                                      title={
                                        selectedNode?.milestoneCode ||
                                        selectedNode?.MilestoneCode ||
                                        ""
                                      }
                                    >
                                      {selectedNode?.milestoneCode ||
                                        selectedNode?.MilestoneCode}
                                    </InfoBadge>
                                  </div>
                                </div>
                              )}
                              {isMilestoneLinkedStudyPlaceholder(selectedNode) &&
                                formatStudyPlaceholderDueOffset(selectedNode) && (
                                  <div className="basic-details-key-value border-bottom">
                                    <label className="small text-secondary">
                                      Milestone due rule
                                    </label>
                                    <div className="valuediv small text-muted">
                                      {formatStudyPlaceholderDueOffset(selectedNode)}
                                      {" — "}
                                      not editable at study level.
                                    </div>
                                  </div>
                                )}
                              {(selectedNode?.DueDate || selectedNode?.dueDate) && (
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Due date
                                  </label>
                                  <div className="valuediv">
                                    <span className="badge badge-doc">
                                      {formatDisplayDueDate(
                                        selectedNode.DueDate ||
                                          selectedNode.dueDate,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {isAdHocStudyPlaceholder(selectedPlaceholderRow) &&
                                selectedPlaceholderRow?.AssignedToName && (
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Assigned to
                                  </label>
                                  <div className="valuediv">
                                    <span className="badge badge-doc">
                                      {selectedPlaceholderRow.AssignedToName}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {selectedPlaceholderSignatureGroups?.requiresSignature ? (
                                <>
                                  <div className="basic-details-key-value border-bottom">
                                    <label className="small text-secondary">
                                      Sign required
                                    </label>
                                    <div className="valuediv">
                                      <span className="isf-milestone-sign-required__badge">
                                        Yes
                                      </span>
                                    </div>
                                  </div>
                                  {selectedPlaceholderSignatureGroups.roleAvatars.length > 0 ? (
                                    <div className="basic-details-key-value border-bottom">
                                      <label className="small text-secondary">
                                        Signer role
                                      </label>
                                      <div className="valuediv">
                                        <MilestoneLibraryStyleAvatars
                                          items={selectedPlaceholderSignatureGroups.roleAvatars}
                                          emptyLabel="None"
                                        />
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="isf-milestone-signature-panel border-bottom">
                                    <div className="isf-milestone-signature-panel__columns">
                                      <div className="isf-milestone-signature-panel__col">
                                        <div className="isf-milestone-signature-panel__col-header">
                                          <i
                                            className="fas fa-file-signature isf-milestone-signature-panel__icon"
                                            aria-hidden="true"
                                          />
                                          <span>Done</span>
                                        </div>
                                        <div className="isf-milestone-signature-panel__col-body">
                                          <MilestoneUserAvatars
                                            items={selectedPlaceholderSignatureGroups.done}
                                            emptyLabel="None"
                                          />
                                        </div>
                                      </div>
                                      <div className="isf-milestone-signature-panel__col">
                                        <div className="isf-milestone-signature-panel__col-header">
                                          <i
                                            className="fas fa-file-signature isf-milestone-signature-panel__icon"
                                            aria-hidden="true"
                                          />
                                          <span>Pending</span>
                                        </div>
                                        <div className="isf-milestone-signature-panel__col-body isf-milestone-signature-panel__col-body--pending">
                                          <MilestoneUserAvatars
                                            items={selectedPlaceholderSignatureGroups.pending}
                                            emptyLabel="None"
                                          />
                                          <SignaturePendingSignLink
                                            visible={canSignPlaceholderDocument}
                                            onSign={() =>
                                              triggerSignDocument(
                                                selectedPlaceholderLinkedDoc,
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ) : null}
                              {!isPendingStudyPlaceholder(
                                selectedPlaceholderRow,
                                documents,
                                studyPlaceholders,
                              ) &&
                                (selectedPlaceholderLinkedDoc ||
                                  selectedPlaceholderRow?.FulfilledDocumentName) && (
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Uploaded file
                                  </label>
                                  <div className="valuediv">
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 text-start"
                                      onClick={() =>
                                        openPlaceholderLinkedDocument()
                                      }
                                      disabled={!selectedPlaceholderLinkedDoc}
                                    >
                                      {selectedPlaceholderLinkedDoc?.FileName ||
                                        selectedPlaceholderRow?.FulfilledDocumentName ||
                                        "Linked document"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Document Section (files only; not folders) */}
                        {showDocumentInfoAndPreview && (
                          <div className="accordion-section mt-2">
                            <div
                              className="accordion-header d-flex justify-content-between align-items-center"
                              onClick={() =>
                                setShowDocumentSection((prev) => !prev)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <span className="fw-semibold small">
                                Document information
                                {isReadOnlyDocumentView ? (
                                  <span className="badge bg-secondary ms-2">
                                    Older version · read-only
                                  </span>
                                ) : null}
                              </span>
                              <i
                                className={`fas ${
                                  showDocumentSection
                                    ? "fa-chevron-up"
                                    : "fa-chevron-down"
                                }`}
                                style={{ fontSize: "0.7rem" }}
                              />
                            </div>
                            {showDocumentSection && (
                              <div className="mt-1">
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary ">
                                    Version
                                  </label>
                                  <div className="valuediv">
                                    <span className="badge badge-doc">
                                      {selectedNode?.VersionNo
                                        ? `V-${selectedNode.VersionNo}`
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Owner
                                  </label>
                                  <div className="valuediv">
                                    <span className="badge badge-doc">
                                      {selectedNode?.OwnerName || "N/A"}
                                    </span>
                                  </div>
                                </div>
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Created Date & Time
                                  </label>
                                  <div className="valuediv">
                                    <span className="badge badge-doc">
                                      {selectedNode?.SystemDateTime || "N/A"}
                                    </span>
                                  </div>
                                </div>
                                {isReadOnlyDocumentView &&
                                selectedNode?.SupersededDateTime ? (
                                  <div className="basic-details-key-value border-bottom">
                                    <label className="small text-secondary">
                                      Replaced on
                                    </label>
                                    <div className="valuediv">
                                      <span className="badge badge-doc">
                                        {selectedNode.SupersededDateTime}
                                      </span>
                                    </div>
                                  </div>
                                ) : null}
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    External Document Id
                                  </label>
                                  <div className="valuediv">
                                    <InfoBadge
                                      className="badge-doc"
                                      title={getExternalDocumentIdDisplay(
                                        selectedNode,
                                      )}
                                    >
                                      {getExternalDocumentIdDisplay(
                                        selectedNode,
                                      )}
                                    </InfoBadge>
                                  </div>
                                </div>
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Document type
                                  </label>
                                  <div className="valuediv">
                                    <span className="badge badge-doc">
                                      {getDisplayDocumentType(
                                        selectedNode,
                                        dmsDocumentTypeOptions,
                                      )}
                                    </span>
                                  </div>
                                </div>
                                {getDisplayDocumentCategory(
                                  selectedNode,
                                  dmsDocumentCategoryOptions,
                                ) && (
                                  <div className="basic-details-key-value border-bottom">
                                    <label className="small text-secondary">
                                      Document category
                                    </label>
                                    <div className="valuediv">
                                      <span className="badge badge-doc">
                                        {getDisplayDocumentCategory(
                                          selectedNode,
                                          dmsDocumentCategoryOptions,
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {getLinkedPlaceholderLabel(
                                  selectedNode,
                                  studyPlaceholders,
                                ) && (
                                  <div className="basic-details-key-value border-bottom">
                                    <label className="small text-secondary">
                                      Linked document placeholder
                                    </label>
                                    <div className="valuediv">
                                      <InfoBadge
                                        className="badge-doc"
                                        title={
                                          getLinkedPlaceholderLabel(
                                            selectedNode,
                                            studyPlaceholders,
                                          ) || ""
                                        }
                                      >
                                        {getLinkedPlaceholderLabel(
                                          selectedNode,
                                          studyPlaceholders,
                                        )}
                                      </InfoBadge>
                                    </div>
                                  </div>
                                )}
                                <div className="basic-details-key-value border-bottom">
                                  <label className="small text-secondary">
                                    Expiry date
                                  </label>
                                  <div className="valuediv">
                                    <span className="badge badge-doc">
                                      {selectedNode?.ExpiryDate
                                        ? selectedNode.ExpiryDate
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Milestone information (from linked placeholder + signature workflow) */}
                        {showMilestoneInfoPanel && (
                            <div className="accordion-section mt-2">
                              <div
                                className="accordion-header d-flex justify-content-between align-items-center"
                                onClick={() =>
                                  setShowMilestoneSection((prev) => !prev)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <span className="fw-semibold small">
                                  Milestone information
                                </span>
                                <i
                                  className={`fas ${
                                    showMilestoneSection
                                      ? "fa-chevron-up"
                                      : "fa-chevron-down"
                                  }`}
                                  style={{ fontSize: "0.7rem" }}
                                />
                              </div>
                              {showMilestoneSection && (
                                <div className="mt-1" data-tour="isf-study-info-matrix">
                                  {!activeMilestonePlaceholders.length ? (
                                    <div className="small text-muted py-2">
                                      {isSelectedMilestone
                                        ? "No expected document placeholder is linked to this milestone yet."
                                        : "No linked expected document placeholder."}
                                      {isMandatoryForSelectedDoc
                                        ? " Signature rules use document metadata only."
                                        : ""}
                                    </div>
                                  ) : (
                                    <>
                                      {milestoneLabel && (
                                        <div className="basic-details-key-value border-bottom">
                                          <label className="small text-secondary">
                                            Milestone
                                          </label>
                                          <div className="valuediv">
                                            <InfoBadge
                                              className="badge-study"
                                              title={milestoneLabel}
                                            >
                                              {milestoneLabel}
                                            </InfoBadge>
                                          </div>
                                        </div>
                                      )}
                                      {isMilestoneLinkedStudyPlaceholder(
                                        milestonePrimaryPlaceholder,
                                      ) &&
                                        milestoneDueRuleDisplay && (
                                          <div className="basic-details-key-value border-bottom">
                                            <label className="small text-secondary">
                                              Milestone due rule
                                            </label>
                                            <div className="valuediv small text-muted">
                                              {milestoneDueRuleDisplay}
                                            </div>
                                          </div>
                                        )}
                                      {(milestonePrimaryPlaceholder?.DueDate ||
                                        milestonePrimaryPlaceholder?.dueDate) && (
                                        <div className="basic-details-key-value border-bottom">
                                          <label className="small text-secondary">
                                            Due date
                                          </label>
                                          <div className="valuediv">
                                            <span className="badge badge-doc">
                                              {formatDisplayDueDate(
                                                milestonePrimaryPlaceholder.DueDate ||
                                                  milestonePrimaryPlaceholder.dueDate,
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      {milestoneCompleteness.placeholderCount > 0 && (
                                        <div className="basic-details-key-value border-bottom">
                                          <label className="small text-secondary">
                                            Expected documents
                                          </label>
                                          <div className="valuediv small">
                                            {milestoneCompleteness.uploadDoneCount}
                                            {" / "}
                                            {milestoneCompleteness.placeholderCount}{" "}
                                            uploaded
                                          </div>
                                        </div>
                                      )}
                                      <div className="basic-details-key-value border-bottom isf-milestone-completeness-block">
                                        <label className="small text-secondary">
                                          Completeness
                                        </label>
                                        <div className="valuediv isf-milestone-completeness-wrap">
                                          <div className="isf-milestone-completeness">
                                            <div className="isf-milestone-completeness__track">
                                              <div
                                                className={`isf-milestone-completeness__fill${
                                                  milestoneCompleteness.percent >=
                                                  100
                                                    ? " is-complete"
                                                    : ""
                                                }`}
                                                style={{
                                                  width: `${milestoneCompleteness.percent}%`,
                                                }}
                                              />
                                            </div>
                                            <div className="isf-milestone-completeness__meta">
                                              <strong>
                                                {milestoneCompleteness.percent}%
                                              </strong>
                                              <span className="text-muted">
                                                Upload{" "}
                                                {milestoneCompleteness.uploadDoneCount}
                                                /
                                                {milestoneCompleteness.uploadTotalCount}
                                                {milestoneCompleteness.requiresSignature
                                                  ? ` · Signature ${milestoneCompleteness.signedCount}/${milestoneCompleteness.totalSigners}`
                                                  : ""}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      {uploadRoleGroups.uploadTotalCount > 0 && (
                                        <div className="basic-details-key-value border-bottom">
                                          <label className="small text-secondary">
                                            Uploader role
                                          </label>
                                          <div className="valuediv">
                                            <MilestoneLibraryStyleAvatars
                                              items={[
                                                ...uploadRoleGroups.pending,
                                                ...uploadRoleGroups.done,
                                              ]}
                                              emptyLabel="None"
                                            />
                                          </div>
                                        </div>
                                      )}
                                      {showMilestoneSignatureInStudyInfo && (
                                        <>
                                          <div className="basic-details-key-value border-bottom">
                                            <label className="small text-secondary">
                                              Sign Required
                                            </label>
                                            <div className="valuediv">
                                              <span className="isf-milestone-sign-required__badge">
                                                Yes
                                              </span>
                                            </div>
                                          </div>
                                          {signatureAvatarGroups.roleAvatars.length >
                                            0 && (
                                            <div className="basic-details-key-value border-bottom">
                                              <label className="small text-secondary">
                                                Signature role
                                              </label>
                                              <div className="valuediv">
                                                <MilestoneLibraryStyleAvatars
                                                  items={
                                                    signatureAvatarGroups.roleAvatars
                                                  }
                                                  emptyLabel="None"
                                                />
                                              </div>
                                            </div>
                                          )}
                                          <div className="isf-milestone-signature-panel border-bottom">
                                            <div className="isf-milestone-signature-panel__columns">
                                              <div className="isf-milestone-signature-panel__col">
                                                <div className="isf-milestone-signature-panel__col-header">
                                                  <i
                                                    className="fas fa-file-signature isf-milestone-signature-panel__icon"
                                                    aria-hidden="true"
                                                  />
                                                  <span>Done</span>
                                                </div>
                                                <div className="isf-milestone-signature-panel__col-body">
                                                  <MilestoneUserAvatars
                                                    items={
                                                      signatureAvatarGroups.done
                                                    }
                                                    emptyLabel="None"
                                                  />
                                                </div>
                                              </div>
                                              <div className="isf-milestone-signature-panel__col">
                                                <div className="isf-milestone-signature-panel__col-header">
                                                  <i
                                                    className="fas fa-file-signature isf-milestone-signature-panel__icon"
                                                    aria-hidden="true"
                                                  />
                                                  <span>pending</span>
                                                </div>
                                                <div className="isf-milestone-signature-panel__col-body isf-milestone-signature-panel__col-body--pending">
                                                  <MilestoneUserAvatars
                                                    items={
                                                      signatureAvatarGroups.pending
                                                    }
                                                    emptyLabel="None"
                                                  />
                                                  <SignaturePendingSignLink
                                                    visible={canSignSelectedDocument}
                                                    onSign={() =>
                                                      triggerSignDocument(
                                                        activeMilestoneDocument ||
                                                          signSource,
                                                      )
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                        {/* Certification info Section */}
                        {showDocumentInfoAndPreview &&
                          selectedNode?.VersionNo && (
                            <div className="accordion-section mt-2">
                              <div
                                className="accordion-header d-flex justify-content-between align-items-center"
                                onClick={() =>
                                  setShowCertificationSection((prev) => !prev)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <span className="fw-semibold small">
                                  Certification information
                                </span>
                                <i
                                  className={`fas ${
                                    showCertificationSection
                                      ? "fa-chevron-up"
                                      : "fa-chevron-down"
                                  }`}
                                  style={{ fontSize: "0.7rem" }}
                                />
                              </div>
                              {showCertificationSection && (
                                <div className="mt-1">
                                  {isReadOnlyDocumentView ? (
                                    <span className="small text-secondary">
                                      Certification is not available for older
                                      document versions.
                                    </span>
                                  ) : selectedNode?.CertificationId ? (
                                    <>
                                      <div className="basic-details-key-value border-bottom">
                                        <label className="small text-secondary">
                                          Certification ID
                                        </label>
                                        <div className="valuediv">
                                          <InfoBadge
                                            className="badge-soft"
                                            title={selectedNode.CertificationId}
                                          >
                                            {selectedNode.CertificationId}
                                          </InfoBadge>
                                        </div>
                                      </div>
                                      <div className="basic-details-key-value border-bottom">
                                        <label className="small text-secondary">
                                          Document
                                        </label>
                                        <div className="valuediv">
                                          <InfoBadge
                                            className="badge-soft"
                                            title={
                                              selectedNode?.CertificationDocumentName ||
                                              selectedNode?.FileName ||
                                              "N/A"
                                            }
                                          >
                                            {selectedNode?.CertificationDocumentName ||
                                              selectedNode?.FileName ||
                                              "N/A"}
                                          </InfoBadge>
                                        </div>
                                      </div>
                                      <div className="basic-details-key-value border-bottom">
                                        <label className="small text-secondary">
                                          External Document Id
                                        </label>
                                        <div className="valuediv">
                                          <InfoBadge
                                            className="badge-soft"
                                            title={getExternalDocumentIdDisplay(
                                              selectedNode,
                                            )}
                                          >
                                            {getExternalDocumentIdDisplay(
                                              selectedNode,
                                            )}
                                          </InfoBadge>
                                        </div>
                                      </div>
                                      <div className="basic-details-key-value border-bottom">
                                        <label className="small text-secondary">
                                          Certified By
                                        </label>
                                        <div className="valuediv">
                                          <InfoBadge
                                            className="badge-soft"
                                            title={getCertifiedByDisplayName(
                                              selectedNode,
                                              userData,
                                            )}
                                          >
                                            {getCertifiedByDisplayName(
                                              selectedNode,
                                              userData,
                                            )}
                                          </InfoBadge>
                                        </div>
                                      </div>
                                      <div className="basic-details-key-value border-bottom">
                                        <label className="small text-secondary">
                                          Date &amp; Time
                                        </label>
                                        <div className="valuediv">
                                          <InfoBadge
                                            className="badge-soft"
                                            title={
                                              selectedNode?.CertifiedDateTime ||
                                              (selectedNode?.CertificationDateTime
                                                ? new Date(
                                                    selectedNode.CertificationDateTime,
                                                  ).toLocaleString()
                                                : "N/A")
                                            }
                                          >
                                            {selectedNode?.CertifiedDateTime ||
                                              (selectedNode?.CertificationDateTime
                                                ? new Date(
                                                    selectedNode.CertificationDateTime,
                                                  ).toLocaleString()
                                                : "N/A")}
                                          </InfoBadge>
                                        </div>
                                      </div>
                                      <div className="basic-details-key-value border-bottom">
                                        <label className="small text-secondary">
                                          Regulatory Standard
                                        </label>
                                        <div className="valuediv">
                                          <InfoBadge
                                            className="badge-soft"
                                            title={
                                              selectedNode?.RegulatoryStandard ||
                                              "21 CFR Part 11 / ICH GCP E6(R3)"
                                            }
                                          >
                                            {selectedNode?.RegulatoryStandard ||
                                              "21 CFR Part 11 / ICH GCP E6(R3)"}
                                          </InfoBadge>
                                        </div>
                                      </div>
                                      <div className="mt-2">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() =>
                                            setShowCertificationDetailModal(
                                              true,
                                            )
                                          }
                                        >
                                          <i className="fas fa-certificate me-1" />
                                          View certification
                                        </button>
                                      </div>
                                    </>
                                  ) : canGenerateCertificationForSelected ? (
                                    <div className="d-flex flex-column gap-2">
                                      <span className="small text-secondary">
                                        No certification generated yet.
                                      </span>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary align-self-start"
                                        onClick={() =>
                                          handleNodeAction(
                                            selectedNode,
                                            "generateCertification",
                                          )
                                        }
                                      >
                                        Generate certification
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="small text-secondary">
                                      No certification generated.
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                  <div className="study-info-quick-tab-wrap">
                    <button
                      type="button"
                      className="study-info-quick-tab"
                      data-tour="isf-study-info"
                      onClick={() => setShowStudyBasicInfo((prev) => !prev)}
                      title={
                        showStudyBasicInfo
                          ? "Hide Study info"
                          : "Show Study info"
                      }
                    >
                      {showStudyBasicInfo ? (
                        <i className="fas fa-chevron-left" title="Hide" />
                      ) : (
                        <i className="fas fa-chevron-right" title="Show" />
                      )}
                      <i className="fas fa-info-circle" />
                      <span>Study info</span>
                    </button>
                  </div>
                  <div
                    className={`isf-preview-panel${
                      showSiteOverviewPanel ? " isf-preview-panel--overview" : ""
                    }`}
                    style={{
                      display:
                        showDocumentInfoAndPreview ||
                        showPlaceholderPanel ||
                        showStudyFolderManagePanel ||
                        showMilestoneDashboard ||
                        showSiteOverviewPanel
                          ? "block"
                          : "none",
                    }}
                  >
                    {showSiteOverviewPanel && (
                      <IsfSiteOverviewPanel
                        siteName={activeSiteLabel}
                        studyName={
                          studyDetails?.StudyName || formData?.study?.label || ""
                        }
                        snapshot={siteOverviewSnapshot}
                        onTaskAction={handleSiteOverviewTask}
                      />
                    )}
                    {showMilestoneDashboard && (
                      <MilestoneDashboard
                        milestoneLabel={milestoneLabel}
                        milestoneCode={milestoneCodeDisplay}
                        milestoneName={milestoneNameDisplay}
                        dueRule={milestoneDueRuleDisplay}
                        actualMilestoneDate={activeStudyMilestoneRow?.actualDate || ""}
                        milestoneStatus={activeStudyMilestoneRow?.status || ""}
                        dueDate={
                          milestonePrimaryPlaceholder?.DueDate ||
                          milestonePrimaryPlaceholder?.dueDate ||
                          ""
                        }
                        folderName={milestoneFolderName}
                        siteName={activeSiteLabel}
                        completeness={milestoneCompleteness}
                        signatureDone={milestoneSignatureOverview.done}
                        signaturePending={milestoneSignatureOverview.pending}
                      />
                    )}
                    {showStudyFolderManagePanel && (
                      <div className="study-folder-ph-panel h-100 overflow-auto">
                        <StudyPlaceholderManager
                          studyId={studyDetails?.StudyId || formData?.study?.value}
                          organizationId={userData?.OrganizationId}
                          createdBy={userData?.StaffId}
                          templateCategory={activeTemplateCategory}
                          siteIdRef={selectedSiteId}
                          selectedFolderId={selectedNode?.id}
                          selectedFolderName={selectedNode?.name}
                          placeholders={studyPlaceholders}
                          staffList={staffList}
                          userData={userData}
                          permissions={permissions}
                          canCreateAdHoc={canCreateStudyAdHoc}
                          canEditAdHoc={canEditStudyAdHoc}
                          readOnly={!canEditCurrentStudyGate}
                          onRefresh={refreshStudyPlaceholders}
                          showToast={showToast}
                          showAlert={showAlert}
                        />
                      </div>
                    )}
                    {showPlaceholderPanel && (
                      <div className="study-placeholder-panel h-100 d-flex flex-column justify-content-center align-items-center p-4">
                        <div className="study-placeholder-card w-100">
                          <div className="d-flex align-items-start gap-3 mb-3">
                            <i className="far fa-file-lines fa-2x text-secondary" />
                            <div>
                              <h6 className="mb-1 fw-semibold">
                                {formatStudyPlaceholderTreeLabel(selectedNode) ||
                                  selectedNode?.name}
                              </h6>
                              <p className="text-muted small mb-0">
                                {selectedNode?.folderName ||
                                  selectedNode?.FolderName ||
                                  "Folder"}{" "}
                                ·{" "}
                                {selectedNode?.IsRequired === false ||
                                selectedNode?.isRequired === false
                                  ? "Optional"
                                  : "Required"}{" "}
                                ·{" "}
                                {selectedPlaceholderDisplayStatus ||
                                  selectedNode?.placeholderStatus ||
                                  selectedNode?.Status ||
                                  "Pending"}
                              </p>
                              {(selectedNode?.Description ||
                                selectedNode?.description) && (
                                <p className="small mt-2 mb-0">
                                  {selectedNode?.Description ||
                                    selectedNode?.description}
                                </p>
                              )}
                              {selectedNode?.DueDate && (
                                <p className="small text-muted mt-1 mb-0">
                                  Due: {selectedNode.DueDate}
                                </p>
                              )}
                            </div>
                          </div>
                          {isAdHocStudyPlaceholder(selectedPlaceholderRow) &&
                            selectedPlaceholderRow?.AssignedToName && (
                            <p className="small text-muted mb-2">
                              Assigned to: {selectedPlaceholderRow.AssignedToName}
                            </p>
                          )}
                          {selectedPlaceholderSignatureGroups?.requiresSignature ? (
                            <div className="border rounded p-3 mb-3 bg-white">
                              <div className="small fw-semibold mb-2">Signature</div>
                              {selectedPlaceholderSignatureGroups.roleAvatars.length > 0 ? (
                                <p className="small text-muted mb-2">
                                  Signer role:{" "}
                                  {selectedPlaceholderSignatureGroups.roleLabels.join(", ")}
                                </p>
                              ) : null}
                              <div className="isf-milestone-signature-panel">
                                <div className="isf-milestone-signature-panel__columns">
                                  <div className="isf-milestone-signature-panel__col">
                                    <div className="isf-milestone-signature-panel__col-header">
                                      <span>Done</span>
                                    </div>
                                    <div className="isf-milestone-signature-panel__col-body">
                                      <MilestoneUserAvatars
                                        items={selectedPlaceholderSignatureGroups.done}
                                        emptyLabel="None"
                                      />
                                    </div>
                                  </div>
                                  <div className="isf-milestone-signature-panel__col">
                                    <div className="isf-milestone-signature-panel__col-header">
                                      <span>Pending</span>
                                    </div>
                                    <div className="isf-milestone-signature-panel__col-body isf-milestone-signature-panel__col-body--pending">
                                      <MilestoneUserAvatars
                                        items={selectedPlaceholderSignatureGroups.pending}
                                        emptyLabel="None"
                                      />
                                      <SignaturePendingSignLink
                                        visible={canSignPlaceholderDocument}
                                        onSign={() =>
                                          triggerSignDocument(
                                            selectedPlaceholderLinkedDoc,
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : null}
                          {isPendingStudyPlaceholder(
                            selectedPlaceholderRow || selectedNode,
                            documents,
                            studyPlaceholders,
                          ) ? (
                            <p className="small text-muted mb-3">
                              {!canEditCurrentStudyGate
                                ? "This phase is read-only. Switch to the current study phase to upload a document."
                                : canUploadToPlaceholder(selectedNode)
                                  ? "Upload a file to fulfill this document placeholder. The file will appear under this slot once uploaded."
                                  : "This expected document is assigned to another team member. You can view details in the folder list or study info panel."}
                            </p>
                          ) : selectedPlaceholderLinkedDoc ? (
                            <div className="border rounded p-3 mb-3 bg-light">
                              <div className="d-flex align-items-start gap-2 mb-2">
                                <i className="far fa-file fa-lg text-secondary mt-1" />
                                <div className="flex-grow-1">
                                  <div className="fw-semibold small">
                                    {selectedPlaceholderLinkedDoc.FileName}
                                  </div>
                                  <div className="text-muted small">
                                    {selectedPlaceholderLinkedDoc.OwnerName
                                      ? `Uploaded by ${selectedPlaceholderLinkedDoc.OwnerName}`
                                      : null}
                                    {selectedPlaceholderLinkedDoc.SystemDateTime
                                      ? ` · ${selectedPlaceholderLinkedDoc.SystemDateTime}`
                                      : null}
                                    {selectedPlaceholderLinkedDoc.VersionNo
                                      ? ` · V-${selectedPlaceholderLinkedDoc.VersionNo}`
                                      : null}
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  onClick={() => openPlaceholderLinkedDocument()}
                                >
                                  <i className="fas fa-folder-open me-1" />
                                  Open document
                                </button>
                              </div>
                            </div>
                          ) : selectedPlaceholderOrphanDocs.length > 0 ? (
                            <div className="border rounded p-3 mb-3 bg-light">
                              <p className="small text-muted mb-2">
                                This slot is fulfilled but not linked to a file yet.
                                Choose the uploaded file below to attach it.
                              </p>
                              <div className="d-flex flex-column gap-2">
                                {selectedPlaceholderOrphanDocs.map((doc) => (
                                  <button
                                    key={doc.DocumentIdRef}
                                    type="button"
                                    className="btn btn-outline-primary btn-sm text-start"
                                    disabled={!canEditCurrentStudyGate}
                                    onClick={() =>
                                      handleLinkOrphanDocumentToPlaceholder(doc)
                                    }
                                  >
                                    <i className="far fa-file me-1" />
                                    Link: {doc.FileName}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="small text-muted mb-3">
                              This slot is marked fulfilled but no linked file was
                              found. Re-upload from the placeholder or contact your
                              administrator.
                            </p>
                          )}
                          <div className="d-flex flex-wrap gap-2">
                            {canEditCurrentStudyGate &&
                              hasActionPermission("newFile", permissions) &&
                              isPendingStudyPlaceholder(
                                selectedPlaceholderRow || selectedNode,
                                documents,
                                studyPlaceholders,
                              ) &&
                              canUploadToPlaceholder(selectedNode) && (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() =>
                                  handleUploadToPlaceholder(selectedNode)
                                }
                              >
                                <i className="fas fa-upload me-1" />
                                Upload document
                              </button>
                            )}
                            {canEditCurrentStudyGate &&
                              canManageStudyPlaceholdersFlag &&
                              studyPlaceholderCanBeWaived(
                                selectedNode,
                                documents,
                                studyPlaceholders,
                              ) && (
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() =>
                                  openWaivePlaceholderModal(selectedNode)
                                }
                              >
                                Waive
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {showDocumentInfoAndPreview && selectedFile?.FileUrl ? (
                      <div
                        key={`preview-session-${editorSessionKey}`}
                        className="h-100"
                        style={{ minHeight: 0 }}
                        data-tour="isf-document-preview"
                      >
                        <DocumentPreviewPane
                          active
                          sourceModule="EISF"
                          sourceTable="EISF_StudyFolderDocument"
                          documentId={String(
                            selectedFile.DocumentIdRef ||
                              selectedFile.id ||
                              "",
                          )}
                          documentUrl={selectedFile.FileUrl}
                          documentExtension={
                            selectedFile.DocumentExtension ||
                            normalizeFileExtension(
                              (selectedFile.FileName || selectedFile.name || "")
                                .split(".")
                                .pop(),
                            )
                          }
                          documentTitle={
                            selectedFile.FileName ||
                            selectedFile.name ||
                            "Document"
                          }
                          showDownloadOriginal
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <IsfPageTour
            open={showIsfTour}
            steps={isfTourSteps}
            staffId={userData?.StaffId}
            onClose={() => setShowIsfTour(false)}
            onTourEnd={handleIsfTourEnd}
          />
        </>
      ) : urlBootstrapLoading ? (
        <div className="d-flex justify-content-center align-items-center h-75">
          <div className="text-center text-muted">
            <i className="fa fa-spinner fa-spin fa-2x mb-3" />
            <p className="mb-0">Loading study…</p>
          </div>
        </div>
      ) : croAccessGate ? (
        <div className="isf-access-gate" role="alert">
          <div className="isf-access-gate__card">
            <div
              className={`isf-access-gate__icon isf-access-gate__icon--${
                croAccessGate.kind === "all" ? "all" : "study"
              }`}
            >
              <i
                className={
                  croAccessGate.kind === "all"
                    ? "fas fa-unlink"
                    : "fas fa-exclamation-triangle"
                }
                aria-hidden="true"
              />
            </div>
            <h2 className="isf-access-gate__title">
              {croAccessGate.kind === "all"
                ? "No shared studies"
                : "Access removed"}
            </h2>
            <p className="isf-access-gate__message">
              {croAccessGate.kind === "all"
                ? "All study sharing for your account has been removed. You no longer have access to any eISF binders. Contact your organisation administrator if you need access again."
                : "Your access to this study has been removed or is no longer available. Other shared studies may still be available — choose one from the list, or contact your organisation administrator."}
            </p>
            <div className="isf-access-gate__actions">
              {croAccessGate.kind === "all" ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setCroAccessGate(null);
                    navigate(
                      resolveSponsorCroLandingRoute(userData) ||
                        SPONSOR_CRO_EMPTY_HOME_ROUTE,
                    );
                  }}
                >
                  Go to home
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setCroAccessGate(null);
                      setShowStudyListPopup(true);
                    }}
                  >
                    View shared studies
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setCroAccessGate(null);
                      navigate(
                        resolveSponsorCroLandingRoute(userData) ||
                          SPONSOR_CRO_EMPTY_HOME_ROUTE,
                      );
                    }}
                  >
                    Go to home
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : showStudyListPopup ? null : (
        <>
          <div className="d-flex justify-content-center align-items-center h-75">
            <div className="text-center">
              <i className="fa fa-layer-group fa-3x text-muted mb-3" />
              <h5 className="fw-semibold">No ISF Loaded</h5>
              <p className="text-muted mb-3">
                Select a Study and Template to begin
              </p>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowStudyListPopup(true);
                  if (userData && !studies.length) {
                    getAllStudies(undefined, { withLoader: false, force: true }).catch(
                      () => {},
                    );
                  }
                }}
              >
                Select Study
              </button>
            </div>
          </div>
        </>
      )}

      {showStudyListPopup && !croAccessGate && (
        <IsfStudyPickerModal
          open={showStudyListPopup}
          onClose={() => {
            const studyIdFromUrl = searchParams.get("studyId");
            if (studyIdFromUrl && !isViewReady) {
              appliedUrlParamsRef.current = false;
              lastUrlStudyIdRef.current = null;
            }
            setShowStudyListPopup(false);
          }}
          studies={studies}
          studiesLoading={studiesLoading}
          lockedCategory={lockedCategory}
          emptyListMessage={
            isCroReadOnlyViewer
              ? "No studies have been shared with you."
              : lockedCategory === "ePSF"
                ? "No studies available for ePSF."
                : "No studies available."
          }
          selectedStudy={formData.study}
          onSelectStudy={handleSelectStudyRecord}
          formData={formData}
          templateCategoryOptions={templateCategoryoptions}
          templateCategoryCreationOptions={templateCategoryCreationOptions}
          templateOptions={templateOptions}
          createModalSites={createModalSites}
          createModalSitesLoading={createModalSitesLoading}
          isMultiSiteCreateModal={isMultiSiteCreateModal}
          sameTemplateForAllSites={sameTemplateForAllSites}
          onSameTemplateForAllChange={setSameTemplateForAllSites}
          perSiteTemplateOptions={perSiteTemplateOptions}
          onPerSiteTemplateChange={handlePerSiteTemplateChange}
          createReferenceDate={createReferenceDate}
          onCreateReferenceDateChange={setCreateReferenceDate}
          perSiteReferenceDates={perSiteReferenceDates}
          onPerSiteReferenceDateChange={handlePerSiteReferenceDateChange}
          templateSetupInfo={createModalTemplateInfo}
          templatesLoading={templatesLoading}
          showCreatePanel={showCreatePanel && !isCroReadOnlyViewer}
          allowCreateBinder={!isCroReadOnlyViewer}
          userData={userData}
          onSelectTemplateCategory={handleSelectTemplateCategory}
          onSelectTemplateCategoryCreation={handleSelectTemplateCategoryCreation}
          onSelectTemplate={(selected) =>
            setFormData((prev) => ({
              ...prev,
              selectedTemplateOption: selected || "",
            }))
          }
          onOpenStudy={() => redirectToISFScreen({ mode: "open" })}
          onCreateStudy={handleCreateStudy}
          onRequestCreate={handleRequestCreate}
        />
      )}

      {/* File Upload side modal start */}
      <Sidemodal
        isOpen={showFileUploadModal}
        onClose={handleCloseFileUpload}
        sidewidth="32rem"
      >
        <div className="h-100 d-flex flex-column" data-tour="isf-upload-modal">
          <div className="mb-1 border-bottom pb-1">
            <h5
              className="mb-1"
              style={{ fontSize: "0.95rem", fontWeight: 600 }}
            >
              {uploadSupersedeDocumentId ? (
                <>
                  Upload new version —{" "}
                  <strong>{selectedNode?.name || "selected file"}</strong>
                </>
              ) : (
                <>
                  Upload Document to —{" "}
                  {selectedNode?.name && <strong>{selectedNode.name}</strong>}
                </>
              )}
            </h5>
          </div>

          <div
            className="flex-grow-1 overflow-auto p-1"
            style={{ maxHeight: "calc(100vh - 9rem)" }}
          >
            <div className="mb-1" data-tour="isf-upload-file">
              <div
                style={{
                  transform: "scale(0.9)",
                }}
              >
                <FileUpload
                  key={uploadFileKey}
                  acceptedFormats={allowedExtensions}
                  returnType="base64"
                  showPreview={false}
                  showFileList={false}
                  onFileSelect={(data) => {
                    setFileData(data);
                    const folderId = stripGuid(
                      uploadTargetFolderId ||
                        (selectedNode?.type === "folder"
                          ? selectedNode?.id
                          : "") ||
                        "",
                    );
                    const phRow = uploadPlaceholderId
                      ? studyPlaceholders.find(
                          (p) =>
                            String(p.StudyPlaceholderId) ===
                            String(uploadPlaceholderId),
                        )
                      : uploadSupersedeDocumentId
                        ? resolveStudyPlaceholderForDocumentRow(
                            documents.find(
                              (d) =>
                                stripGuid(d.DocumentIdRef).toLowerCase() ===
                                stripGuid(uploadSupersedeDocumentId).toLowerCase(),
                            ),
                            studyPlaceholders,
                          )
                        : null;
                    const uniqueName = buildUniqueUploadDocumentName(data?.name, {
                      placeholderRow: phRow,
                    });
                    setFormData((prev) => ({
                      ...prev,
                      uploadFileName: uniqueName,
                    }));
                    setUploadFileNameError(
                      uniqueName
                        ? validateUploadDocumentName(
                            uniqueName,
                            documents,
                            folderId,
                            { checkDuplicate: false },
                          ) || ""
                        : "Document name is required.",
                    );
                  }}
                  onError={(error) => {
                    showAlert({
                      title: "File error",
                      message:
                        error?.message ||
                        "The selected file could not be read.",
                      type: "danger",
                    });
                  }}
                />
              </div>
            </div>

            {uploadSupersedeDocumentId ? (
              <p
                className="text-danger mb-2 mt-0"
                style={{ fontSize: "0.75rem", lineHeight: 1.35 }}
              >
                Uploading a new version — the current file will remain available in older versions.
              </p>
            ) : null}

            {fileData?.name && formData.uploadFileName ? (
              <div className="mb-2" data-tour="isf-upload-document-name">
                <label
                  className="form-label fw-semibold mb-1"
                  style={{ fontSize: "0.85rem" }}
                >
                  Document name
                </label>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <span
                    className="small text-break"
                    style={{ color: "var(--bs-body-color, #212529)" }}
                    title={formData.uploadFileName}
                  >
                    {formData.uploadFileName}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-secondary p-0 flex-shrink-0"
                    onClick={clearUploadFileSelection}
                  >
                    Clear file
                  </button>
                </div>
                {showUploadFileNameValidation && uploadFileNameError && (
                  <span className="small text-danger d-block mt-1">
                    {uploadFileNameError}
                  </span>
                )}
              </div>
            ) : null}

            <div className="row g-2" data-tour="isf-upload-metadata">
              {(() => {
                const folderId =
                  uploadTargetFolderId || selectedNode?.id || "";
                const pendingOptions = studyPlaceholders
                  .filter(
                    (p) =>
                      String(p.FolderIdRef) === String(folderId) &&
                      !p.DocumentIdRef &&
                      p.Status !== "Waived" &&
                      p.Status !== "Fulfilled" &&
                      studyPlaceholderCanUpload(
                        resolveStudyPlaceholderRow(p, studyPlaceholders),
                        userData,
                        { staffList, roleOptions: studyRoleOptions },
                      ),
                  )
                  .map((p) => ({
                    value: p.StudyPlaceholderId,
                    label: `${p.PlaceholderName}${p.Status === "Overdue" ? " (Overdue)" : ""}`,
                  }));

                if (!pendingOptions.length && !uploadPlaceholderLocked) return null;

                const lockedOption =
                  uploadPlaceholderLocked && uploadPlaceholderId
                    ? pendingOptions.find(
                        (o) => o.value === uploadPlaceholderId,
                      ) ||
                      (() => {
                        const ph = studyPlaceholders.find(
                          (p) =>
                            String(p.StudyPlaceholderId) ===
                            String(uploadPlaceholderId),
                        );
                        return ph
                          ? {
                              value: ph.StudyPlaceholderId,
                              label: `${ph.PlaceholderName}${ph.Status === "Overdue" ? " (Overdue)" : ""}`,
                            }
                          : null;
                      })()
                    : null;

                const dropdownOptions = uploadPlaceholderLocked && lockedOption
                  ? [lockedOption]
                  : [
                      { value: "", label: "Ad-hoc upload (no slot)" },
                      ...pendingOptions,
                    ];

                const selectedOption =
                  uploadPlaceholderLocked && lockedOption
                    ? lockedOption
                    : pendingOptions.find(
                        (o) => o.value === uploadPlaceholderId,
                      ) || {
                        value: "",
                        label: "Ad-hoc upload (no slot)",
                      };

                return (
                  <div className="col-12">
                    <label
                      className="form-label fw-semibold"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {uploadPlaceholderLocked
                        ? "Document placeholder"
                        : "Expected document placeholder (optional)"}
                    </label>
                    <Dropdown
                      disabled={uploadPlaceholderLocked}
                      options={dropdownOptions}
                      selected={selectedOption}
                      onSelect={(opt) => setUploadPlaceholderId(opt?.value || "")}
                    />
                  </div>
                );
              })()}

              {/* Row: Document type + category */}
              <div className="col-md-6">
                <label
                  className="form-label fw-semibold milestone-modal-label milestone-modal-label--compact"
                  style={{ fontSize: "0.85rem" }}
                >
                  Document type
                </label>
                <MilestoneSingleOptionPicker
                  layout="inline-field"
                  value={formData.documentType || ""}
                  options={dmsDocumentTypeOptions.filter((opt) => opt.value)}
                  showSearch
                  addLabel="Select type"
                  changeLabel="Change type"
                  emptyPlaceholder="Select document type…"
                  popoverTitle="Document type"
                  popoverSubtitle="Choose document type"
                  searchPlaceholder="Search types..."
                  emptyMessage="No document types found."
                  onChange={(nextValue) =>
                    setFormData((prev) => ({
                      ...prev,
                      documentType: nextValue || "",
                    }))
                  }
                />
              </div>
              <div className="col-md-6">
                <label
                  className="form-label fw-semibold milestone-modal-label milestone-modal-label--compact"
                  style={{ fontSize: "0.85rem" }}
                >
                  Document category
                </label>
                <MilestoneSingleOptionPicker
                  layout="inline-field"
                  value={formData.documentCategory || ""}
                  options={dmsDocumentCategoryOptions.filter((opt) => opt.value)}
                  showSearch
                  addLabel="Select category"
                  changeLabel="Change category"
                  emptyPlaceholder="Select document category…"
                  popoverTitle="Document category"
                  popoverSubtitle="Choose document category"
                  searchPlaceholder="Search categories..."
                  emptyMessage="No document categories found."
                  onChange={(nextValue) =>
                    setFormData((prev) => ({
                      ...prev,
                      documentCategory: nextValue || "",
                    }))
                  }
                />
              </div>

              {/* Row: External document ID + expiry date */}
              <div className="col-md-6">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "0.85rem" }}
                >
                  External document ID <span style={{ color: "red" }}>*</span>
                </label>
                <div className="isf-upload-ext-doc-field">
                  <input
                    type="text"
                    maxLength={10}
                    className={`form-control form-control-sm ${
                      documentCodeError || isDocumentIdMissing
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formData.documentCode || ""}
                    onChange={(e) => {
                      const rawValue = e.target.value || "";
                      const cleanedValue = rawValue
                        .replace(/[^A-Za-z0-9_-]/g, "")
                        .slice(0, 10);
                      setDocumentCodeError(
                        cleanedValue
                          ? validateUploadExternalDocumentId(
                              documents,
                              cleanedValue,
                              { checkDuplicate: !uploadSupersedeDocumentId },
                            ) || ""
                          : "",
                      );
                      setFormData((prev) => ({
                        ...prev,
                        documentCode: cleanedValue,
                      }));
                    }}
                    placeholder="Enter external document ID"
                  />
                  {(documentCodeError || isDocumentIdMissing) && (
                    <span className="isf-upload-ext-doc-field__hint">
                      {documentCodeError || "Required · max 10 chars"}
                    </span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "0.85rem" }}
                >
                  Expiry date
                </label>
                <DatePicker
                  selected={formData.expiryDate}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      expiryDate: date,
                    }))
                  }
                  className="form-control"
                  dateFormat="dd-MM-yyyy"
                  minDate={
                    new Date(new Date().setDate(new Date().getDate() + 1))
                  }
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="dd-mm-yyyy"
                />
              </div>

              {/* Row: Search tags - single row textarea */}
              <div className="col-12 mt-1 mb-1">
                <label
                  className="form-label fw-semibold"
                  style={{ fontSize: "0.85rem" }}
                >
                  Additional tags
                </label>
                <textarea
                  rows={1}
                  className="form-control"
                  placeholder="Comma separated tags"
                  value={formData.searchTags || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      searchTags: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div
            className="mt-3 pt-2 border-top d-flex justify-content-end gap-2"
            data-tour="isf-upload-actions"
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseFileUpload}
            >
              Cancel
            </button>
            <span className="isf-upload-action-wrap">
              {isUploadDisabled && isFileMissing ? (
                <span className="isf-upload-action-wrap__tooltip" role="tooltip">
                  Select a file above to enable Upload.
                </span>
              ) : isUploadDisabled && documentCodeError ? (
                <span className="isf-upload-action-wrap__tooltip" role="tooltip">
                  {documentCodeError}
                </span>
              ) : null}
              <button
                type="button"
                className="btn btn-primary"
                disabled={isUploadDisabled}
                onClick={() => {
                  if (!isUploadDisabled) {
                    fileUploadingProcess();
                  }
                }}
              >
                {uploadSupersedeDocumentId ? "Upload version" : "Upload"}
              </button>
            </span>
          </div>
        </div>
      </Sidemodal>
      {/* File upload side modal end */}

      {/* folder CRUD modal start */}
      {showFolderCRUDModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Add/Update{" "}
                  {selectedAction === "newFolder" ? "Folder" : "Sub-Folder"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowFolderCRUDModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <label>Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={modalFolderName}
                      onChange={(e) => {
                        setModaFolderName(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowFolderCRUDModal(false)}
                >
                  Close
                </button>
                {/* {(formData.haseISF || formData.hasePSF) && ( */}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    folderCRUDProcess();
                  }}
                >
                  Submit
                </button>
                {/* )} */}
              </div>
            </div>
          </div>
        </div>
      )}
      {showSignModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Sign Document's</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSignModal(false);
                    setIsProtocolSelected([!isPRotocolSelected[0]]);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    In accordance with 21 CFR Part 11 of the Code of Federal
                    Regulations this is to certify that this electronic
                    signature is to be the legally binding equivalent of my
                    signature.
                  </div>
                  <div className="col-md-12 mt-3">
                    {sessionSignature ? (
                      <div className="d-flex flex-column align-items-start">
                        <label className="small text-secondary mb-1">
                          Your e-signature on file
                        </label>
                        <img
                          src={sessionSignature}
                          alt="E-signature"
                          style={{
                            maxWidth: "240px",
                            maxHeight: "100px",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            backgroundColor: "#f8f9fa",
                            padding: "4px",
                          }}
                        />
                      </div>
                    ) : (
                      <div className="alert alert-warning py-2">
                        <div className="d-flex flex-column align-items-center flex-sm-row justify-content-between">
                          <span className="mb-2 mb-sm-0">
                            You do not have an e-signature saved yet. Please add
                            your signature before signing.
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary ms-sm-3"
                            onClick={() => {
                              setShowSignatureModal(true);
                              setTimeout(
                                () => signatureCanvasRef.current?.clear?.(),
                                50,
                              );
                            }}
                          >
                            Add signature
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col-md-12">
                    <CheckBox
                      options={[
                        {
                          value: true,
                          label: "Protocol signature ?",
                        },
                      ]}
                      selectedValues={isPRotocolSelected}
                      onChange={() => handleCheckboxChange()}
                    />
                  </div>
                  {isPRotocolSelected[0] == true && (
                    <div className="col-md-12">
                      This signature confirms herewith to have read and
                      understood this trial protocol and attached appendices,
                      furthermore, to accomplish this study according to the
                      protocol and the Good Clinical Practice guidelines, as
                      well as local regulations, and to accept respective
                      revisions conducted by authorized personnel.
                    </div>
                  )}

                  <div className="col-md-12 mt-2">
                    Are you sure that you want to sign this document? You will
                    be asked for a reason, then to authenticate with your
                    password, access code, and multi-factor verification.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSignModal(false);
                    setIsProtocolSelected([!isPRotocolSelected[0]]);
                  }}
                >
                  No
                </button>
                {/* {(formData.haseISF || formData.hasePSF) && ( */}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    signProcess();
                  }}
                  disabled={!sessionSignature}
                >
                  Yes
                </button>
                {/* )} */}
              </div>
            </div>
          </div>
        </div>
      )}
      {showSignatureModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
          role="dialog"
          aria-modal="true"
          aria-label="Draw your signature"
          onClick={(e) =>
            e.target === e.currentTarget && setShowSignatureModal(false)
          }
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-pen-fancy me-2" aria-hidden="true" />
                  E-Signature
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSignatureModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <button
                    type="button"
                    className={`btn btn-sm me-2 ${
                      signatureMode === "draw"
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => setSignatureMode("draw")}
                  >
                    <i className="fas fa-pen me-1" aria-hidden="true" />
                    Draw
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      signatureMode === "type"
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => setSignatureMode("type")}
                  >
                    <i className="fas fa-keyboard me-1" aria-hidden="true" />
                    Type
                  </button>
                </div>
                {signatureMode === "draw" ? (
                  <>
                    <p className="text-muted small mb-2">
                      Draw your signature in the box below.
                    </p>
                    <div
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        padding: "4px",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <SignatureCanvas
                        ref={signatureCanvasRef}
                        canvasProps={{
                          style: {
                            width: "100%",
                            height: "160px",
                          },
                        }}
                        penColor="#1a1a1a"
                        backgroundColor="#f8f9fa"
                        clearOnResize={false}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-muted small mb-2">
                      Type your signature in the box below.
                    </p>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. John Smith"
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      aria-label="Type your signature"
                    />
                    {typedSignature.trim() && (
                      <div className="mt-2">
                        <span
                          style={{
                            fontFamily:
                              '"Dancing Script", "Brush Script MT", "Segoe Script", cursive',
                            fontSize: "32px",
                            fontStyle: "italic",
                          }}
                        >
                          {typedSignature.trim()}
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className="mt-3 d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      if (signatureMode === "draw") {
                        signatureCanvasRef.current?.clear?.();
                      } else {
                        setTypedSignature("");
                      }
                    }}
                  >
                    Clear
                  </button>
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      onClick={async () => {
                        let dataUrl = null;
                        if (signatureMode === "draw") {
                          if (signatureCanvasRef.current?.isEmpty?.()) return;
                          dataUrl =
                            signatureCanvasRef.current.toDataURL("image/png");
                        } else {
                          dataUrl = typedSignatureToDataUrl(typedSignature);
                          if (!dataUrl) return;
                        }
                        const saved =
                          await saveSignatureForCurrentUser(dataUrl);
                        if (saved) {
                          setShowSignatureModal(false);
                          setTypedSignature("");
                        }
                      }}
                    >
                      Save signature
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowSignatureModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRestricFolderModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Restrict User's</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRestricFolderModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <DualListBox
                      fetchSelectedItems={fetchSelectedItemsfunc}
                      allItems={staffList}
                      onChange={(selected, available) => {
                        // Store in ref to avoid infinite loop (don't update state here)
                        currentSelectedStaffRef.current = selected;
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRestricFolderModal(false)}
                >
                  Close
                </button>
                {/* {(formData.haseISF || formData.hasePSF) && ( */}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    handleRestrictedUserSave();
                  }}
                >
                  Submit
                </button>
                {/* )} */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certification generation wizard modal */}
      {showCertificationWizardModal && selectedNode && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Generate certification</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeCertificationWizard}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body cert-wizard-modal-body">
                {/* Horizontal stepper (steps 1–4) – shown from step 1 onward */}
                {certificationWizardStep >= 1 && (
                  <div className="cert-wizard-stepper">
                    {CERTIFICATION_STEPS.map((label, idx) => {
                      const stepNum = idx + 1;
                      const isCompleted = stepNum < certificationWizardStep;
                      const isCurrent = stepNum === certificationWizardStep;
                      const isUpcoming = stepNum > certificationWizardStep;
                      return (
                        <div
                          key={label}
                          className={`cert-step ${
                            isCompleted
                              ? "completed"
                              : isCurrent
                                ? "current"
                                : "upcoming"
                          }`}
                        >
                          <div
                            className={`cert-step-circle ${
                              isCompleted
                                ? "completed"
                                : isCurrent
                                  ? "current"
                                  : "upcoming"
                            }`}
                          >
                            {isCompleted ? (
                              <i className="fas fa-check" />
                            ) : (
                              stepNum
                            )}
                          </div>
                          <span
                            className={`cert-step-label ${
                              isCompleted
                                ? "completed"
                                : isCurrent
                                  ? "current"
                                  : "upcoming"
                            }`}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Step 0: Do you want to generate? */}
                {certificationWizardStep === 0 && (
                  <div className="text-center py-3">
                    <p className="mb-4">
                      Do you want to generate certification for this document?
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeCertificationWizard}
                      >
                        No
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setCertificationWizardStep(1)}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 1: Have you reviewed? */}
                {certificationWizardStep === 1 && (
                  <div className="cert-wizard-step-content">
                    <p className="mb-4">
                      Have you reviewed this document completely?
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setCertificationWizardStep(0)}
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setCertificationWizardStep(2)}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Document info */}
                {certificationWizardStep === 2 && (
                  <div className="cert-wizard-step-content">
                    <div className="border rounded p-3 mb-3 bg-light cert-wizard-step-block">
                      <div className="row g-2 small">
                        <div className="col-4 text-secondary">
                          Document Name
                        </div>
                        <div className="col-8">
                          {selectedNode?.FileName || selectedNode?.name || "—"}
                        </div>
                        <div className="col-4 text-secondary">Version</div>
                        <div className="col-8">
                          V-{selectedNode?.VersionNo ?? "—"}
                        </div>
                        <div className="col-4 text-secondary">
                          External Document Id
                        </div>
                        <div className="col-8">
                          {(() => {
                            const id =
                              getExternalDocumentIdDisplay(selectedNode);
                            return id === "N/A" ? "—" : id;
                          })()}
                        </div>
                        <div className="col-4 text-secondary">SHA-256 Hash</div>
                        <div className="col-8 text-break">
                          {selectedNode?.DocumentHash ||
                            selectedNode?.Sha256Hash ||
                            certGeneratedHash ||
                            "—"}
                        </div>
                      </div>
                    </div>
                    <p className="mb-2">Is all information correct?</p>
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setCertificationWizardStep(1)}
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setCertificationWizardStep(3)}
                      >
                        Yes, continue
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Declaration */}
                {certificationWizardStep === 3 && (
                  <div className="cert-wizard-step-content">
                    <h6 className="mb-2">
                      Read & Accept Certification Declaration
                    </h6>
                    <p className="small text-muted mb-2">
                      You must read the full declaration before you can proceed
                      to sign. This declaration will be permanently recorded.
                    </p>
                    {!certDeclarationScrolledToEnd && (
                      <p className="small text-warning mb-2">
                        <i className="fas fa-arrow-down me-1" />
                        Scroll to the end of the declaration to continue.
                      </p>
                    )}
                    <div
                      ref={certDeclarationScrollRef}
                      className="border rounded p-3 mb-3 bg-dark text-light small overflow-auto cert-wizard-step-block"
                      style={{ maxHeight: "280px" }}
                      onScroll={handleDeclarationScroll}
                    >
                      <strong className="d-block mb-2">
                        CERTIFICATION OF COPY — READ ONLY
                      </strong>
                      <p className="mb-2">
                        I, {userData?.UserName || "—"},{" "}
                        {userData?.Role || "Clinical Research Coordinator"},{" "}
                        {studyDetails?.SiteName || "Site"}, hereby certify the
                        following:
                      </p>
                      <ol className="mb-2 ps-3">
                        <li className="mb-1">
                          That I have personally reviewed the digital document
                          identified as{" "}
                          <strong>
                            {selectedNode?.FileName ||
                              selectedNode?.name ||
                              "—"}
                          </strong>{" "}
                          (External Document Id:{" "}
                          {(() => {
                            const id =
                              getExternalDocumentIdDisplay(selectedNode);
                            return id === "N/A" ? "—" : id;
                          })()}
                          , Version{" "}
                          {selectedNode?.VersionNo
                            ? `${selectedNode.VersionNo}.0`
                            : "—"}
                          ), uploaded to the AQ eISF platform on{" "}
                          {selectedNode?.CreatedDateTime
                            ? new Date(
                                selectedNode.CreatedDateTime,
                              ).toLocaleDateString(undefined, {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "—"}
                          .
                        </li>
                        <li className="mb-1">
                          That this digital copy is an accurate, complete, and
                          unaltered reproduction of the original document.
                        </li>
                        <li className="mb-1">
                          That all pages of the document have been reviewed and
                          are legible, and that no pages are missing, obscured,
                          or altered.
                        </li>
                        <li className="mb-1">
                          That this certification is made in accordance with{" "}
                          <span className="text-info">
                            ICH GCP E6(R3), 21 CFR Part 11
                          </span>
                          , and applicable FDA and EMA guidance on electronic
                          records and certified copies.
                        </li>
                        <li className="mb-1">
                          That I understand this certification creates a legally
                          binding attestation and that knowingly certifying an
                          inaccurate or incomplete copy constitutes a serious
                          breach of Good Clinical Practice (GCP) obligations and
                          may constitute research misconduct.
                        </li>
                        <li className="mb-1">
                          That this electronic signature is the legal equivalent
                          of my handwritten signature for the purposes of this
                          certification event.
                        </li>
                      </ol>
                      <p className="mb-0 small">
                        Declaration reference: {certDeclarationRefId} ·
                        System-generated timestamp will be applied upon
                        submission.
                      </p>
                    </div>
                    <div
                      className={`cert-declaration-confirm-block mb-3 cert-wizard-step-block ${
                        certDeclarationScrolledToEnd ? "is-available" : ""
                      }`}
                    >
                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="cert-declaration-accept"
                          checked={certDeclarationAccepted}
                          disabled={!certDeclarationScrolledToEnd}
                          onChange={(e) =>
                            setCertDeclarationAccepted(e.target.checked)
                          }
                        />
                        <label
                          className={`form-check-label small ${
                            !certDeclarationScrolledToEnd ? "text-muted" : ""
                          }`}
                          htmlFor="cert-declaration-accept"
                        >
                          I confirm that I have read and understood the above
                          declaration and that the statements made therein are
                          true and accurate to the best of my knowledge.
                          {!certDeclarationScrolledToEnd &&
                            " (Scroll to the end first.)"}
                        </label>
                      </div>
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setCertificationWizardStep(2)}
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={
                          !certDeclarationScrolledToEnd ||
                          !certDeclarationAccepted
                        }
                        onClick={() => setCertificationWizardStep(4)}
                      >
                        Accept Declaration →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 4: Sign & Certify */}
                {certificationWizardStep === 4 && (
                  <div className="cert-wizard-step-content">
                    <h6 className="mb-2">
                      Electronic Signature – 21 CFR Part 11
                    </h6>
                    <p className="small text-muted mb-3">
                      Select a reason for signing, then click the button below.
                      You will be asked to authenticate; after successful
                      authentication the certification will be saved.
                    </p>
                    <div className="cert-wizard-step-block mx-auto">
                      <div className="mb-3">
                        <label className="form-label small">
                          Signature Meaning / Reason{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={certSignatureReason}
                          onChange={(e) =>
                            setCertSignatureReason(e.target.value)
                          }
                        >
                          <option value="">Select reason for signing...</option>
                          {SIGNATURE_REASONS.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="border rounded p-3 mb-3 bg-light small">
                        <strong className="d-block mb-2">
                          SIGNATURE WILL RECORD
                        </strong>
                        <div className="row g-1">
                          <div className="col-4 text-secondary">Certifier</div>
                          <div className="col-8">
                            {userData?.UserName || "—"}
                            {userData?.Role ? `, ${userData.Role}` : ""}
                          </div>
                          <div className="col-4 text-secondary">Timestamp</div>
                          <div className="col-8">System-generated (UTC)</div>
                          <div className="col-4 text-secondary">
                            Document Hash
                          </div>
                          <div className="col-8 text-break">
                            {certGeneratedHash || "—"}
                          </div>
                          <div className="col-4 text-secondary">Site</div>
                          <div className="col-8">
                            {selectedNode?.SiteName ||
                              selectedNode?.SiteCode ||
                              selectedNode?.SiteId ||
                              "N/A"}
                          </div>
                          <div className="col-4 text-secondary">IP Address</div>
                          <div className="col-8">{certClientIp || "—"}</div>
                          <div className="col-4 text-secondary">Standard</div>
                          <div className="col-8">
                            21 CFR Part 11 / ICH GCP E6(R3)
                          </div>
                        </div>
                      </div>
                      <p className="small text-muted mb-3">
                        This action is irreversible. The certification record
                        will be permanently stored in the audit trail.
                      </p>
                      <div className="d-flex gap-2 justify-content-center">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setCertificationWizardStep(3)}
                        >
                          ← Back
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={submitCertificationWizard}
                        >
                          <i className="fas fa-lock me-1" /> Apply Electronic
                          Signature & Certify
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certification detail modal – when document already has certification */}
      {showCertificationDetailModal && selectedNode?.CertificationId && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content certification-detail-modal position-relative">
              <button
                type="button"
                className="btn-close position-absolute top-0 end-0 m-3"
                style={{ zIndex: 10 }}
                onClick={() => setShowCertificationDetailModal(false)}
                aria-label="Close"
              />
              <div className="modal-body pt-5">
                <div className="certification-detail-header text-center py-4 px-3 rounded">
                  <i className="fas fa-check-circle fa-3x text-white mb-2 opacity-90" />
                  <h4 className="text-white mb-1">
                    Copy Certified Successfully
                  </h4>
                  <p className="text-white mb-0 small opacity-95">
                    This document has been certified as an accurate and complete
                    reproduction
                  </p>
                </div>

                <div className="certification-detail-record mt-4">
                  <h6 className="text-uppercase small fw-bold text-secondary mb-3">
                    Certification record
                  </h6>
                  <div className="row g-2 small">
                    <div className="col-4 text-muted">Certification ID</div>
                    <div className="col-8">{selectedNode.CertificationId}</div>
                    <div className="col-4 text-muted">Document</div>
                    <div className="col-8">
                      {selectedNode?.CertificationDocumentName ||
                        selectedNode?.FileName ||
                        "N/A"}
                    </div>
                    <div className="col-4 text-muted">External Document Id</div>
                    <div className="col-8">
                      {getExternalDocumentIdDisplay(selectedNode)}
                    </div>
                    <div className="col-4 text-muted">Certified By</div>
                    <div className="col-8">
                      {getCertifiedByDisplayName(selectedNode, userData)}
                    </div>
                    <div className="col-4 text-muted">Date & Time</div>
                    <div className="col-8">
                      {selectedNode?.CertifiedDateTime ||
                        (selectedNode?.CertificationDateTime
                          ? new Date(
                              selectedNode.CertificationDateTime,
                            ).toLocaleString()
                          : "N/A")}
                    </div>

                    <div className="col-8">
                      {selectedNode?.PageCount
                        ? `${selectedNode.PageCount} of ${selectedNode.PageCount}`
                        : "N/A"}
                    </div>
                    <div className="col-4 text-muted">Regulatory Standard</div>
                    <div className="col-8">
                      {selectedNode?.RegulatoryStandard ||
                        "21 CFR Part 11 / ICH GCP E6(R3)"}
                    </div>
                  </div>
                </div>

                <div className="certification-detail-hash mt-4">
                  <h6 className="text-uppercase small fw-bold text-secondary mb-2">
                    Document hash (SHA-256)
                  </h6>
                  <div className="d-flex gap-2 align-items-center">
                    <input
                      type="text"
                      className="form-control form-control-sm font-monospace small"
                      value={
                        selectedNode?.DocumentHash ||
                        selectedNode?.CertificationDocumentHash ||
                        ""
                      }
                      readOnly
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary flex-shrink-0"
                      onClick={handleCopyCertificationHash}
                    >
                      <i className="fas fa-copy me-1" />
                      Copy
                    </button>
                  </div>
                </div>

                <div className="certification-detail-declaration mt-4 p-3 rounded border border-success bg-light">
                  <p className="small text-success mb-0">
                    Declaration recorded: I,{" "}
                    {getCertifiedByDisplayName(selectedNode, userData)}
                    , certify that this is an accurate and complete reproduction
                    of the original document. This digital copy has been
                    verified for legibility, completeness, and integrity in
                    accordance with ICH GCP E6(R3) and 21 CFR Part 11.
                  </p>
                </div>

                <div className="d-flex gap-2 justify-content-end mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCertificationDetailModal(false)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleDownloadCertificationPdf}
                  >
                    <i className="fas fa-file-pdf me-1" />
                    Download as PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* folder CRUD modal end */}
      {/* Add all the modals and toasts */}
      <ConfirmModal
        isOpen={modalState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        {...modalState}
      />

      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <StudyGateAdvanceModal
        open={showGateAdvanceModal}
        onClose={() => setShowGateAdvanceModal(false)}
        studyId={studyDetails?.StudyId || formData?.study?.value}
        studyLabel={studyBreadcrumbLabel}
        currentGateCode={studyGateProgress.actualStudyGateCode}
        siteIdRef={selectedSiteId || null}
        templateCategory={
          formData?.templateCategory?.value ||
          formData?.templateCategory ||
          activeTemplateCategory ||
          ""
        }
        roleOptions={studyRoleOptions}
        userData={userData}
        onAdvanced={handleStudyGateAdvanced}
      />

      <Sidemodal
        isOpen={openSide}
        onClose={() => setOpenSide(false)}
        sidewidth="30rem"
      >
        {/* 🎉 Your custom HTML here */}
        {/* <h2>Menu</h2>
        <p>Select an item</p> */}
        <CommentSystem
          inputNode={selectedNode}
          studyId={studyDetails.StudyId}
          isOpen={openSide}
          readOnly={isReadOnlyDocumentView}
          staffList={staffList}
          templateCategory={
            formData?.templateCategory?.value ||
            formData?.templateCategory ||
            activeTemplateCategory ||
            ""
          }
        />

        {/* You can even put forms, images, anything! */}
      </Sidemodal>

      <CapaCreateModal
        open={showCapaCreateModal}
        context={capaCreateContext}
        titleId="isf-capa-create-title"
        onClose={() => {
          setShowCapaCreateModal(false);
          setCapaCreateContext(null);
        }}
        onSaved={() => {
          setShowCapaCreateModal(false);
          setCapaCreateContext(null);
          showToast?.({
            title: "CAPA created",
            message: "Record has been saved successfully.",
            type: "success",
            icon: "fas fa-check-circle",
            duration: 5000,
          });
        }}
      />

      {/* Audit Log Modal - fetches data inside ISFAuditLog */}
      <ISFAuditLog
        variant="modal"
        isOpen={showAuditLogModal}
        onClose={() => {
          setShowAuditLogModal(false);
          setAuditLogFolderId(null);
          setAuditLogDocumentId(null);
        }}
        folderId={auditLogFolderId}
        documentId={auditLogDocumentId}
        studyId={studyDetails?.StudyId || formData?.study?.value}
        organizationId={userData?.OrganizationId || ""}
        showFilters={true}
      />

      {showWaiveModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1150 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Waive expected document</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowWaiveModal(false);
                    setWaiveTarget(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Waive{" "}
                  <strong>
                    {waiveTarget?.PlaceholderName || waiveTarget?.name}
                  </strong>
                  ? Existing files are not changed.
                </p>
                <label className="form-label small">Reason</label>
                <textarea
                  className="form-control form-control-sm"
                  rows={3}
                  value={waiveReason}
                  onChange={(e) => setWaiveReason(e.target.value)}
                  placeholder="Enter reason for waiving..."
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowWaiveModal(false);
                    setWaiveTarget(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleConfirmWaivePlaceholder}
                >
                  Waive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <IsfSiteSetupWizard
        open={showSiteSetupWizard}
        studyId={formData?.study?.value}
        templateCategory={
          activeCategory?.value ||
          formData?.templateCategory?.value ||
          formData?.templateCategoryCreation?.value ||
          ""
        }
        userData={userData}
        studySites={studySiteRows}
        templateOptions={templateOptions}
        onApplied={handleSiteSetupApplied}
        onClose={() => setShowSiteSetupWizard(false)}
      />
    </div>
  );
};

export default ISF;
